import L from "leaflet";
import * as toGeoJSON from "@tmcw/togeojson";
import JSZip from "jszip";
import { createSlipInventoryRuntime } from "../slip-delivery/index.js";

const IMAGE_GENERATOR_STATE_VERSION = 3;
const DAILY_TOTAL_HOURS = 10;
const DEFAULT_DATASET_URL = "/data/shm-marinas.kml";
const DEFAULT_DATASET_NAME = "SHM Marinas.kml";
const DEFAULT_PORTFOLIO_URL = "/data/shm-portfolio.xlsx";
const DEFAULT_PORTFOLIO_NAME = "SHM Portfolio.xlsx";
const GLOBAL_AIRPORTS_URL = "/data/global-airports.json";
const GSA_CONUS_RATES_URL = "/data/gsa-fy2026-conus.json";
const DOD_OCONUS_NONFOREIGN_URL = "/data/dod-oconus-nonforeign.json";
const STATE_FOREIGN_RATES_URL = "/data/state-foreign-march-2026.json";
const LOCAL_STATE_KEY = `hydrotools:image-generator:v${IMAGE_GENERATOR_STATE_VERSION}`;
const TRAVEL_PLANNING_STATE_KEY = "hydrotools:travel-planning:v1";
const ENABLE_REPORT_POPUPS = false;
const DEFAULT_CONUS_LODGING = 110;
const DEFAULT_CONUS_MIE = 68;
const FEDERAL_MILEAGE_RATE_2026 = 0.725;
const DEFAULT_CREW_SIZE = 2;
const FIELD_STAY_LOOKAHEAD_MINUTES = 90;
const FIELD_STAY_LOOKAHEAD_MILES = 75;
const AIRPORT_TRANSFER_SPLIT_MINUTES = 60;
const AIR_TRIGGER_REGIONS = [
  "PUERTO RICO",
  "US VIRGIN ISLANDS",
  "U.S. VIRGIN ISLANDS",
  "VIRGIN ISLANDS",
  "ST. KITTS",
  "SAINT KITTS",
  "ST. KITTS AND NEVIS",
  "SAINT KITTS AND NEVIS",
  "ST KITTS",
  "ST KITTS AND NEVIS",
];
const DEFAULT_OFFICES = [
  { id: "office-default-san-rafael-ca", name: "San Rafael, CA", lat: 37.9747795, lng: -122.5316686, airportCode: "OAK" },
  { id: "office-default-thousand-oaks-ca", name: "Thousand Oaks, CA", lat: 34.1705609, lng: -118.8375937, airportCode: "BUR" },
  { id: "office-default-port-orchard-wa", name: "Port Orchard, WA", lat: 47.5315625, lng: -122.6384056, airportCode: "SEA" },
  { id: "office-default-anchorage-ak", name: "Anchorage, AK", lat: 61.2163129, lng: -149.8948520, airportCode: "ANC" },
  { id: "office-default-la-porte-tx", name: "La Porte, TX", lat: 29.6657838, lng: -95.0193729, airportCode: "HOU" },
  { id: "office-default-charleston-sc", name: "Charleston, SC", lat: 32.7884363, lng: -79.9399309, airportCode: "CHS" },
  { id: "office-default-boston-ma", name: "Boston, MA", lat: 42.3588336, lng: -71.0578303, airportCode: "BOS" },
  { id: "office-default-virginia-beach-va", name: "Virginia Beach, VA", lat: 36.8529841, lng: -75.9774183, airportCode: "ORF" },
  { id: "office-default-dayton-oh", name: "Dayton, OH", lat: 39.7589478, lng: -84.1916069, airportCode: "DAY" },
  { id: "office-default-miami-fl", name: "Miami, FL", lat: 25.7741566, lng: -80.1935973, airportCode: "MIA" },
  { id: "office-default-dublin-ireland", name: "Dublin, Ireland", lat: 53.3493795, lng: -6.2605593, airportCode: "DUB" },
];
const OFFICE_SELECTION_COLORS = [
  { fill: "#f59e0b", stroke: "#fef3c7", text: "#fef3c7" },
  { fill: "#a855f7", stroke: "#f3e8ff", text: "#f3e8ff" },
  { fill: "#14b8a6", stroke: "#ccfbf1", text: "#ccfbf1" },
  { fill: "#eab308", stroke: "#fef9c3", text: "#fef9c3" },
  { fill: "#ec4899", stroke: "#fce7f3", text: "#fce7f3" },
  { fill: "#06b6d4", stroke: "#cffafe", text: "#cffafe" },
  { fill: "#84cc16", stroke: "#ecfccb", text: "#ecfccb" },
  { fill: "#f97316", stroke: "#ffedd5", text: "#ffedd5" },
];
const REPORT_CAPTURE_RIGHT_PANEL_WIDTH = "680px";
const DOCK_TRAINING_CLASSES = [
  { id: "dock", label: "Dock", color: "#22c55e" },
  { id: "boat", label: "Boat", color: "#f97316" },
  { id: "water", label: "Water", color: "#38bdf8" },
  { id: "pile", label: "Pile / Structure", color: "#a78bfa" },
  { id: "ignore", label: "Ignore", color: "#94a3b8" },
];

export const imageGeneratorTool = {
  id: "image-generator",
  name: "Ports and Harbors Toolset",
  stateVersion: IMAGE_GENERATOR_STATE_VERSION,
  init(app) {
    this._rt = this._rt ?? createRuntime(app);
  },
  destroy() {
    this._rt?.destroy?.();
    this._rt = null;
  },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
  serialize() {
    return this._rt?.snapshot?.() ?? null;
  },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

function createRuntime(app) {
  const map = app.map.instance;
  let containerEl = null;
  let fileInputEl = null;
  let captureLayer = null;
  let legendControlEl = null;
  let exporting = false;
  let cancelRequested = false;
  let defaultDatasetAttempted = false;
  let defaultPortfolioAttempted = false;
  let portfolioWorkbook = null;
  let routeMatrixKey = "";
  let routeMatrixDurations = null;
  let routeMatrixDistances = null;
  let routeMatrixStatus = "idle";
  let routeMatrixError = "";
  let routeMatrixRequestId = 0;
  let routeGeometryKey = "";
  let routeGeometryStatus = "idle";
  let routeGeometryError = "";
  let routeGeometryFeatures = [];
  let routeGeometryRequestId = 0;
  let suppressUiRouteSync = false;
  let slipInventoryRuntime = null;
  let globalAirports = null;
  let globalAirportsPromise = null;
  let gsaConusRates = null;
  let gsaConusRatesPromise = null;
  let dodOconusRates = null;
  let dodOconusRatesPromise = null;
  let stateForeignRates = null;
  let stateForeignRatesPromise = null;
  let enrichedTripPlanKey = "";
  let enrichedTripPlan = null;
  let enrichedTripPlanRequestId = 0;
  let autoSaveViewTimer = 0;
  let suppressAutoSaveUntil = 0;
  let hydratedFromLocalState = false;
  let suppressAutoSaveView = false;
  const dockTrainingRoot = L.featureGroup().addTo(map);
  const dockTrainingSavedLayer = L.featureGroup().addTo(dockTrainingRoot);
  const dockTrainingSketchLayer = L.featureGroup().addTo(dockTrainingRoot);
  let dockTrainingDrawSession = null;
  let status = "Load a KMZ/KML/GeoJSON point dataset.";
  let captureProgressCurrent = 0;
  let captureProgressTotal = 0;
  let state = {
    datasetName: "",
    items: [],
    activeIndex: -1,
    defaultZoom: 18,
    format: "jpg",
    scale: 2,
    marker: true,
    prefix: "marina",
    batchScope: "selected",
    offices: defaultOffices(),
    selectedOfficeIds: [],
    visibleOfficeMarinaIds: [],
    previewCodeFilter: "",
    officeDraft: { name: "", lat: "", lng: "" },
    planning: {
      averageSpeedMph: 45,
      roadFactor: 1.2,
      clusterSpacingMiles: 70,
      maxMarinasPerCluster: 5,
      defaultSurveyHours: 6,
      maxWorkHoursPerDay: 7,
      maxDriveHoursPerDay: 3,
      maxMarinasPerDay: 2,
      pricePerMarina: 6150,
      weatherContingencyPct: 10,
      gsaApiKey: "",
      travelMode: "auto",
      airOverheadHours: 4,
      airCruiseMph: 450,
      tripOfficeId: DEFAULT_OFFICES[0].id,
      returnToOffice: true,
    },
    showLegend: true,
    showMarinaCodes: true,
    showTripRoute: false,
    tripPlaybackDayIndex: -1,
    tripPlaybackZoomToDay: false,
    editMarinaViews: false,
    panels: {
      previewQueue: true,
      officeAccess: true,
      tripPlanning: true,
      slipInventory: true,
      dockExtraction: true,
      previewItems: true,
    },
    lasDock: {
      filePath: "",
      resolution: 1,
      minAboveWater: 0.5,
      maxAboveWater: 6,
      toolStatus: null,
      inspection: null,
      products: null,
      statusText: "No LAS inspected yet.",
      training: {
        activeClass: "dock",
        labels: [],
        selectedLabelId: "",
      },
    },
  };

  function defaultOffices() {
    return DEFAULT_OFFICES.map((office) => ({ ...office }));
  }

  function normalizeOffice(office) {
    const source = office && typeof office === "object" ? office : {};
    const defaultMatch = DEFAULT_OFFICES.find((entry) => entry.id === source.id || entry.name === source.name) || null;
    return {
      ...(defaultMatch || {}),
      ...source,
      airportCode: String(source.airportCode || defaultMatch?.airportCode || "").trim().toUpperCase(),
    };
  }

  function normalizeDailyPlanning(changedKey = "") {
    const totalCap = DAILY_TOTAL_HOURS;
    let work = Math.max(1, Math.min(totalCap, Number(state.planning.maxWorkHoursPerDay) || 7));
    let drive = Math.max(0, Math.min(totalCap, Number(state.planning.maxDriveHoursPerDay) || 3));
    if ((work + drive) > totalCap) {
      if (changedKey === "drive") {
        work = Math.max(1, totalCap - drive);
      } else {
        drive = Math.max(0, totalCap - work);
      }
    }
    state.planning.maxWorkHoursPerDay = Number(work.toFixed(2));
    state.planning.maxDriveHoursPerDay = Number(drive.toFixed(2));
  }

  function isEditableTarget(target) {
    if (!target) return false;
    const tag = String(target.tagName || "").toLowerCase();
    if (target.isContentEditable) return true;
    return ["input", "textarea", "select", "button"].includes(tag);
  }

  function esc(value) {
    return String(value ?? "").replace(/[&<>"']/g, (ch) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    }[ch]));
  }

  function dockTrainingClassById(classId) {
    return DOCK_TRAINING_CLASSES.find((entry) => entry.id === classId) || DOCK_TRAINING_CLASSES[0];
  }

  function dockTrainingClassOptions(selectedId) {
    return DOCK_TRAINING_CLASSES.map((entry) => `<option value="${esc(entry.id)}"${entry.id === selectedId ? " selected" : ""}>${esc(entry.label)}</option>`).join("");
  }

  function makeDockTrainingId() {
    return `dock-label-${Math.random().toString(36).slice(2, 10)}`;
  }

  function countDockTrainingByClass(classId) {
    return (state.lasDock.training?.labels || []).filter((entry) => entry?.classId === classId).length;
  }

  function dockTrainingExportPayload() {
    return {
      schema: "hydrotools-dock-training-v1",
      createdAt: new Date().toISOString(),
      source: {
        lasFilePath: String(state.lasDock.filePath || ""),
        products: state.lasDock.products?.products || null,
        outputDir: state.lasDock.products?.outputDir || "",
      },
      labels: (state.lasDock.training?.labels || []).map((label) => ({
        id: String(label.id || ""),
        classId: String(label.classId || ""),
        classLabel: dockTrainingClassById(label.classId).label,
        createdAt: String(label.createdAt || ""),
        geometry: label.geometry || null,
      })),
    };
  }

  function downloadDockTrainingLabels() {
    const payload = dockTrainingExportPayload();
    const stem = sanitizeFilePart(String(state.lasDock.filePath || "").split(/[\\/]/).pop()?.replace(/\.[^.]+$/, "") || "dock-training", "dock-training");
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${stem}_training_labels.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  function renderDockTrainingLayers() {
    dockTrainingSavedLayer.clearLayers();
    const labels = state.lasDock.training?.labels || [];
    labels.forEach((label) => {
      const ring = Array.isArray(label?.geometry?.coordinates?.[0]) ? label.geometry.coordinates[0] : [];
      if (ring.length < 4) return;
      const style = dockTrainingClassById(label.classId);
      const latlngs = ring.map((coord) => [Number(coord[1]), Number(coord[0])]);
      const isSelected = String(label.id || "") === String(state.lasDock.training?.selectedLabelId || "");
      const layer = L.polygon(latlngs, {
        color: style.color,
        weight: isSelected ? 3 : 2,
        fillColor: style.color,
        fillOpacity: isSelected ? 0.28 : 0.18,
        dashArray: isSelected ? null : "6 4",
      }).addTo(dockTrainingSavedLayer);
      layer.on("click", () => {
        state.lasDock.training.selectedLabelId = String(label.id || "");
        persistLocalState();
        renderDockTrainingLayers();
        render();
      });
      layer.bindTooltip(`${style.label}`, { sticky: true, direction: "top", opacity: 0.9 });
    });
  }

  function stopDockTrainingDraw(reason = "stopped") {
    if (!dockTrainingDrawSession) return;
    const { tempLine, tempFill, off } = dockTrainingDrawSession;
    off();
    try { dockTrainingSketchLayer.removeLayer(tempLine); } catch {}
    try { dockTrainingSketchLayer.removeLayer(tempFill); } catch {}
    dockTrainingDrawSession = null;
    const mapEl = map?.getContainer?.() || null;
    if (mapEl) mapEl.style.cursor = "";
    if (reason === "cancelled") app.ui.toast("Dock training draw cancelled.");
    render();
  }

  function startDockTrainingDraw() {
    stopDockTrainingDraw("replaced");
    const mapEl = map?.getContainer?.() || null;
    const activeClass = dockTrainingClassById(state.lasDock.training?.activeClass || "dock");
    if (mapEl) mapEl.style.cursor = "crosshair";
    const points = [];
    const tempLine = L.polyline([], { color: activeClass.color, weight: 2, dashArray: "6 6" }).addTo(dockTrainingSketchLayer);
    const tempFill = L.polygon([], { color: activeClass.color, weight: 2, fillColor: activeClass.color, fillOpacity: 0.14 }).addTo(dockTrainingSketchLayer);
    try { tempLine.bringToFront(); } catch {}
    try { tempFill.bringToFront(); } catch {}

    const updatePreview = (cursor = null) => {
      const path = cursor ? [...points, cursor] : [...points];
      tempLine.setLatLngs(path);
      if (points.length >= 2) tempFill.setLatLngs(path);
    };

    const finishDraw = () => {
      if (points.length < 3) {
        app.ui.toast("Need at least 3 vertices for a training polygon.");
        return;
      }
      const coords = points.map((p) => [Number(p.lng), Number(p.lat)]);
      const geometry = { type: "Polygon", coordinates: [[...coords, coords[0]]] };
      const label = {
        id: makeDockTrainingId(),
        classId: activeClass.id,
        geometry,
        createdAt: new Date().toISOString(),
      };
      state.lasDock.training.labels = [...(state.lasDock.training.labels || []), label];
      state.lasDock.training.selectedLabelId = label.id;
      persistLocalState();
      stopDockTrainingDraw("completed");
      renderDockTrainingLayers();
      render();
      app.ui.toast(`${activeClass.label} label captured.`);
    };

    const onClick = (e) => {
      points.push(e.latlng);
      updatePreview();
    };
    const onMove = (e) => updatePreview(e.latlng);
    const onDblClick = (e) => {
      L.DomEvent.stop(e);
      finishDraw();
    };
    const onContextMenu = (e) => {
      L.DomEvent.stop(e);
      finishDraw();
    };
    const onKey = (e) => {
      if (e.key === "Escape") stopDockTrainingDraw("cancelled");
    };

    const dblWas = map.doubleClickZoom?.enabled?.() ?? false;
    map.doubleClickZoom.disable();
    map.on("click", onClick);
    map.on("mousemove", onMove);
    map.on("dblclick", onDblClick);
    map.on("contextmenu", onContextMenu);
    document.addEventListener("keydown", onKey);

    dockTrainingDrawSession = {
      tempLine,
      tempFill,
      off() {
        map.off("click", onClick);
        map.off("mousemove", onMove);
        map.off("dblclick", onDblClick);
        map.off("contextmenu", onContextMenu);
        document.removeEventListener("keydown", onKey);
        if (dblWas) window.setTimeout(() => map.doubleClickZoom.enable(), 0);
      },
    };
    render();
    app.ui.toast(`Drawing ${activeClass.label} label. Click vertices, then double-click or right-click to finish.`);
  }

  function removeSelectedDockTrainingLabel() {
    const selectedId = String(state.lasDock.training?.selectedLabelId || "");
    if (!selectedId) {
      app.ui.toast("Select a training label first.");
      return;
    }
    state.lasDock.training.labels = (state.lasDock.training.labels || []).filter((label) => String(label?.id || "") !== selectedId);
    state.lasDock.training.selectedLabelId = "";
    persistLocalState();
    renderDockTrainingLayers();
    render();
  }

  function setStatus(message) {
    status = String(message || "").trim() || "Ready.";
    const el = containerEl?.querySelector?.("#imggen-status");
    if (el) el.textContent = status;
  }

  async function checkLasToolchain() {
    state.lasDock.statusText = "Checking LAS toolchain...";
    render();
    try {
      const response = await fetch("/api/image-generator/las-status");
      const payload = await response.json();
      if (!response.ok || payload?.ok === false) throw new Error(payload?.error || "LAS toolchain check failed.");
      state.lasDock.toolStatus = payload;
      state.lasDock.statusText = "LAS toolchain verified on host.";
      render();
    } catch (error) {
      state.lasDock.statusText = `LAS toolchain check failed: ${error?.message || "unknown error"}`;
      render();
      app.ui.toast("LAS toolchain check failed.");
    }
  }

  async function browseLasSource() {
    try {
      const response = await fetch("/api/qimera/pick-file", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: "Select marina LAS/LAZ file",
          selectedPath: state.lasDock.filePath || "C:\\HydroShare",
          filter: "Point Cloud (*.las;*.laz)|*.las;*.laz|LAS (*.las)|*.las|LAZ (*.laz)|*.laz",
        }),
      });
      const payload = await response.json();
      if (!response.ok || payload?.ok === false) throw new Error(payload?.error || "File picker failed.");
      if (payload?.canceled || !payload?.path) return;
      const nextPath = String(payload.path || "");
      const changedSource = nextPath.trim().toLowerCase() !== String(state.lasDock.filePath || "").trim().toLowerCase();
      state.lasDock.filePath = nextPath;
      state.lasDock.inspection = null;
      state.lasDock.products = null;
      if (changedSource) {
        state.lasDock.training = { activeClass: state.lasDock.training?.activeClass || "dock", labels: [], selectedLabelId: "" };
        renderDockTrainingLayers();
      }
      state.lasDock.statusText = "LAS selected. Inspect it to frame the dock extraction workflow.";
      render();
    } catch (error) {
      state.lasDock.statusText = `Could not browse LAS source: ${error?.message || "unknown error"}`;
      render();
      app.ui.toast("LAS browse failed.");
    }
  }

  async function inspectLasSource() {
    const filePath = String(state.lasDock.filePath || "").trim();
    if (!filePath) {
      app.ui.toast("Choose a LAS file first.");
      return;
    }
    state.lasDock.statusText = "Inspecting LAS with PDAL...";
    render();
    try {
      const response = await fetch("/api/image-generator/las-inspect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filePath }),
      });
      const payload = await response.json();
      if (!response.ok || payload?.ok === false) throw new Error(payload?.error || "LAS inspection failed.");
      state.lasDock.inspection = payload;
      state.lasDock.statusText = "LAS inspection complete. Ready to frame the extraction pipeline.";
      render();
    } catch (error) {
      state.lasDock.statusText = `LAS inspection failed: ${error?.message || "unknown error"}`;
      render();
      app.ui.toast("LAS inspection failed.");
    }
  }

  async function generateLasProducts() {
    const filePath = String(state.lasDock.filePath || "").trim();
    if (!filePath) {
      app.ui.toast("Choose a LAS file first.");
      return;
    }
    state.lasDock.statusText = "Generating elevation and intensity rasters with PDAL...";
    render();
    try {
      const response = await fetch("/api/image-generator/las-products", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filePath,
          resolution: Number(state.lasDock.resolution) || 1,
          minAboveWater: Number(state.lasDock.minAboveWater) || 0.5,
          maxAboveWater: Number(state.lasDock.maxAboveWater) || 6,
        }),
      });
      const payload = await response.json();
      if (!response.ok || payload?.ok === false) throw new Error(payload?.error || "LAS product generation failed.");
      state.lasDock.products = payload;
      state.lasDock.statusText = "LAS products generated. Elevation and intensity rasters are ready.";
      render();
      app.ui.toast("LAS products generated.");
    } catch (error) {
      state.lasDock.statusText = `LAS product generation failed: ${error?.message || "unknown error"}`;
      render();
      app.ui.toast("LAS product generation failed.");
    }
  }

  function buildHostedTiffUrl(filePath) {
    return `/api/qimera/local-file?path=${encodeURIComponent(String(filePath || ""))}&v=${Date.now()}`;
  }

  function loadLasProductsIntoMap() {
    const layersService = app?.services?.layers;
    const products = state.lasDock.products?.products || null;
    if (!layersService) {
      app.ui.toast("Layer services are not available.");
      return;
    }
    if (!products?.elevation && !products?.intensity) {
      app.ui.toast("Generate products first.");
      return;
    }
    const existingLayers = layersService.listLayers?.() || [];
    const addProduct = (kind, filePath, nameSuffix) => {
      if (!filePath) return;
      const existing = existingLayers.find((layer) => String(layer?.meta?.lasProductPath || "").trim().toLowerCase() === String(filePath).toLowerCase());
      const def = {
        id: existing?.id || undefined,
        type: "geotiff-url",
        kind: "overlay",
        name: `LAS ${nameSuffix}`,
        url: buildHostedTiffUrl(filePath),
        opacity: existing?.opacity ?? 0.92,
        visible: true,
        meta: {
          sourceType: "las-products",
          lasProductKind: kind,
          lasProductPath: filePath,
        },
      };
      if (existing?.id) {
        layersService.removeLayer(existing.id);
      }
      layersService.addLayer("image-generator", def);
    };
    addProduct("elevation", products.elevation, "Elevation");
    addProduct("intensity", products.intensity, "Intensity");
    addProduct("dock-band", products.dockBand, "Dock Band");
    state.lasDock.statusText = "Loaded LAS products into Hydrotools.";
    render();
    app.ui.toast("LAS products loaded.");
  }

  function updateCaptureProgressDom() {
    const wrapEl = containerEl?.querySelector?.("#imggen-progress-wrap");
    const fillEl = containerEl?.querySelector?.("#imggen-progress-fill");
    if (!wrapEl || !fillEl) return;
    const total = Math.max(0, Number(captureProgressTotal) || 0);
    const current = Math.max(0, Math.min(total, Number(captureProgressCurrent) || 0));
    const percent = total > 0 ? Math.max(0, Math.min(100, (current / total) * 100)) : 0;
    wrapEl.style.display = total > 0 ? "block" : "none";
    fillEl.style.width = `${percent}%`;
    fillEl.setAttribute("aria-valuenow", String(current));
    fillEl.setAttribute("aria-valuemax", String(total || 1));
  }

  function setCaptureProgress(current, total) {
    captureProgressCurrent = Math.max(0, Number(current) || 0);
    captureProgressTotal = Math.max(0, Number(total) || 0);
    updateCaptureProgressDom();
  }

  function clearCaptureProgress() {
    setCaptureProgress(0, 0);
  }

  function suppressAutoSaveFor(ms = 1200) {
    suppressAutoSaveUntil = Math.max(suppressAutoSaveUntil, Date.now() + Math.max(0, Number(ms) || 0));
  }

  function persistLocalState() {
    try {
      localStorage.setItem(LOCAL_STATE_KEY, JSON.stringify(snapshot()));
    } catch {}
  }

  function readLocalState() {
    try {
      const raw = localStorage.getItem(LOCAL_STATE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function sanitizeFilePart(value, fallback = "item") {
    const cleaned = String(value || "")
      .trim()
      .replace(/[<>:"/\\|?*\x00-\x1F]+/g, "-")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
    return cleaned || fallback;
  }

  function stripKnownSuffix(name) {
    return String(name || "").replace(/\.(kmz|kml|geojson|json)$/i, "");
  }

  function stripKnownSpreadsheetSuffix(name) {
    return String(name || "").replace(/\.(xlsx|xls)$/i, "");
  }

  function normalizeMarinaKey(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/\([^)]*\)/g, "")
      .replace(/\bsafe harbor\b/g, "")
      .replace(/ship'?s/g, "ships")
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function normalizeFeatureCollection(data) {
    if (data?.type === "FeatureCollection" && Array.isArray(data.features)) return JSON.parse(JSON.stringify(data));
    if (data?.type === "Feature" && data.geometry) {
      return { type: "FeatureCollection", features: [JSON.parse(JSON.stringify(data))] };
    }
    return { type: "FeatureCollection", features: [] };
  }

  function childElementsByLocalName(node, localName) {
    return Array.from(node?.children || []).filter((child) => String(child?.localName || "").toLowerCase() === String(localName || "").toLowerCase());
  }

  function firstChildText(node, localName) {
    const match = childElementsByLocalName(node, localName)[0];
    return String(match?.textContent || "").trim();
  }

  function parsePlacemarkExtendedData(placemarkEl) {
    const out = {};
    childElementsByLocalName(placemarkEl, "ExtendedData").forEach((extEl) => {
      childElementsByLocalName(extEl, "Data").forEach((dataEl) => {
        const key = String(dataEl.getAttribute("name") || "").trim();
        const value = firstChildText(dataEl, "value");
        if (key) out[key] = value;
      });
    });
    return out;
  }

  function parseCoordinateText(raw) {
    const text = String(raw || "").trim();
    if (!text) return null;
    const token = text.split(/\s+/).find(Boolean);
    if (!token) return null;
    const parts = token.split(",");
    const lng = Number(parts[0]);
    const lat = Number(parts[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }

  function extractItemsFromKmlDom(dom) {
    const placemarkEls = Array.from(dom?.getElementsByTagName?.("*") || []).filter((el) => String(el?.localName || "").toLowerCase() === "placemark");
    const items = [];
    placemarkEls.forEach((placemarkEl, placemarkIndex) => {
      const pointEl = Array.from(placemarkEl.getElementsByTagName("*")).find((el) => String(el?.localName || "").toLowerCase() === "point");
      if (!pointEl) return;
      const coordEl = Array.from(pointEl.getElementsByTagName("*")).find((el) => String(el?.localName || "").toLowerCase() === "coordinates");
      const coord = parseCoordinateText(coordEl?.textContent || "");
      if (!coord) return;
      const properties = {
        name: firstChildText(placemarkEl, "name"),
        description: firstChildText(placemarkEl, "description"),
        ...parsePlacemarkExtendedData(placemarkEl),
      };
      items.push({
        id: `kml-${placemarkIndex + 1}`,
        name: properties.name || `Marina ${placemarkIndex + 1}`,
        lat: coord.lat,
        lng: coord.lng,
        properties,
        selected: true,
        view: null,
        coveredSlips: false,
        excludeFromReport: false,
        noFlyZone: false,
        prioritizeAirTravel: false,
      });
    });
    return items;
  }

  function pointName(feature, fallbackIndex) {
    const props = feature?.properties || {};
    const keys = ["name", "Name", "title", "Title", "marina", "Marina", "facility", "Facility"];
    for (const key of keys) {
      const value = String(props?.[key] || "").trim();
      if (value) return value;
    }
    return `Marina ${fallbackIndex + 1}`;
  }

  function formatBounds(bounds) {
    if (!bounds) return "Default";
    return `W ${bounds.west.toFixed(4)} S ${bounds.south.toFixed(4)} E ${bounds.east.toFixed(4)} N ${bounds.north.toFixed(4)}`;
  }

  function isAirMissionPriority(value) {
    if (value === true) return true;
    if (typeof value === "number") return value === 1;
    const text = String(value ?? "").trim().toLowerCase();
    return ["true", "1", "yes", "y", "on"].includes(text);
  }

  function normalizeItem(item = {}) {
    return {
      ...item,
      selected: item.selected !== false,
      view: item.view || null,
      coveredSlips: item.coveredSlips === true,
      excludeFromReport: item.excludeFromReport === true,
      noFlyZone: item.noFlyZone === true,
      prioritizeAirTravel: isAirMissionPriority(item.prioritizeAirTravel),
    };
  }

  function currentItem() {
    if (state.activeIndex < 0 || state.activeIndex >= state.items.length) return null;
    return state.items[state.activeIndex] || null;
  }

  function normalizedTripPlaybackDayIndex(plan) {
    const days = Array.isArray(plan?.days) ? plan.days : [];
    if (!days.length) return -1;
    if (Number.isInteger(state.tripPlaybackDayIndex) && state.tripPlaybackDayIndex >= 0) {
      return Math.max(0, Math.min(days.length - 1, state.tripPlaybackDayIndex));
    }
    return 0;
  }

  function currentTripPlaybackDay(plan) {
    const index = normalizedTripPlaybackDayIndex(plan);
    return index >= 0 ? plan?.days?.[index] || null : null;
  }

  function milesBetween(aLat, aLng, bLat, bLng) {
    const meters = map.distance([Number(aLat), Number(aLng)], [Number(bLat), Number(bLng)]);
    return Number.isFinite(meters) ? (meters / 1609.344) : 0;
  }

  function estimateDriveMinutes(miles) {
    const speed = Math.max(5, Number(state.planning.averageSpeedMph) || 45);
    const factor = Math.max(1, Number(state.planning.roadFactor) || 1.2);
    return (Number(miles) || 0) * factor / speed * 60;
  }

  function haversineMiles(lat1, lon1, lat2, lon2) {
    const toRad = (d) => (Number(d) * Math.PI) / 180;
    const p1 = toRad(lat1);
    const p2 = toRad(lat2);
    const dp = toRad(lat2 - lat1);
    const dl = toRad(lon2 - lon1);
    const aa = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
    const km = 6371 * (2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa)));
    return km * 0.621371;
  }

  function airportTypeAdj(type) {
    const value = String(type || "").toLowerCase();
    if (value === "large" || value === "large_airport") return 24;
    if (value === "medium" || value === "medium_airport") return 14;
    return 8;
  }

  function estimateFare(oneWayMiles, originAdj, destAdj) {
    const d = Math.max(0, Number(oneWayMiles) || 0);
    const seg1 = Math.min(d, 500) * 0.16;
    const seg2 = Math.min(Math.max(d - 500, 0), 1000) * 0.12;
    const seg3 = Math.max(d - 1500, 0) * 0.09;
    const oneWay = Math.max(69, 58 + seg1 + seg2 + seg3 + originAdj + destAdj);
    const roundTrip = oneWay * 1.87;
    return {
      oneWay,
      roundTrip,
      low: roundTrip * 0.72,
      high: roundTrip * 1.28,
    };
  }

  function airportPoint(airport) {
    if (!airport) return null;
    const lat = Number(airport.lat);
    const lng = Number(airport.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }

  function samePoint(a, b, tolerance = 1e-6) {
    if (!a || !b) return false;
    return Math.abs(Number(a.lat) - Number(b.lat)) <= tolerance
      && Math.abs(Number(a.lng) - Number(b.lng)) <= tolerance;
  }

  function ensureGlobalAirportsLoaded() {
    if (Array.isArray(globalAirports)) return Promise.resolve(globalAirports);
    if (globalAirportsPromise) return globalAirportsPromise;
    globalAirportsPromise = fetch(GLOBAL_AIRPORTS_URL, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(`Airport data HTTP ${response.status}`);
        return response.json();
      })
      .then((rows) => {
        globalAirports = Array.isArray(rows) ? rows.filter((row) => row && row.iata && Number.isFinite(Number(row.lat)) && Number.isFinite(Number(row.lon))) : [];
        return globalAirports;
      })
      .catch(() => {
        globalAirports = [];
        return globalAirports;
      })
      .finally(() => {
        globalAirportsPromise = null;
        clearEnrichedTripPlan();
        render();
      });
    return globalAirportsPromise;
  }

  function ensureGsaConusRatesLoaded() {
    if (Array.isArray(gsaConusRates)) return Promise.resolve(gsaConusRates);
    if (gsaConusRatesPromise) return gsaConusRatesPromise;
    gsaConusRatesPromise = fetch(GSA_CONUS_RATES_URL, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(`GSA CONUS data HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        gsaConusRates = Array.isArray(payload?.entries) ? payload.entries : [];
        return gsaConusRates;
      })
      .catch(() => {
        gsaConusRates = [];
        return gsaConusRates;
      })
      .finally(() => {
        gsaConusRatesPromise = null;
      });
    return gsaConusRatesPromise;
  }

  function ensureDodOconusRatesLoaded() {
    if (Array.isArray(dodOconusRates)) return Promise.resolve(dodOconusRates);
    if (dodOconusRatesPromise) return dodOconusRatesPromise;
    dodOconusRatesPromise = fetch(DOD_OCONUS_NONFOREIGN_URL, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(`DoD OCONUS data HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        dodOconusRates = Array.isArray(payload?.entries) ? payload.entries : [];
        return dodOconusRates;
      })
      .catch(() => {
        dodOconusRates = [];
        return dodOconusRates;
      })
      .finally(() => {
        dodOconusRatesPromise = null;
      });
    return dodOconusRatesPromise;
  }

  function ensureStateForeignRatesLoaded() {
    if (Array.isArray(stateForeignRates)) return Promise.resolve(stateForeignRates);
    if (stateForeignRatesPromise) return stateForeignRatesPromise;
    stateForeignRatesPromise = fetch(STATE_FOREIGN_RATES_URL, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(`State foreign data HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        stateForeignRates = Array.isArray(payload?.entries) ? payload.entries : [];
        return stateForeignRates;
      })
      .catch(() => {
        stateForeignRates = [];
        return stateForeignRates;
      })
      .finally(() => {
        stateForeignRatesPromise = null;
      });
    return stateForeignRatesPromise;
  }

  function nearestGlobalAirport(lat, lon) {
    if (!Array.isArray(globalAirports) || !globalAirports.length) return null;
    let best = null;
    let bestMiles = Number.POSITIVE_INFINITY;
    globalAirports.forEach((airport) => {
      const miles = haversineMiles(lat, lon, airport.lat, airport.lon);
      if (miles < bestMiles) {
        bestMiles = miles;
        best = { ...airport, miles };
      }
    });
    return best;
  }

  function airportSizeRank(airport) {
    const size = String(airport?.size || "").toLowerCase();
    if (size === "large" || size === "large_airport") return 3;
    if (size === "medium" || size === "medium_airport") return 2;
    if (size === "small" || size === "small_airport") return 1;
    return 0;
  }

  function findGlobalAirportByCode(code) {
    const needle = String(code || "").trim().toUpperCase();
    if (!needle || !Array.isArray(globalAirports) || !globalAirports.length) return null;
    const airport = globalAirports.find((entry) => String(entry?.iata || "").trim().toUpperCase() === needle);
    return airport ? { ...airport, miles: 0 } : null;
  }

  function normalizeRegionText(...values) {
    return values
      .map((value) => String(value || "").trim().toUpperCase())
      .filter(Boolean)
      .join(" | ");
  }

  function marinaRegionText(item) {
    return normalizeRegionText(
      item?.portfolio?.stateCountry,
      item?.portfolio?.country,
      item?.properties?.stateCountry,
      item?.properties?.country,
      item?.properties?.Country,
    );
  }

  function isAirTriggerRegion(item) {
    const regionText = marinaRegionText(item);
    return AIR_TRIGGER_REGIONS.some((region) => regionText.includes(region));
  }

  function resolveTripTravelMode() {
    const requested = String(state.planning.travelMode || "auto").toLowerCase();
    if (requested === "drive" || requested === "air") return requested;
    return "auto";
  }

  function resolveOfficeAirport(office) {
    if (!office) return null;
    return findGlobalAirportByCode(office.airportCode) || nearestGlobalAirport(office.lat, office.lng);
  }

  function estimateAirLeg(from, to, { originAirport = null, destAirport = null } = {}) {
    const resolvedOriginAirport = originAirport || nearestGlobalAirport(from.lat, from.lng);
    const resolvedDestAirport = destAirport || nearestGlobalAirport(to.lat, to.lng);
    const airMiles = resolvedOriginAirport && resolvedDestAirport
      ? haversineMiles(resolvedOriginAirport.lat, resolvedOriginAirport.lon, resolvedDestAirport.lat, resolvedDestAirport.lon)
      : milesBetween(from.lat, from.lng, to.lat, to.lng);
    const overheadHours = Math.max(0, Number(state.planning.airOverheadHours) || 4);
    const cruiseMph = Math.max(100, Number(state.planning.airCruiseMph) || 450);
    const originGroundMiles = resolvedOriginAirport
      ? milesBetween(from.lat, from.lng, resolvedOriginAirport.lat, resolvedOriginAirport.lon)
      : 0;
    const destGroundMiles = resolvedDestAirport
      ? milesBetween(to.lat, to.lng, resolvedDestAirport.lat, resolvedDestAirport.lon)
      : 0;
    const groundMinutes = estimateDriveMinutes(originGroundMiles + destGroundMiles);
    const fares = estimateFare(
      airMiles,
      airportTypeAdj(resolvedOriginAirport?.size),
      airportTypeAdj(resolvedDestAirport?.size),
    );
    return {
      miles: originGroundMiles + destGroundMiles,
      driveMinutes: groundMinutes + (overheadHours * 60) + ((airMiles / cruiseMph) * 60),
      source: "air",
      airMiles,
      airfare: fares.roundTrip * DEFAULT_CREW_SIZE,
      originAirport: resolvedOriginAirport,
      destAirport: resolvedDestAirport,
    };
  }

  function marinaMissionRegionKey(item) {
    return normalizeRegionText(
      item?.portfolio?.country,
      item?.portfolio?.stateCountry,
      item?.properties?.country,
      item?.properties?.Country,
      item?.properties?.stateCountry,
    );
  }

  function missionGroupCentroid(items) {
    if (!Array.isArray(items) || !items.length) return null;
    const sum = items.reduce((acc, item) => {
      acc.lat += Number(item?.lat) || 0;
      acc.lng += Number(item?.lng) || 0;
      return acc;
    }, { lat: 0, lng: 0 });
    return {
      lat: sum.lat / items.length,
      lng: sum.lng / items.length,
    };
  }

  function preferredMissionAirportCode(items) {
    const regionKey = marinaMissionRegionKey(items?.[0]);
    if (!regionKey) return "";
    if (regionKey.includes("PUERTO RICO")) return "SJU";
    if (/ST\.?\s*KITTS|SAINT KITTS|ST KITTS/.test(regionKey)) return "NEV";
    return "";
  }

  function resolveMissionAirport(items) {
    if (!Array.isArray(globalAirports) || !globalAirports.length || !Array.isArray(items) || !items.length) return null;
    const preferredCode = preferredMissionAirportCode(items);
    if (preferredCode) {
      const preferred = findGlobalAirportByCode(preferredCode);
      if (preferred) return preferred;
    }
    const centroid = missionGroupCentroid(items);
    if (!centroid) return null;
    const regionKey = marinaMissionRegionKey(items[0]);
    const countryToken = String(items[0]?.portfolio?.country || items[0]?.properties?.country || items[0]?.properties?.Country || "").trim().toUpperCase();
    const regionCandidates = globalAirports.filter((airport) => {
      const airportCountry = String(airport?.country || "").trim().toUpperCase();
      if (countryToken && airportCountry === countryToken) return true;
      if (regionKey && regionKey.includes("PUERTO RICO") && airportCountry === "PR") return true;
      if (regionKey && /ST\.?\s*KITTS|SAINT KITTS|ST KITTS/.test(regionKey) && airportCountry === "KN") return true;
      return false;
    });
    const candidates = regionCandidates.length ? regionCandidates : globalAirports;
    const preferred = candidates.filter((airport) => airportSizeRank(airport) >= 2);
    const pool = preferred.length ? preferred : candidates;
    let best = null;
    let bestScore = Number.POSITIVE_INFINITY;
    pool.forEach((airport) => {
      const distance = haversineMiles(centroid.lat, centroid.lng, airport.lat, airport.lon);
      const sizeBias = airportSizeRank(airport) >= 2 ? 0 : 80;
      const score = distance + sizeBias;
      if (score < bestScore) {
        bestScore = score;
        best = { ...airport, miles: distance };
      }
    });
    return best;
  }

  function areMissionMarinasConnected(a, b, nodeIndexById, scopeKey) {
    const aIndex = nodeIndexById.get(a?.id) || 0;
    const bIndex = nodeIndexById.get(b?.id) || 0;
    if (aIndex > 0 && bIndex > 0 && routeMatrixStatus === "ready" && routeMatrixKey === scopeKey) {
      return hasRoutedConnectivity(scopeKey, aIndex, bIndex) || hasRoutedConnectivity(scopeKey, bIndex, aIndex);
    }
    const regionA = marinaMissionRegionKey(a);
    const regionB = marinaMissionRegionKey(b);
    if (regionA && regionA === regionB) return true;
    return milesBetween(a?.lat, a?.lng, b?.lat, b?.lng) <= 150;
  }

  function buildAirMissionGroups(items, nodeIndexById, scopeKey) {
    const remaining = new Set(items.map((item) => item.id));
    const byId = new Map(items.map((item) => [item.id, item]));
    const groups = [];
    while (remaining.size) {
      const seedId = remaining.values().next().value;
      const seed = byId.get(seedId);
      const queue = seed ? [seed] : [];
      const group = [];
      remaining.delete(seedId);
      while (queue.length) {
        const current = queue.shift();
        group.push(current);
        Array.from(remaining).forEach((candidateId) => {
          const candidate = byId.get(candidateId);
          if (!candidate) return;
          if (!areMissionMarinasConnected(current, candidate, nodeIndexById, scopeKey)) return;
          remaining.delete(candidateId);
          queue.push(candidate);
        });
      }
      if (group.length) groups.push(group);
    }
    return groups;
  }

  function hasRoutedConnectivity(scopeKey, nodeIndexA, nodeIndexB) {
    if (routeMatrixStatus !== "ready" || routeMatrixKey !== scopeKey) return false;
    const value = routeMatrixDurations?.[nodeIndexA]?.[nodeIndexB];
    return Number.isFinite(Number(value));
  }

  function formatDriveMinutes(minutes) {
    const total = Math.max(0, Math.round(Number(minutes) || 0));
    if (total < 60) return `${total} min`;
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    return mins ? `${hours}h ${mins}m` : `${hours}h`;
  }

  function formatMiles(miles) {
    return `${(Number(miles) || 0).toFixed(1)} mi`;
  }

  function useMapCenterForOfficeDraft() {
    const center = map.getCenter?.();
    if (!center) return;
    state.officeDraft = {
      ...state.officeDraft,
      lat: Number(center.lat).toFixed(6),
      lng: Number(center.lng).toFixed(6),
    };
    render();
  }

  function addOffice() {
    const name = String(state.officeDraft.name || "").trim();
    const lat = Number(state.officeDraft.lat);
    const lng = Number(state.officeDraft.lng);
    if (!name || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      app.ui.toast("Office name, latitude, and longitude are required.");
      return;
    }
    state.offices.push({
      id: `office-${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 6)}`,
      name,
      lat,
      lng,
      airportCode: "",
    });
    if (!state.planning.tripOfficeId) state.planning.tripOfficeId = state.offices[state.offices.length - 1]?.id || "";
    state.officeDraft = { name: "", lat: "", lng: "" };
    recomputePlanning();
    drawPlanningOverlay(currentItem());
    render();
  }

  function removeOffice(index) {
    if (index < 0 || index >= state.offices.length) return;
    const removed = state.offices[index];
    state.offices.splice(index, 1);
    state.selectedOfficeIds = state.selectedOfficeIds.filter((id) => id !== removed?.id);
    state.visibleOfficeMarinaIds = state.visibleOfficeMarinaIds.filter((id) => id !== removed?.id);
    if (state.planning.tripOfficeId === removed?.id) {
      state.planning.tripOfficeId = state.offices[0]?.id || "";
    }
    recomputePlanning();
    persistLocalState();
    drawPlanningOverlay(currentItem());
    render();
  }

  function toggleOfficeSelection(index) {
    const office = state.offices[index];
    if (!office) return;
    if (state.selectedOfficeIds.includes(office.id)) {
      state.selectedOfficeIds = state.selectedOfficeIds.filter((id) => id !== office.id);
    } else {
      state.selectedOfficeIds = [...state.selectedOfficeIds, office.id];
    }
    drawPlanningOverlay(currentItem());
    render();
  }

  function toggleOfficeMarinaVisibility(index) {
    const office = state.offices[index];
    if (!office) return;
    const isOn = state.visibleOfficeMarinaIds.includes(office.id);
    if (isOn) {
      state.visibleOfficeMarinaIds = state.visibleOfficeMarinaIds.filter((id) => id !== office.id);
      state.selectedOfficeIds = state.selectedOfficeIds.filter((id) => id !== office.id);
    } else {
      state.visibleOfficeMarinaIds = [...state.visibleOfficeMarinaIds, office.id];
      if (!state.selectedOfficeIds.includes(office.id)) {
        state.selectedOfficeIds = [...state.selectedOfficeIds, office.id];
      }
    }
    persistLocalState();
    drawPlanningOverlay(currentItem());
    render();
  }

  function selectedOfficeColorMap() {
    const mapById = new Map();
    state.selectedOfficeIds.forEach((id, index) => {
      mapById.set(id, OFFICE_SELECTION_COLORS[index % OFFICE_SELECTION_COLORS.length]);
    });
    return mapById;
  }

  function buildNeighborhoods(items) {
    const spacingMiles = Math.max(0.25, Number(state.planning.clusterSpacingMiles) || 70);
    const visited = new Set();
    const neighborhoods = [];
    items.forEach((seed) => {
      if (visited.has(seed.id)) return;
      const queue = [seed];
      const group = [];
      visited.add(seed.id);
      while (queue.length) {
        const current = queue.shift();
        group.push(current);
        items.forEach((candidate) => {
          if (visited.has(candidate.id)) return;
          const spacing = milesBetween(current.lat, current.lng, candidate.lat, candidate.lng);
          if (spacing > spacingMiles) return;
          visited.add(candidate.id);
          queue.push(candidate);
        });
      }
      neighborhoods.push(group);
    });
    return neighborhoods;
  }

  function neighborhoodCentroid(items) {
    if (!items.length) return null;
    const sum = items.reduce((acc, item) => {
      acc.lat += Number(item.lat) || 0;
      acc.lng += Number(item.lng) || 0;
      return acc;
    }, { lat: 0, lng: 0 });
    return {
      lat: sum.lat / items.length,
      lng: sum.lng / items.length,
    };
  }

  function neighborhoodSpreadMiles(items, centroid) {
    if (!items.length || !centroid) return 0;
    return items.reduce((max, item) => Math.max(max, milesBetween(item.lat, item.lng, centroid.lat, centroid.lng)), 0);
  }

  function scoreOfficeForNeighborhood(office, items) {
    const centroid = neighborhoodCentroid(items);
    if (!office || !centroid) return Number.POSITIVE_INFINITY;
    const officeToCentroid = milesBetween(office.lat, office.lng, centroid.lat, centroid.lng);
    const spread = neighborhoodSpreadMiles(items, centroid);
    return officeToCentroid + (spread * 0.35);
  }

  function assignOfficeToNeighborhood(items) {
    if (!state.offices.length) return null;
    let bestOffice = null;
    let bestScore = Number.POSITIVE_INFINITY;
    state.offices.forEach((office) => {
      const score = scoreOfficeForNeighborhood(office, items);
      if (score < bestScore) {
        bestScore = score;
        bestOffice = office;
      }
    });
    return bestOffice;
  }

  function splitNeighborhoodIntoClusters(items, office) {
    const maxMarinas = Math.max(1, Math.round(Number(state.planning.maxMarinasPerCluster) || 5));
    const sorted = items
      .slice()
      .sort((a, b) => (a.officeDriveMinutes || 0) - (b.officeDriveMinutes || 0));
    const clusters = [];
    for (let i = 0; i < sorted.length; i += maxMarinas) {
      clusters.push({
        officeId: office?.id || "",
        officeName: office?.name || "",
        items: sorted.slice(i, i + maxMarinas),
      });
    }
    return clusters;
  }

  function recomputePlanning() {
    clearEnrichedTripPlan();
    state.items.forEach((item) => {
      item.officeId = "";
      item.officeName = "";
      item.officeDistanceMiles = null;
      item.officeDriveMinutes = null;
      item.clusterId = "";
      item.clusterLabel = "";
      item.neighborhoodId = "";
    });

    if (!state.offices.length) return;

    const neighborhoods = buildNeighborhoods(state.items);
    neighborhoods.forEach((group, neighborhoodIndex) => {
      const office = assignOfficeToNeighborhood(group);
      group.forEach((item) => {
        item.neighborhoodId = `neighborhood-${neighborhoodIndex + 1}`;
        item.officeId = office?.id || "";
        item.officeName = office?.name || "";
        if (office) {
          const miles = milesBetween(item.lat, item.lng, office.lat, office.lng);
          item.officeDistanceMiles = miles;
          item.officeDriveMinutes = estimateDriveMinutes(miles);
        }
      });
    });

    let clusterSeq = 1;
    const byNeighborhood = new Map();
    state.items.forEach((item) => {
      const key = item.neighborhoodId || "none";
      const bucket = byNeighborhood.get(key) || [];
      bucket.push(item);
      byNeighborhood.set(key, bucket);
    });
    [...byNeighborhood.values()].forEach((group) => {
      const office = state.offices.find((entry) => entry.id === group[0]?.officeId) || null;
      const clusters = splitNeighborhoodIntoClusters(group, office);
      clusters.forEach((cluster) => {
        const label = `${cluster.officeName || "Unassigned"}-${clusterSeq}`;
        cluster.items.forEach((item) => {
          item.clusterId = `cluster-${clusterSeq}`;
          item.clusterLabel = label;
        });
        clusterSeq += 1;
      });
    });
  }

  function clusterSummaries() {
    const mapByLabel = new Map();
    state.items.forEach((item) => {
      if (!item.clusterLabel) return;
      const current = mapByLabel.get(item.clusterLabel) || {
        label: item.clusterLabel,
        officeName: item.officeName || "Unassigned",
        count: 0,
        selectedCount: 0,
        totalDriveMinutes: 0,
      };
      current.count += 1;
      current.selectedCount += item.selected ? 1 : 0;
      current.totalDriveMinutes += Number(item.officeDriveMinutes) || 0;
      mapByLabel.set(item.clusterLabel, current);
    });
    return [...mapByLabel.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }));
  }

  function zoomToCluster(label) {
    const clusterItems = state.items.filter((item) => item.clusterLabel === label);
    if (!clusterItems.length) return;
    const officeName = clusterItems[0]?.officeName || "";
    const office = state.offices.find((entry) => entry.name === officeName) || null;
    const points = clusterItems.map((item) => [Number(item.lat), Number(item.lng)]);
    if (office) points.push([Number(office.lat), Number(office.lng)]);
    if (!points.length) return;
    const bounds = L.latLngBounds(points);
    if (!bounds.isValid()) return;
    suppressAutoSaveFor();
    map.fitBounds(bounds.pad(0.18), { animate: false });
    map.invalidateSize(false);
    drawPlanningOverlay(currentItem());
  }

  function officeSummaries() {
    return state.offices.map((office) => {
      const items = state.items.filter((item) => item.officeId === office.id);
      const selected = items.filter((item) => item.selected).length;
      const totalDriveMinutes = items.reduce((sum, item) => sum + (Number(item.officeDriveMinutes) || 0), 0);
      const roadCount = items.filter((item) => !isAirMissionPriority(item?.prioritizeAirTravel)).length;
      const airCount = items.filter((item) => isAirMissionPriority(item?.prioritizeAirTravel)).length;
      return {
        id: office.id,
        name: office.name,
        count: items.length,
        selected,
        totalDriveMinutes,
        roadCount,
        airCount,
      };
    });
  }

  function zoomToOfficeCoverage(officeId) {
    const bounds = officeCoverageBounds(officeId, { selectedOnly: false });
    if (!bounds) return;
    suppressAutoSaveFor();
    map.fitBounds(bounds, { animate: false });
    map.invalidateSize(false);
    drawPlanningOverlay(currentItem());
  }

  function fitMapToTripPlaybackDay(plan, tripDayIndex) {
    const day = Array.isArray(plan?.days) && tripDayIndex >= 0 ? plan.days[tripDayIndex] || null : null;
    if (!day) return;
    const coords = [];
    if (day.startMode !== "hotel" && plan?.office) coords.push([Number(plan.office.lat), Number(plan.office.lng)]);
    if (Array.isArray(day.stops)) {
      day.stops.forEach((stop) => {
        if (Number.isFinite(Number(stop?.item?.lat)) && Number.isFinite(Number(stop?.item?.lng))) {
          coords.push([Number(stop.item.lat), Number(stop.item.lng)]);
        }
      });
    }
    routeGeometryFeatures
      .filter((feature) => Number(feature.day) === Number(day.day))
      .forEach((feature) => {
        (feature.latlngs || []).forEach((latlng) => {
          if (Array.isArray(latlng) && latlng.length >= 2) coords.push([Number(latlng[0]), Number(latlng[1])]);
        });
      });
    if (!coords.length) return;
    const bounds = L.latLngBounds(coords);
    if (!bounds.isValid()) return;
    suppressAutoSaveFor();
    map.fitBounds(bounds.pad(coords.length === 1 ? 0.2 : 0.12), { animate: false, padding: [24, 24] });
    map.invalidateSize(false);
  }

  function syncTripPlaybackOverlay(plan, { fit = false } = {}) {
    const tripDayIndex = normalizedTripPlaybackDayIndex(plan);
    state.tripPlaybackDayIndex = tripDayIndex;
    drawPlanningOverlay(tripDayIndex >= 0 ? null : currentItem(), {
      tripPlan: plan,
      tripDayIndex,
      routeScopeKey: String(plan?.routeScopeKey || plan?.routing?.scopeKey || ""),
    });
    if (fit) fitMapToTripPlaybackDay(plan, tripDayIndex);
  }

  function focusTripPlaybackRelative(step) {
    const plan = buildTripPlan();
    const dayCount = Array.isArray(plan?.days) ? plan.days.length : 0;
    if (!dayCount) return;
    const currentIndex = normalizedTripPlaybackDayIndex(plan);
    const nextIndex = Math.max(0, Math.min(dayCount - 1, currentIndex + step));
    state.tripPlaybackDayIndex = nextIndex;
    syncTripPlaybackOverlay(plan, { fit: state.tripPlaybackZoomToDay === true });
    render();
    const day = plan.days[nextIndex];
    setStatus(`Trip playback Day ${day?.day || (nextIndex + 1)}${day?.travelOnly ? `: ${day.travelLabel || "travel only"}` : day?.stops?.length ? `: ${day.stops.map((stop) => stop.item?.portfolio?.code || stop.item?.name).filter(Boolean).join(", ")}` : ""}`);
  }

  function officeCoverageBounds(officeId, { selectedOnly = true, excludeReportExcluded = false, missionScope = "all" } = {}) {
    const office = state.offices.find((entry) => entry.id === officeId);
    if (!office) return null;
    const points = [[Number(office.lat), Number(office.lng)]];
    scopeForOffice(officeId, { excludeReportExcluded, missionScope }).marinas
      .filter((item) => (!selectedOnly || item.selected))
      .forEach((item) => {
        points.push([Number(item.lat), Number(item.lng)]);
      });
    const bounds = L.latLngBounds(points);
    return bounds.isValid() ? bounds.pad(0.18) : null;
  }

  async function prepareOfficeCoverageCapture(officeId, { excludeReportExcluded = false, missionScope = "all", routeScopeKey: routeScopeKeyOverride = "" } = {}) {
    const bounds = officeCoverageBounds(officeId, { selectedOnly: false, excludeReportExcluded, missionScope });
    if (bounds) {
      map.fitBounds(bounds, { animate: false });
      map.invalidateSize(false);
    }
    await delay(120);
    map.invalidateSize({ pan: false, debounceMoveend: false });
    await delay(120);
    const toggledOfficeIds = Array.isArray(state.visibleOfficeMarinaIds) ? state.visibleOfficeMarinaIds.filter(Boolean) : [];
    const overlayItems = toggledOfficeIds.length
      ? state.items.filter((entry) => (
          toggledOfficeIds.includes(entry.officeId)
          && (!excludeReportExcluded || entry.excludeFromReport !== true)
        ))
      : scopeForOffice(officeId, { excludeReportExcluded, missionScope }).marinas;
    drawPlanningOverlay(currentItem(), {
      items: overlayItems,
      routeScopeKey: routeScopeKeyOverride,
    });
    if (state.showTripRoute && routeScopeKeyOverride && routeGeometryKey === routeScopeKeyOverride && routeGeometryFeatures.length) {
      const waitStart = Date.now();
      while ((Date.now() - waitStart) < 6000) {
        const drawnRouteCount = ensureCaptureLayer().getLayers().filter((entry) => entry instanceof L.Polyline).length;
        if (drawnRouteCount >= routeGeometryFeatures.length) break;
        drawPlanningOverlay(currentItem(), {
          items: overlayItems,
          routeScopeKey: routeScopeKeyOverride,
        });
        await delay(120);
      }
    }
    await waitForMapToSettle({ timeoutMs: 12000, stableMs: 700, pollMs: 100 });
    if (state.showTripRoute && routeScopeKeyOverride && routeGeometryKey === routeScopeKeyOverride && routeGeometryFeatures.length) {
      await delay(250);
    }
  }

  function clearRouteMatrix() {
    routeMatrixKey = "";
    routeMatrixDurations = null;
    routeMatrixDistances = null;
    routeMatrixStatus = "idle";
    routeMatrixError = "";
    routeMatrixRequestId += 1;
    clearRouteGeometry();
    clearEnrichedTripPlan();
  }

  function clearRouteGeometry() {
    routeGeometryKey = "";
    routeGeometryStatus = "idle";
    routeGeometryError = "";
    routeGeometryFeatures = [];
  }

  function scopeForOffice(officeId = state.planning.tripOfficeId, { excludeReportExcluded = false, missionScope = "all" } = {}) {
    const office = state.offices.find((entry) => entry.id === officeId) || state.offices[0] || null;
    const marinas = state.items.filter((item) => (
      item.selected
      && item.officeId === office?.id
      && (!excludeReportExcluded || item.excludeFromReport !== true)
      && (
        missionScope === "all"
        || (missionScope === "road" && !isAirMissionPriority(item.prioritizeAirTravel))
        || (missionScope === "air" && isAirMissionPriority(item.prioritizeAirTravel))
      )
    ));
    return { office, marinas };
  }

  function activeTripScope() {
    return scopeForOffice(state.planning.tripOfficeId);
  }

  function routeScopeKey(office, marinas) {
    return `${office?.id || "none"}|${marinas.map((item) => item.id).join(",")}`;
  }

  function tripRouteGeometryKey(scopeKey, route = [], days = []) {
    const routeSig = Array.isArray(route)
      ? route.map((stop) => `${stop?.item?.id || "none"}:${stop?.driveSource || "estimated"}`).join(",")
      : "";
    const daySig = Array.isArray(days)
      ? days.map((day) => {
        const stopSig = Array.isArray(day?.stops)
          ? day.stops.map((stop) => `${stop?.item?.id || "none"}:${stop?.driveSource || "estimated"}`).join(",")
          : "";
        return [
          day?.day || 0,
          day?.startMode || "office",
          day?.endMode || "field",
          day?.travelOnly ? 1 : 0,
          day?.travelSource || "survey",
          stopSig,
          day?.overnightAnchorItem?.id || "none",
        ].join(":");
      }).join("|")
      : "";
    return `${scopeKey || "none"}|route=${routeSig}|days=${daySig}`;
  }

  function currentTripRouteScopeKey() {
    const plan = buildTripPlan(state.planning.tripOfficeId, { excludeReportExcluded: false });
    return String(plan?.routeScopeKey || plan?.routing?.scopeKey || "");
  }

  async function refreshRouteMatrix({ force = false, officeId = state.planning.tripOfficeId, excludeReportExcluded = false, missionScope = "all" } = {}) {
    const { office, marinas } = scopeForOffice(officeId, { excludeReportExcluded, missionScope });
    if (!office || !marinas.length) {
      clearRouteMatrix();
      render();
      return;
    }
    const key = routeScopeKey(office, marinas);
    if (!force && routeMatrixStatus === "ready" && routeMatrixKey === key && routeMatrixDurations) return;
    if (!force && routeMatrixStatus === "loading" && routeMatrixKey === key) return;
    if (marinas.length + 1 > 90) {
      routeMatrixKey = key;
      routeMatrixDurations = null;
      routeMatrixDistances = null;
      routeMatrixStatus = "fallback";
      routeMatrixError = "Too many stops for live routed matrix; using estimated travel.";
      render();
      return;
    }
    routeMatrixKey = key;
    routeMatrixStatus = "loading";
    routeMatrixError = "";
    routeMatrixDurations = null;
    routeMatrixDistances = null;
    const requestId = routeMatrixRequestId + 1;
    routeMatrixRequestId = requestId;
    render();
    try {
      const coords = [[office.lng, office.lat], ...marinas.map((item) => [item.lng, item.lat])]
        .map(([lng, lat]) => `${Number(lng)},${Number(lat)}`)
        .join(";");
      const url = `https://router.project-osrm.org/table/v1/driving/${coords}?annotations=duration,distance`;
      const response = await fetch(url, { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error(`Routing HTTP ${response.status}`);
      const payload = await response.json();
      if (requestId !== routeMatrixRequestId) return;
      if (payload?.code !== "Ok" || !Array.isArray(payload?.durations)) {
        throw new Error(payload?.message || "Routing matrix unavailable");
      }
      routeMatrixDurations = payload.durations;
      routeMatrixDistances = Array.isArray(payload.distances) ? payload.distances : null;
      routeMatrixStatus = "ready";
      routeMatrixError = "";
      render();
    } catch (error) {
      if (requestId !== routeMatrixRequestId) return;
      console.warn("[ImageGenerator] routed matrix failed", error);
      routeMatrixDurations = null;
      routeMatrixDistances = null;
      routeMatrixStatus = "fallback";
      routeMatrixError = error?.message || "Routing unavailable; using estimated travel.";
      render();
    }
  }

  function estimateLeg(a, b) {
    const miles = milesBetween(a.lat, a.lng, b.lat, b.lng);
    return {
      miles,
      driveMinutes: estimateDriveMinutes(miles),
    };
  }

  function formatCurrency(amount) {
    const value = Number(amount) || 0;
    return value.toLocaleString(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function formatMilesPrecise(miles) {
    return `${(Number(miles) || 0).toFixed(1)} mi`;
  }

  function readTravelPlanningGsaApiKey() {
    const localKey = String(state?.planning?.gsaApiKey || "").trim();
    if (localKey) return localKey;
    try {
      const raw = localStorage.getItem(TRAVEL_PLANNING_STATE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return String(parsed?.gsaApiKey || "").trim();
    } catch {
      return "";
    }
  }

  function writeTravelPlanningGsaApiKey(value) {
    const nextValue = String(value || "").trim();
    try {
      const raw = localStorage.getItem(TRAVEL_PLANNING_STATE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      localStorage.setItem(TRAVEL_PLANNING_STATE_KEY, JSON.stringify({
        ...(parsed && typeof parsed === "object" ? parsed : {}),
        gsaApiKey: nextValue,
      }));
    } catch {}
  }

  function normalizeUsStateCode(stateCodeRaw, stateNameRaw = "") {
    const code = String(stateCodeRaw || "").trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(code)) return code;
    if (/^US-[A-Z]{2}$/.test(code)) return code.slice(3);
    const codeMatches = String(stateCodeRaw || "").toUpperCase().match(/\b([A-Z]{2})\b/g) || [];
    const extractedCode = codeMatches.find((entry) => entry !== "US");
    if (extractedCode && /^[A-Z]{2}$/.test(extractedCode)) return extractedCode;
    const name = String(stateNameRaw || "").trim().toUpperCase();
    const byName = {
      ALABAMA: "AL", ALASKA: "AK", ARIZONA: "AZ", ARKANSAS: "AR", CALIFORNIA: "CA", COLORADO: "CO",
      CONNECTICUT: "CT", DELAWARE: "DE", "DISTRICT OF COLUMBIA": "DC", FLORIDA: "FL", GEORGIA: "GA",
      HAWAII: "HI", IDAHO: "ID", ILLINOIS: "IL", INDIANA: "IN", IOWA: "IA", KANSAS: "KS", KENTUCKY: "KY",
      LOUISIANA: "LA", MAINE: "ME", MARYLAND: "MD", MASSACHUSETTS: "MA", MICHIGAN: "MI", MINNESOTA: "MN",
      MISSISSIPPI: "MS", MISSOURI: "MO", MONTANA: "MT", NEBRASKA: "NE", NEVADA: "NV", "NEW HAMPSHIRE": "NH",
      "NEW JERSEY": "NJ", "NEW MEXICO": "NM", "NEW YORK": "NY", "NORTH CAROLINA": "NC", "NORTH DAKOTA": "ND",
      OHIO: "OH", OKLAHOMA: "OK", OREGON: "OR", PENNSYLVANIA: "PA", "RHODE ISLAND": "RI", "SOUTH CAROLINA": "SC",
      "SOUTH DAKOTA": "SD", TENNESSEE: "TN", TEXAS: "TX", UTAH: "UT", VERMONT: "VT", VIRGINIA: "VA",
      WASHINGTON: "WA", "WEST VIRGINIA": "WV", WISCONSIN: "WI", WYOMING: "WY",
      "PUERTO RICO": "PR", "U.S. VIRGIN ISLANDS": "VI", "US VIRGIN ISLANDS": "VI", "VIRGIN ISLANDS": "VI",
      GUAM: "GU", "NORTHERN MARIANA ISLANDS": "MP", "AMERICAN SAMOA": "AS",
    };
    return byName[name] || "";
  }

  function firstNumber(obj, keys) {
    for (const key of keys) {
      const val = obj?.[key];
      const n = Number(val);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  }

  function normalizeRateCity(value) {
    return String(value || "")
      .toUpperCase()
      .replace(/\([^)]*\)/g, " ")
      .replace(/[^A-Z0-9]+/g, " ")
      .replace(/\bSAINT\b/g, "ST")
      .replace(/\bFT\b/g, "FORT")
      .replace(/\s+/g, " ")
      .trim();
  }

  function isConusState(stateCode) {
    const code = String(stateCode || "").trim().toUpperCase();
    return /^[A-Z]{2}$/.test(code) && !["AK", "HI", "PR", "VI", "GU", "AS", "MP"].includes(code);
  }

  function isDodNonForeignState(stateCode) {
    return ["PR", "VI", "AK", "HI", "GU", "AS", "MP"].includes(String(stateCode || "").trim().toUpperCase());
  }

  function isForeignPerDiemCandidate(item) {
    const country = String(item?.portfolio?.stateCountry || item?.properties?.stateCountry || item?.properties?.country || item?.properties?.Country || "").trim().toUpperCase();
    if (!country) return false;
    if (country === "PUERTO RICO" || country === "U.S. VIRGIN ISLANDS" || country === "US VIRGIN ISLANDS" || country === "VIRGIN ISLANDS") return false;
    return !/^[A-Z]{2}$/.test(country) || !["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","PR","VI","GU","AS","MP"].includes(country);
  }

  function monthInWrappedRange(month, startMonth, endMonth) {
    const m = Number(month);
    const start = Number(startMonth);
    const end = Number(endMonth);
    if (!Number.isFinite(m) || !Number.isFinite(start) || !Number.isFinite(end)) return false;
    if (start <= end) return m >= start && m <= end;
    return m >= start || m <= end;
  }

  function lookupLocalConusPerDiem(city, stateCode, month) {
    if (!isConusState(stateCode) || !Array.isArray(gsaConusRates) || !gsaConusRates.length) return null;
    const cityNorm = normalizeRateCity(city);
    const stateNorm = String(stateCode || "").trim().toUpperCase();
    const candidates = gsaConusRates.filter((entry) => String(entry?.state || "").toUpperCase() === stateNorm);
    if (!candidates.length) return null;
    let best = null;
    let bestScore = -1;
    candidates.forEach((entry) => {
      const entryCityNorm = normalizeRateCity(entry?.city || "");
      let score = 0;
      if (entryCityNorm === cityNorm) score += 10;
      else if (entryCityNorm.includes(cityNorm) || cityNorm.includes(entryCityNorm)) score += 5;
      if (score > bestScore) {
        bestScore = score;
        best = entry;
      }
    });
    if (!best || bestScore < 0) return null;
    const lodging = Number(best?.months?.[String(month)] ?? best?.months?.[String(((month - 1 + 12) % 12) + 1)]);
    const mie = Number(best?.mie);
    if (!Number.isFinite(lodging) || !Number.isFinite(mie)) return null;
    return {
      ok: true,
      fallback: false,
      source: "gsa-local",
      reason: "",
      lodging,
      mie,
    };
  }

  function lookupLocalDodOconusPerDiem(city, stateCode, month) {
    const stateNorm = String(stateCode || "").trim().toUpperCase();
    if (!isDodNonForeignState(stateNorm) || !Array.isArray(dodOconusRates) || !dodOconusRates.length) return null;
    const cityNorm = normalizeRateCity(city);
    const candidates = dodOconusRates.filter((entry) => String(entry?.state || "").toUpperCase() === stateNorm);
    if (!candidates.length) return null;
    let best = null;
    let bestScore = -1;
    candidates.forEach((entry) => {
      const aliases = [entry?.city, ...(Array.isArray(entry?.aliases) ? entry.aliases : [])].map((value) => normalizeRateCity(value));
      let score = 0;
      if (aliases.includes(cityNorm)) score += 10;
      else if (aliases.some((alias) => alias && (alias.includes(cityNorm) || cityNorm.includes(alias)))) score += 5;
      else if (aliases.includes("OTHER")) score += 1;
      if (score > bestScore) {
        bestScore = score;
        best = entry;
      }
    });
    const period = Array.isArray(best?.periods) ? best.periods.find((entry) => monthInWrappedRange(month, entry.startMonth, entry.endMonth)) : null;
    if (!best || !period) return null;
    const lodging = Number(period?.lodging);
    const mie = Number(period?.mie);
    if (!Number.isFinite(lodging) || !Number.isFinite(mie)) return null;
    return {
      ok: true,
      fallback: false,
      source: "dod-oconus",
      reason: "",
      lodging,
      mie,
    };
  }

  function normalizeForeignCountry(value) {
    return String(value || "")
      .toUpperCase()
      .replace(/&/g, "AND")
      .replace(/\bST\b/g, "SAINT")
      .replace(/[^A-Z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function lookupLocalStateForeignPerDiem(item, month) {
    if (!isForeignPerDiemCandidate(item) || !Array.isArray(stateForeignRates) || !stateForeignRates.length) return null;
    const countryRaw = String(item?.portfolio?.stateCountry || item?.properties?.stateCountry || item?.properties?.country || item?.properties?.Country || "").trim();
    const cityRaw = String(item?.portfolio?.city || item?.properties?.city || item?.properties?.City || "").trim();
    const countryNorm = normalizeForeignCountry(countryRaw);
    const cityNorm = normalizeRateCity(cityRaw);
    if (!countryNorm) return null;
    const candidates = stateForeignRates.filter((entry) => normalizeForeignCountry(entry?.country || "") === countryNorm);
    if (!candidates.length) return null;
    let best = null;
    let bestScore = -1;
    candidates.forEach((entry) => {
      const locationNorm = normalizeRateCity(entry?.location || "");
      const seasonCode = String(entry?.seasonCode || "").trim().toUpperCase();
      let score = 0;
      if (cityNorm && locationNorm === cityNorm) score += 10;
      else if (cityNorm && (locationNorm.includes(cityNorm) || cityNorm.includes(locationNorm))) score += 5;
      else if (locationNorm === "OTHER") score += 1;
      if (seasonCode === "S1") score += 0.25;
      if (score > bestScore) {
        bestScore = score;
        best = entry;
      }
    });
    if (!best) return null;
    const startMonth = Number(String(best?.start || "").slice(5, 7));
    const endMonth = Number(String(best?.end || "").slice(5, 7));
    const inSeason = monthInWrappedRange(month, startMonth || month, endMonth || month);
    if (!inSeason && bestScore <= 1) return null;
    const lodging = Number(best?.lodging);
    const mie = Number(best?.mie);
    if (!Number.isFinite(lodging) || !Number.isFinite(mie)) return null;
    return {
      ok: true,
      fallback: false,
      source: "state-foreign",
      reason: "",
      lodging,
      mie,
    };
  }

  function parseGsaRatePayload(json, month, cityNorm = "", stateNorm = "") {
    const rates = Array.isArray(json?.rates) ? json.rates : [];
    let best = null;
    let bestScore = -1;
    for (const rateBlock of rates) {
      if (stateNorm && String(rateBlock?.state || "").toUpperCase() !== stateNorm) continue;
      const entries = Array.isArray(rateBlock?.rate) ? rateBlock.rate : (rateBlock?.rate ? [rateBlock.rate] : []);
      for (const entry of entries) {
        const city = String(entry?.city || "").trim().toUpperCase();
        const meals = firstNumber(entry, ["meals", "mie", "meals_incidental", "mealsIncidental", "me"]);
        const monthRows = Array.isArray(entry?.months?.month) ? entry.months.month : (entry?.months?.month ? [entry.months.month] : []);
        const exactMonth = monthRows.find((m) => Number(m?.number) === Number(month));
        const fallbackMonth = monthRows[0] || null;
        const lodging = Number(exactMonth?.value ?? fallbackMonth?.value);
        if (!Number.isFinite(lodging) || !Number.isFinite(meals)) continue;
        let score = 0;
        if (cityNorm && city && city === cityNorm) score += 10;
        if (String(entry?.standardRate || "").toLowerCase() === "false") score += 3;
        if (!city) score -= 2;
        if (score > bestScore) {
          bestScore = score;
          best = { lodging, mie: meals };
        }
      }
    }
    return best;
  }

  async function fetchGsaPerDiem(city, stateCode, apiKey, item = null) {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = now.getUTCMonth() + 1;
    const cityNorm = String(city || "").trim().toUpperCase();
    const stateNorm = String(stateCode || "").trim().toUpperCase();
    const isForeign = !!(item && isForeignPerDiemCandidate(item));
    if (isForeign) {
      await ensureStateForeignRatesLoaded();
      const foreignRate = lookupLocalStateForeignPerDiem(item, month);
      if (foreignRate) return foreignRate;
      if (!cityNorm) {
        return {
          ok: false,
          fallback: true,
          source: "fallback",
          reason: "missing foreign city/country",
          lodging: DEFAULT_CONUS_LODGING,
          mie: DEFAULT_CONUS_MIE,
        };
      }
    } else if (!cityNorm || !stateNorm) {
      return {
        ok: false,
        fallback: true,
        source: "fallback",
        reason: "missing city/state",
        lodging: DEFAULT_CONUS_LODGING,
        mie: DEFAULT_CONUS_MIE,
      };
    }
    if (isConusState(stateNorm)) {
      await ensureGsaConusRatesLoaded();
      const localRate = lookupLocalConusPerDiem(cityNorm, stateNorm, month);
      if (localRate) return localRate;
    }
    if (isDodNonForeignState(stateNorm)) {
      await ensureDodOconusRatesLoaded();
      const dodRate = lookupLocalDodOconusPerDiem(cityNorm, stateNorm, month);
      if (dodRate) return dodRate;
    }
    if (!apiKey) {
      return {
        ok: false,
        fallback: true,
        source: "fallback",
        reason: isConusState(stateNorm)
          ? "missing GSA API key and no local CONUS match"
          : isDodNonForeignState(stateNorm)
            ? "no local DoD OCONUS match"
            : isForeign
              ? "no local State foreign match"
              : "missing GSA API key",
        lodging: DEFAULT_CONUS_LODGING,
        mie: DEFAULT_CONUS_MIE,
      };
    }
    const urls = [
      `https://api.gsa.gov/travel/perdiem/v2/rates/city/${encodeURIComponent(cityNorm)}/state/${encodeURIComponent(stateNorm)}/year/${year}?api_key=${encodeURIComponent(apiKey)}`,
      `https://api.gsa.gov/travel/perdiem/v2/rates/city/${encodeURIComponent(cityNorm)}/year/${year}?api_key=${encodeURIComponent(apiKey)}`,
      `https://api.gsa.gov/travel/perdiem/v2/rates/state/${encodeURIComponent(stateNorm)}/year/${year}?api_key=${encodeURIComponent(apiKey)}`,
    ];
    const failures = [];
    for (const url of urls) {
      try {
        const response = await fetch(url, { headers: { Accept: "application/json" } });
        if (!response.ok) {
          failures.push(`HTTP ${response.status}`);
          continue;
        }
        const payload = await response.json();
        const parsed = parseGsaRatePayload(payload, month, cityNorm, stateNorm);
        if (parsed) return { ok: true, fallback: false, source: "gsa", reason: "", ...parsed };
        failures.push(payload?.message || "no matching GSA rate");
      } catch {}
    }
    return {
      ok: false,
      fallback: true,
      source: "fallback",
      reason: failures.length ? `GSA lookup failed (${uniqueNonEmpty(failures).join("; ")}); using CONUS fallback` : "GSA lookup failed; using CONUS fallback",
      lodging: DEFAULT_CONUS_LODGING,
      mie: DEFAULT_CONUS_MIE,
    };
  }

  function uniqueNonEmpty(list) {
    return Array.from(new Set((list || []).map((value) => String(value || "").trim()).filter(Boolean)));
  }

  function perDiemSourceSummary(plan) {
    const days = Array.isArray(plan?.days) ? plan.days.filter((day) => (Number(day?.perDiemCost) || 0) > 0) : [];
    if (!days.length) {
      const planDays = Array.isArray(plan?.days) ? plan.days : [];
      const overnightDays = planDays.filter((day) => day?.startMode === "hotel" || day?.endMode === "hotel");
      return {
        label: "Not used",
        reason: overnightDays.length
          ? "No per diem-eligible overnight days were priced."
          : "Trip has no hotel/overnight days.",
      };
    }
    const hasGsa = days.some((day) => String(day.perDiemSource || "").startsWith("gsa"));
    const hasDod = days.some((day) => String(day.perDiemSource || "") === "dod-oconus");
    const hasState = days.some((day) => String(day.perDiemSource || "") === "state-foreign");
    const hasFallback = days.some((day) => day.perDiemSource === "fallback");
    const reasons = uniqueNonEmpty(days.filter((day) => day.perDiemSource === "fallback").map((day) => day.perDiemReason));
    const positiveSources = [hasGsa, hasDod, hasState].filter(Boolean).length;
    if ((positiveSources > 1) || ((hasGsa || hasDod) && hasFallback)) {
      return {
        label: "Mixed",
        reason: reasons.join("; "),
      };
    }
    if (hasGsa) {
      return {
        label: "GSA location-based",
        reason: "",
      };
    }
    if (hasDod) {
      return {
        label: "DoD OCONUS",
        reason: "",
      };
    }
    if (hasState) {
      return {
        label: "State Foreign",
        reason: "",
      };
    }
    return {
      label: "CONUS fallback",
      reason: reasons.join("; ") || "fallback used",
    };
  }

  function officeCityState(office) {
    const raw = String(office?.name || "").trim();
    const parts = raw.split(",").map((part) => part.trim()).filter(Boolean);
    return {
      city: parts[0] || raw,
      stateCode: normalizeUsStateCode(parts[1] || "", parts[1] || ""),
    };
  }

  function officePerDiemLocation(office) {
    const raw = String(office?.name || "").trim();
    const parts = raw.split(",").map((part) => part.trim()).filter(Boolean);
    const city = parts[0] || raw;
    const regionRaw = parts.slice(1).join(", ");
    const stateCode = normalizeUsStateCode(regionRaw, regionRaw);
    return {
      city,
      stateCode,
      country: stateCode ? "" : regionRaw,
      label: [city, stateCode || regionRaw].filter(Boolean).join(", "),
      cacheKey: stateCode
        ? `us|${String(city || "").toUpperCase()}|${stateCode}`
        : `foreign|${String(city || "").toUpperCase()}|${String(regionRaw || "").toUpperCase()}`,
    };
  }

  function itemPerDiemLocation(item, office = null) {
    const city = String(item?.portfolio?.city || item?.properties?.city || item?.properties?.City || "").split(",")[0].trim();
    const stateRaw = String(item?.portfolio?.stateCountry || item?.properties?.state || item?.properties?.State || item?.properties?.country || item?.properties?.Country || "").trim();
    const stateCode = normalizeUsStateCode(stateRaw, stateRaw);
    if (city && stateCode) {
      return {
        city,
        stateCode,
        country: "",
        label: [city, stateCode].filter(Boolean).join(", "),
        cacheKey: `us|${String(city || "").toUpperCase()}|${stateCode}`,
      };
    }
    if (item && isForeignPerDiemCandidate(item)) {
      const country = stateRaw;
      if (city || country) {
        return {
          city: city || country,
          stateCode: "",
          country,
          label: [city, country].filter(Boolean).join(", "),
          cacheKey: `foreign|${String(city || "").toUpperCase()}|${String(country || "").toUpperCase()}`,
        };
      }
    }
    return officePerDiemLocation(office);
  }

  function itemCityState(item, office = null) {
    const city = String(item?.portfolio?.city || item?.properties?.city || item?.properties?.City || "").split(",")[0].trim();
    const stateRaw = String(item?.portfolio?.stateCountry || item?.properties?.state || item?.properties?.State || "").trim();
    const stateCode = normalizeUsStateCode(stateRaw, stateRaw);
    if (city && stateCode) return { city, stateCode };
    return officeCityState(office);
  }

  function computeWeatherContingencyDays(baseTripDays) {
    const pct = Math.max(0, Number(state.planning.weatherContingencyPct) || 0);
    const baseDays = Math.max(0, Math.round(Number(baseTripDays) || 0));
    if (!baseDays || !pct) return 0;
    return Math.ceil(baseDays * (pct / 100));
  }

  function routingStatusLabel(routing) {
    const mode = String(routing?.mode || "estimated").toLowerCase();
    const status = String(routing?.status || "idle").toLowerCase();
    const error = String(routing?.error || "").trim();
    if (mode === "routed" && status === "ready") return "Routed";
    if (status === "fallback") {
      if (/too many stops/i.test(error)) return "Estimated: too many stops";
      if (/routing unavailable/i.test(error)) return "Estimated: routing unavailable";
      return error ? `Estimated: ${error}` : "Estimated: fallback";
    }
    if (status === "loading") return "Estimated: routing pending";
    if (status === "idle") return mode === "routed" ? "Routed" : "Estimated: not requested";
    return error ? `${mode === "routed" ? "Routed" : "Estimated"}: ${error}` : (mode === "routed" ? "Routed" : "Estimated");
  }

  function statusTone(kind, value) {
    const text = String(value || "").toLowerCase();
    if (kind === "routing") {
      if (text.includes("routed")) return "good";
      if (text.includes("pending")) return "warn";
      return "bad";
    }
    if (kind === "perDiem") {
      if (text.includes("gsa") || text.includes("dod") || text.includes("state")) return "good";
      if (text.includes("mixed")) return "warn";
      if (text.includes("not used")) return "neutral";
      return "bad";
    }
    if (kind === "routeOverlay") {
      if (text.includes("shown")) return "good";
      if (text.includes("loading") || text.includes("pending")) return "warn";
      return "neutral";
    }
    return "neutral";
  }

  function statusBadgeHtml(label, value, kind) {
    const tone = statusTone(kind, value);
    const styles = {
      good: { bg: "rgba(34,197,94,0.14)", border: "rgba(34,197,94,0.35)", color: "#86efac" },
      warn: { bg: "rgba(245,158,11,0.14)", border: "rgba(245,158,11,0.35)", color: "#fcd34d" },
      bad: { bg: "rgba(239,68,68,0.14)", border: "rgba(239,68,68,0.35)", color: "#fca5a5" },
      neutral: { bg: "rgba(148,163,184,0.12)", border: "rgba(148,163,184,0.28)", color: "#cbd5e1" },
    }[tone];
    return `<div class="small" style="margin-top:4px;">${esc(label)}: <span style="display:inline-flex; align-items:center; gap:6px; padding:2px 8px; border-radius:999px; border:1px solid ${styles.border}; background:${styles.bg}; color:${styles.color}; font-weight:700;">${esc(value)}</span></div>`;
  }

  function routingBadge(officeId) {
    if (officeId !== state.planning.tripOfficeId) {
      return {
        text: "Not trip office",
        bg: "rgba(148,163,184,0.12)",
        border: "rgba(148,163,184,0.28)",
        color: "#94a3b8",
      };
    }
    const label = routingStatusLabel({
      mode: routeMatrixStatus === "ready" ? "routed" : "estimated",
      status: routeMatrixStatus,
      error: routeMatrixError,
    });
    if (routeMatrixStatus === "ready") {
      return {
        text: label,
        bg: "rgba(34,197,94,0.14)",
        border: "rgba(34,197,94,0.35)",
        color: "#86efac",
      };
    }
    if (routeMatrixStatus === "loading") {
      return {
        text: label,
        bg: "rgba(245,158,11,0.14)",
        border: "rgba(245,158,11,0.35)",
        color: "#fcd34d",
      };
    }
    if (routeMatrixStatus === "fallback") {
      return {
        text: label,
        bg: "rgba(239,68,68,0.14)",
        border: "rgba(239,68,68,0.35)",
        color: "#fca5a5",
      };
    }
    return {
      text: label,
      bg: "rgba(148,163,184,0.12)",
      border: "rgba(148,163,184,0.28)",
      color: "#cbd5e1",
    };
  }

  function officeReportScopes(officeId, { excludeReportExcluded = true } = {}) {
    const road = scopeForOffice(officeId, { excludeReportExcluded, missionScope: "road" });
    const air = scopeForOffice(officeId, { excludeReportExcluded, missionScope: "air" });
    const scopes = [];
    if (road.office && road.marinas.length) scopes.push({ key: "road", office: road.office, marinas: road.marinas });
    if (air.office && air.marinas.length) scopes.push({ key: "air", office: air.office, marinas: air.marinas });
    if (!scopes.length) {
      const all = scopeForOffice(officeId, { excludeReportExcluded, missionScope: "all" });
      if (all.office && all.marinas.length) scopes.push({ key: "all", office: all.office, marinas: all.marinas });
    }
    return scopes;
  }

  function airRegionLabel(items) {
    const regionText = normalizeRegionText(...(items || []).map((item) => marinaMissionRegionKey(item)));
    if (/PUERTO RICO|ST\.?\s*KITTS|SAINT KITTS|NEVIS|VIRGIN ISLANDS|CARIBBEAN/.test(regionText)) return "Caribbean";
    return "Regional";
  }

  function reportTitleForScope(office, missionScope, marinas = []) {
    if (missionScope === "road") return `Trip Report: ${office?.name || "Office"} Office Proximity`;
    if (missionScope === "air") return `Trip Report: ${office?.name || "Office"} ${airRegionLabel(marinas)} Air Mission`;
    return `Trip Report: ${office?.name || "Office"} Office Proximity`;
  }

  function reportLauncherLabel(scope) {
    if (scope?.key === "air") return `${scope?.office?.name || "Office"} Air Report`;
    if (scope?.key === "road") return `${scope?.office?.name || "Office"} Road Report`;
    return `${scope?.office?.name || "Office"} Report`;
  }

  async function enrichPlanCosts(plan, office) {
    if (!plan || !Array.isArray(plan.days)) return plan;
    const apiKey = readTravelPlanningGsaApiKey();
    const overnightRateCache = new Map();
    for (const day of plan.days) {
      day.driveMiles = Number(day.driveMiles) || 0;
      day.mileageCost = day.driveMiles * FEDERAL_MILEAGE_RATE_2026;
      day.perDiemCost = 0;
      day.perDiemLocation = "";
      day.perDiemSource = "";
      day.perDiemReason = "";
      const usesHotel = day.startMode === "hotel" || day.endMode === "hotel";
      if (!usesHotel) continue;
      const anchorItem = day.stops?.[day.stops.length - 1]?.item || day.stops?.[0]?.item || day.overnightAnchorItem || null;
      const loc = itemPerDiemLocation(anchorItem, office);
      const cacheKey = loc.cacheKey;
      let rate = overnightRateCache.get(cacheKey);
      if (!rate) {
        rate = await fetchGsaPerDiem(loc.city, loc.stateCode, apiKey, anchorItem);
        overnightRateCache.set(cacheKey, rate);
      }
      day.perDiemLocation = loc.label;
      day.perDiemCost = DEFAULT_CREW_SIZE * ((Number(rate.lodging) || 0) + (Number(rate.mie) || 0));
      day.perDiemSource = String(rate?.source || (rate?.fallback ? "fallback" : "gsa")).toLowerCase();
      day.perDiemReason = String(rate?.reason || "").trim();
    }
    plan.totals.driveMiles = plan.days.reduce((sum, day) => sum + (Number(day.driveMiles) || 0), 0);
    plan.totals.mileageCost = plan.days.reduce((sum, day) => sum + (Number(day.mileageCost) || 0), 0);
    plan.totals.perDiemCost = plan.days.reduce((sum, day) => sum + (Number(day.perDiemCost) || 0), 0);
    plan.totals.perDiemDays = plan.days.reduce((sum, day) => sum + ((Number(day.perDiemCost) || 0) > 0 ? 1 : 0), 0);
    plan.totals.airfareCost = plan.days.reduce((sum, day) => sum + (Number(day.airfareCost) || 0), 0);
    plan.totals.travelCost = plan.totals.mileageCost + plan.totals.airfareCost + plan.totals.perDiemCost;
    plan.totals.pricePerMarina = Math.max(0, Number(state.planning.pricePerMarina) || 0);
    plan.totals.weatherContingencyPct = Math.max(0, Number(state.planning.weatherContingencyPct) || 0);
    plan.totals.baseTripDays = Math.max(0, Number(plan.days?.length) || 0);
    plan.totals.travelOnlyDays = plan.days.reduce((sum, day) => sum + (day?.travelOnly ? 1 : 0), 0);
    plan.totals.weatherContingencyDays = computeWeatherContingencyDays(plan.totals.baseTripDays);
    plan.totals.adjustedTripDays = plan.totals.baseTripDays + plan.totals.weatherContingencyDays;
    plan.totals.marinaRevenue = plan.totals.marinaCount * plan.totals.pricePerMarina;
    plan.totals.supportDayRate = plan.totals.pricePerMarina * 0.5;
    plan.totals.supportDayQty = plan.totals.travelOnlyDays + plan.totals.weatherContingencyDays;
    plan.totals.laborEquipmentCost = plan.totals.supportDayRate * plan.totals.supportDayQty;
    plan.totals.estimateTotal = plan.totals.marinaRevenue + plan.totals.travelCost + plan.totals.laborEquipmentCost;
    plan.totals.perDiemRate = plan.totals.perDiemDays > 0
      ? (plan.totals.perDiemCost / plan.totals.perDiemDays)
      : 0;
    const perDiemSummary = perDiemSourceSummary(plan);
    plan.totals.perDiemSource = perDiemSummary.label;
    plan.totals.perDiemReason = perDiemSummary.reason;
    plan.costs = {
      mileageRate: FEDERAL_MILEAGE_RATE_2026,
      crewSize: DEFAULT_CREW_SIZE,
    };
    return plan;
  }

  function clearEnrichedTripPlan() {
    enrichedTripPlanKey = "";
    enrichedTripPlan = null;
    enrichedTripPlanRequestId += 1;
  }

  function clonePlan(plan) {
    if (!plan) return plan;
    if (typeof structuredClone === "function") {
      try { return structuredClone(plan); } catch {}
    }
    try {
      return JSON.parse(JSON.stringify(plan));
    } catch {
      return plan;
    }
  }

  function enrichedTripPlanCacheKey(plan) {
    return [
      plan?.routing?.scopeKey || "",
      routeMatrixStatus,
      routeMatrixKey,
      state.planning.tripOfficeId,
      state.planning.defaultSurveyHours,
      state.planning.maxWorkHoursPerDay,
      state.planning.maxDriveHoursPerDay,
      state.planning.maxMarinasPerDay,
      state.planning.pricePerMarina,
      state.planning.weatherContingencyPct,
      state.planning.returnToOffice ? 1 : 0,
      readTravelPlanningGsaApiKey(),
      state.planning.travelMode,
      state.planning.airOverheadHours,
      state.planning.airCruiseMph,
      state.items.filter((item) => item.selected && !item.excludeFromReport).map((item) => `${item.id}:${Number(item.surveyHours) || 0}:${item.prioritizeAirTravel ? 1 : 0}`).join("|"),
    ].join("::");
  }

  function ensureEnrichedTripPlan(plan) {
    const key = enrichedTripPlanCacheKey(plan);
    if (enrichedTripPlanKey === key && enrichedTripPlan) return;
    const requestId = enrichedTripPlanRequestId + 1;
    enrichedTripPlanRequestId = requestId;
    enrichPlanCosts(clonePlan(plan), plan?.office)
      .then((result) => {
        if (requestId !== enrichedTripPlanRequestId) return;
        enrichedTripPlanKey = key;
        enrichedTripPlan = result;
        render();
      })
      .catch(() => {});
  }

  function getMatrixLeg(nodeIndexA, nodeIndexB, from, to) {
    if (routeMatrixStatus === "ready" && Array.isArray(routeMatrixDurations) && routeMatrixDurations[nodeIndexA]?.[nodeIndexB] != null) {
      const durationSeconds = Number(routeMatrixDurations[nodeIndexA][nodeIndexB]);
      const distanceMeters = Number(routeMatrixDistances?.[nodeIndexA]?.[nodeIndexB]);
      return {
        miles: Number.isFinite(distanceMeters) ? distanceMeters / 1609.344 : milesBetween(from.lat, from.lng, to.lat, to.lng),
        driveMinutes: Number.isFinite(durationSeconds) ? durationSeconds / 60 : estimateDriveMinutes(milesBetween(from.lat, from.lng, to.lat, to.lng)),
        source: "routed",
      };
    }
    const estimated = estimateLeg(from, to);
    return { ...estimated, source: "estimated" };
  }

  function buildTripRouteLegRequests(plan) {
    const office = plan?.office;
    if (!office || !Array.isArray(plan?.days)) return [];
    const legs = [];
    let previousFieldPoint = null;
    function pushLeg(base) {
      if (!base?.from || !base?.to) return;
      legs.push(base);
    }
      function airLegEndpoints(leg, fallbackFrom, fallbackTo) {
        const fromAirportPoint = airportPoint(leg?.originAirport);
        const toAirportPoint = airportPoint(leg?.destAirport);
        return {
          from: fromAirportPoint || fallbackFrom || null,
          to: toAirportPoint || fallbackTo || null,
        };
      }
      plan.days.forEach((day) => {
        if (day?.travelOnly) {
          const anchorItem = day?.overnightAnchorItem || null;
          const anchorPoint = anchorItem
            ? { lat: Number(anchorItem.lat), lng: Number(anchorItem.lng) }
            : null;
          const officePoint = { lat: office.lat, lng: office.lng };
          const startPoint = day.startMode === "hotel" && previousFieldPoint
            ? previousFieldPoint
            : officePoint;
          const travelFrom = normalizeTravelPoint(day?.travelFrom) || startPoint;
          const travelTo = normalizeTravelPoint(day?.travelTo) || (day.endMode === "office" ? officePoint : anchorPoint);
          if (day?.travelSource === "air") {
            const groundFrom = normalizeTravelPoint(day?.travelFrom) || startPoint;
            const groundTo = normalizeTravelPoint(day?.travelTo) || (day.endMode === "office" ? officePoint : anchorPoint);
            const from = airportPoint(day.originAirport);
            const to = airportPoint(day.destAirport);
            if (groundFrom && from && (
              Math.abs(Number(groundFrom.lat) - Number(from.lat)) > 1e-6
              || Math.abs(Number(groundFrom.lng) - Number(from.lng)) > 1e-6
            )) {
              pushLeg({
                day: day.day,
                segment: "travel-only-ground-start",
                source: "estimated",
                from: groundFrom,
                to: from,
              });
            }
            if (from && to) {
              pushLeg({
              day: day.day,
              segment: "travel-only",
              source: "air",
              from,
                to,
              });
            }
            if (to && groundTo && (
              Math.abs(Number(groundTo.lat) - Number(to.lat)) > 1e-6
              || Math.abs(Number(groundTo.lng) - Number(to.lng)) > 1e-6
            )) {
              pushLeg({
                day: day.day,
                segment: "travel-only-ground-end",
                source: "estimated",
                from: to,
                to: groundTo,
              });
            }
          } else if ((day?.travelSource || "estimated") !== "air") {
            if (travelFrom && travelTo) {
              pushLeg({
                day: day.day,
                segment: "travel-only",
                source: day?.travelSource || "estimated",
                from: travelFrom,
                to: travelTo,
                startRatio: day?.travelStartRatio,
                endRatio: day?.travelEndRatio,
              });
            }
          }
          previousFieldPoint = day.endMode === "hotel"
            ? (normalizeTravelPoint(day?.travelTo) || anchorPoint || previousFieldPoint)
            : null;
          return;
        }
      if (!Array.isArray(day?.stops) || !day.stops.length) return;
      const firstStop = day.stops[0];
      const firstStopTravelFrom = normalizeTravelPoint(firstStop?.travelFrom);
      const firstStopTravelTo = normalizeTravelPoint(firstStop?.travelTo) || { lat: Number(firstStop?.item?.lat), lng: Number(firstStop?.item?.lng) };
      const firstStopHasSplitApproach = !!firstStopTravelFrom
        && !!firstStopTravelTo
        && ((Number(firstStop?.travelStartRatio) || 0) > 1e-6 || (Number(firstStop?.travelEndRatio) || 1) < (1 - 1e-6));
      if (day.startMode === "office") {
        if (firstStop?.driveSource === "air-arrival") {
          previousFieldPoint = { lat: Number(firstStop.item.lat), lng: Number(firstStop.item.lng) };
        } else if (firstStopHasSplitApproach) {
          pushLeg({
            day: day.day,
            segment: "start",
            source: firstStop?.driveSource || "estimated",
            from: firstStopTravelFrom,
            to: firstStopTravelTo,
            startRatio: firstStop?.travelStartRatio,
            endRatio: firstStop?.travelEndRatio,
          });
        } else {
        const airEndpoints = firstStop?.driveSource === "air"
          ? airLegEndpoints(firstStop, { lat: office.lat, lng: office.lng }, { lat: firstStop.item.lat, lng: firstStop.item.lng })
          : null;
        if (firstStop?.driveSource === "air") {
          if (airEndpoints?.from && airEndpoints?.to) {
            pushLeg({
              day: day.day,
              segment: "start",
              source: "air",
              from: airEndpoints.from,
              to: airEndpoints.to,
            });
          }
        } else {
          pushLeg({
            day: day.day,
            segment: "start",
            source: firstStop?.driveSource || "estimated",
            from: { lat: office.lat, lng: office.lng },
            to: { lat: firstStop.item.lat, lng: firstStop.item.lng },
          });
        }
        }
      } else if (day.startMode === "hotel" && previousFieldPoint) {
        if (firstStop?.driveSource === "air-arrival") {
          previousFieldPoint = { lat: Number(firstStop.item.lat), lng: Number(firstStop.item.lng) };
        } else if (firstStopHasSplitApproach) {
          pushLeg({
            day: day.day,
            segment: "field-start",
            source: firstStop?.driveSource || "estimated",
            from: firstStopTravelFrom,
            to: firstStopTravelTo,
            startRatio: firstStop?.travelStartRatio,
            endRatio: firstStop?.travelEndRatio,
          });
        } else {
        const airEndpoints = firstStop?.driveSource === "air"
          ? airLegEndpoints(firstStop, previousFieldPoint, { lat: firstStop.item.lat, lng: firstStop.item.lng })
          : null;
        if (firstStop?.driveSource === "air") {
          if (airEndpoints?.from && airEndpoints?.to) {
            pushLeg({
              day: day.day,
              segment: "field-start",
              source: "air",
              from: airEndpoints.from,
              to: airEndpoints.to,
            });
          }
        } else {
          pushLeg({
            day: day.day,
            segment: "field-start",
            source: firstStop?.driveSource || "estimated",
            from: { lat: previousFieldPoint.lat, lng: previousFieldPoint.lng },
            to: { lat: firstStop.item.lat, lng: firstStop.item.lng },
          });
        }
        }
      } else if (day.startMode === "hotel") {
        if (firstStop?.driveSource === "air-arrival") {
          previousFieldPoint = { lat: Number(firstStop.item.lat), lng: Number(firstStop.item.lng) };
        } else if (firstStopHasSplitApproach) {
          pushLeg({
            day: day.day,
            segment: "field-start",
            source: firstStop?.driveSource || "estimated",
            from: firstStopTravelFrom,
            to: firstStopTravelTo,
            startRatio: firstStop?.travelStartRatio,
            endRatio: firstStop?.travelEndRatio,
          });
        } else {
        const airEndpoints = firstStop?.driveSource === "air"
          ? airLegEndpoints(firstStop, { lat: office.lat, lng: office.lng }, { lat: firstStop.item.lat, lng: firstStop.item.lng })
          : null;
        if (firstStop?.driveSource === "air") {
          if (airEndpoints?.from && airEndpoints?.to) {
            pushLeg({
              day: day.day,
              segment: "start",
              source: "air",
              from: airEndpoints.from,
              to: airEndpoints.to,
            });
          }
        } else {
          pushLeg({
            day: day.day,
            segment: "start",
            source: firstStop?.driveSource || "estimated",
            from: { lat: office.lat, lng: office.lng },
            to: { lat: firstStop.item.lat, lng: firstStop.item.lng },
          });
        }
        }
      }
      for (let i = 1; i < day.stops.length; i += 1) {
        const stop = day.stops[i];
        const prevStop = day.stops[i - 1];
        const airEndpoints = stop?.driveSource === "air"
          ? airLegEndpoints(stop, { lat: prevStop.item.lat, lng: prevStop.item.lng }, { lat: stop.item.lat, lng: stop.item.lng })
          : null;
        if (stop?.driveSource === "air") {
          if (airEndpoints?.from && airEndpoints?.to) {
            pushLeg({
              day: day.day,
              segment: "stop",
              source: "air",
              from: airEndpoints.from,
              to: airEndpoints.to,
            });
          }
        } else {
          pushLeg({
            day: day.day,
            segment: "stop",
            source: stop?.driveSource || "estimated",
            from: { lat: prevStop.item.lat, lng: prevStop.item.lng },
            to: { lat: stop.item.lat, lng: stop.item.lng },
          });
        }
      }
      if (day.endMode === "office" && day.returnLeg) {
        const lastStop = day.stops[day.stops.length - 1]?.item;
        if (lastStop) {
          const airEndpoints = day.returnLeg?.source === "air"
            ? airLegEndpoints(day.returnLeg, { lat: lastStop.lat, lng: lastStop.lng }, { lat: office.lat, lng: office.lng })
            : null;
          if (day.returnLeg?.source === "air") {
            if (airEndpoints?.from && airEndpoints?.to) {
              pushLeg({
                day: day.day,
                segment: "return",
                source: "air",
                from: airEndpoints.from,
                to: airEndpoints.to,
              });
            }
          } else {
            pushLeg({
              day: day.day,
              segment: "return",
              source: day.returnLeg?.source || "estimated",
              from: { lat: lastStop.lat, lng: lastStop.lng },
              to: { lat: office.lat, lng: office.lng },
            });
          }
        }
        previousFieldPoint = null;
      } else {
        const lastStop = day.stops[day.stops.length - 1]?.item;
        previousFieldPoint = lastStop
          ? { lat: Number(lastStop.lat), lng: Number(lastStop.lng) }
          : previousFieldPoint;
      }
    });
    return legs;
  }

  function routeColorForDay(day) {
    const colors = ["#f59e0b", "#a855f7", "#14b8a6", "#eab308", "#ec4899", "#06b6d4", "#84cc16", "#f97316"];
    return colors[(Math.max(1, Number(day) || 1) - 1) % colors.length];
  }

  async function fetchRouteGeometry(from, to) {
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeoutId = controller ? window.setTimeout(() => controller.abort(), 8000) : 0;
    const coords = `${Number(from.lng)},${Number(from.lat)};${Number(to.lng)},${Number(to.lat)}`;
    const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" }, signal: controller?.signal });
      if (!response.ok) throw new Error(`Route HTTP ${response.status}`);
      const payload = await response.json();
      const geometry = payload?.routes?.[0]?.geometry;
      if (payload?.code !== "Ok" || !geometry?.coordinates?.length) {
        throw new Error(payload?.message || "Route geometry unavailable");
      }
      return geometry.coordinates.map(([lng, lat]) => [Number(lat), Number(lng)]);
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
    }
  }

  async function fetchRouteGeometryWithDeadline(from, to, timeoutMs = 9000) {
    return await Promise.race([
      fetchRouteGeometry(from, to),
      new Promise((_, reject) => {
        window.setTimeout(() => reject(new Error("Route geometry timeout")), Math.max(1000, Number(timeoutMs) || 9000));
      }),
    ]);
  }

  function normalizeRouteEndpoints(latlngs, from, to) {
    const points = Array.isArray(latlngs) ? latlngs.map((pair) => [Number(pair[0]), Number(pair[1])]) : [];
    const fromPoint = [Number(from?.lat), Number(from?.lng)];
    const toPoint = [Number(to?.lat), Number(to?.lng)];
    const normalized = [];
    if (Number.isFinite(fromPoint[0]) && Number.isFinite(fromPoint[1])) normalized.push(fromPoint);
    points.forEach((pair) => {
      if (Array.isArray(pair) && pair.length >= 2 && Number.isFinite(pair[0]) && Number.isFinite(pair[1])) {
        const previous = normalized[normalized.length - 1];
        if (!previous || Math.abs(previous[0] - pair[0]) > 1e-6 || Math.abs(previous[1] - pair[1]) > 1e-6) {
          normalized.push(pair);
        }
      }
    });
    if (Number.isFinite(toPoint[0]) && Number.isFinite(toPoint[1])) {
      const previous = normalized[normalized.length - 1];
      if (!previous || Math.abs(previous[0] - toPoint[0]) > 1e-6 || Math.abs(previous[1] - toPoint[1]) > 1e-6) {
        normalized.push(toPoint);
      }
    }
    return normalized;
  }

  function normalizeTravelPoint(point) {
    if (!point) return null;
    const lat = Number(point.lat);
    const lng = Number(point.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }

  function interpolateRoutePoint(a, b, ratio) {
    const t = Math.max(0, Math.min(1, Number(ratio) || 0));
    return [
      Number(a[0]) + ((Number(b[0]) - Number(a[0])) * t),
      Number(a[1]) + ((Number(b[1]) - Number(a[1])) * t),
    ];
  }

  function pointAtRouteDistance(points, targetDistance) {
    if (!Array.isArray(points) || !points.length) return null;
    if (points.length === 1 || targetDistance <= 0) return [Number(points[0][0]), Number(points[0][1])];
    let traveled = 0;
    for (let index = 1; index < points.length; index += 1) {
      const prev = points[index - 1];
      const next = points[index];
      const segment = haversineMiles(prev[0], prev[1], next[0], next[1]) * 1609.344;
      if (segment <= 1e-6) continue;
      if ((traveled + segment) >= targetDistance) {
        return interpolateRoutePoint(prev, next, (targetDistance - traveled) / segment);
      }
      traveled += segment;
    }
    const last = points[points.length - 1];
    return [Number(last[0]), Number(last[1])];
  }

  function sliceRouteGeometryByRatio(points, startRatio = 0, endRatio = 1) {
    if (!Array.isArray(points) || points.length < 2) return Array.isArray(points) ? points : [];
    const clampedStart = Math.max(0, Math.min(1, Number(startRatio) || 0));
    const clampedEnd = Math.max(clampedStart, Math.min(1, Number(endRatio) || 1));
    if (clampedStart <= 1e-6 && clampedEnd >= (1 - 1e-6)) return points;
    let totalDistance = 0;
    const cumulative = [0];
    for (let index = 1; index < points.length; index += 1) {
      const prev = points[index - 1];
      const next = points[index];
      totalDistance += haversineMiles(prev[0], prev[1], next[0], next[1]) * 1609.344;
      cumulative.push(totalDistance);
    }
    if (totalDistance <= 1e-6) return [points[0], points[points.length - 1]];
    const startDistance = totalDistance * clampedStart;
    const endDistance = totalDistance * clampedEnd;
    const sliced = [];
    const startPoint = pointAtRouteDistance(points, startDistance);
    const endPoint = pointAtRouteDistance(points, endDistance);
    if (startPoint) sliced.push(startPoint);
    for (let index = 1; index < points.length - 1; index += 1) {
      if (cumulative[index] > startDistance && cumulative[index] < endDistance) {
        sliced.push([Number(points[index][0]), Number(points[index][1])]);
      }
    }
    if (endPoint) {
      const previous = sliced[sliced.length - 1];
      if (!previous || Math.abs(previous[0] - endPoint[0]) > 1e-6 || Math.abs(previous[1] - endPoint[1]) > 1e-6) {
        sliced.push(endPoint);
      }
    }
    return sliced.length >= 2 ? sliced : [startPoint || points[0], endPoint || points[points.length - 1]];
  }

  async function refreshRouteGeometry(plan, { force = false, preferEstimated = false } = {}) {
    const office = plan?.office;
    const marinas = Array.isArray(plan?.route)
      ? Array.from(new Map(plan.route.map((stop) => [stop?.item?.id, stop?.item]).filter((entry) => entry[0] && entry[1]))).map(([, item]) => item)
      : scopeForOffice(office?.id, { excludeReportExcluded: false }).marinas;
    const key = String(plan?.routeScopeKey || plan?.routing?.scopeKey || routeScopeKey(office, marinas));
    const wantsPlaybackGeometry = normalizedTripPlaybackDayIndex(plan) >= 0;
    if (!state.showTripRoute && !wantsPlaybackGeometry) {
      clearRouteGeometry();
      return { key, status: "hidden", features: [] };
    }
    if (!office || !marinas.length) {
      clearRouteGeometry();
      return { key, status: "empty", features: [] };
    }
    if (!force && routeGeometryStatus === "ready" && routeGeometryKey === key && routeGeometryFeatures.length) {
      return { key, status: "ready", features: routeGeometryFeatures };
    }
    if (!force && routeGeometryStatus === "loading" && routeGeometryKey === key) {
      return { key, status: "loading", features: routeGeometryFeatures };
    }
    const legs = buildTripRouteLegRequests(plan);
    if (!legs.length) {
      routeGeometryKey = key;
      routeGeometryStatus = "fallback";
      routeGeometryError = "No mappable route legs for the current trip plan.";
      routeGeometryFeatures = [];
      render();
      return { key, status: "fallback", features: [], error: routeGeometryError };
    }
    routeGeometryKey = key;
    routeGeometryStatus = "loading";
    routeGeometryError = "";
    routeGeometryFeatures = [];
    const requestId = routeGeometryRequestId + 1;
    routeGeometryRequestId = requestId;
    render();
    try {
      const features = [];
      for (let index = 0; index < legs.length; index += 1) {
        const leg = legs[index];
        if (requestId !== routeGeometryRequestId) return { key, status: "cancelled", features: [] };
        try {
          if (leg.source === "air") {
            features.push({
              ...leg,
              index,
              latlngs: [
                [Number(leg.from.lat), Number(leg.from.lng)],
                [Number(leg.to.lat), Number(leg.to.lng)],
              ],
              geometrySource: "air",
            });
            continue;
          }
          if (preferEstimated) {
            features.push({
              ...leg,
              index,
              latlngs: [
                [Number(leg.from.lat), Number(leg.from.lng)],
                [Number(leg.to.lat), Number(leg.to.lng)],
              ],
              geometrySource: "estimated",
            });
            continue;
          }
          const latlngs = await fetchRouteGeometryWithDeadline(leg.from, leg.to);
          const normalized = normalizeRouteEndpoints(latlngs, leg.from, leg.to);
          features.push({
            ...leg,
            index,
            latlngs: sliceRouteGeometryByRatio(normalized, leg.startRatio, leg.endRatio),
            geometrySource: "routed",
          });
        } catch {
          features.push({
            ...leg,
            index,
            latlngs: [
              [Number(leg.from.lat), Number(leg.from.lng)],
              [Number(leg.to.lat), Number(leg.to.lng)],
            ],
            geometrySource: "estimated",
          });
        }
      }
      if (requestId !== routeGeometryRequestId) return { key, status: "cancelled", features: [] };
      routeGeometryFeatures = features;
      routeGeometryStatus = "ready";
      routeGeometryError = "";
      syncTripPlaybackOverlay(plan);
      render();
      return { key, status: "ready", features };
    } catch (error) {
      if (requestId !== routeGeometryRequestId) return { key, status: "cancelled", features: [] };
      routeGeometryFeatures = [];
      routeGeometryStatus = "fallback";
      routeGeometryError = error?.message || "Route geometry unavailable";
      syncTripPlaybackOverlay(plan);
      render();
      return { key, status: "fallback", features: [], error: routeGeometryError };
    }
  }

  function ensureRouteMatrixForOffice(officeId = state.planning.tripOfficeId, { excludeReportExcluded = false, force = false, missionScope = "all" } = {}) {
    const { office, marinas } = scopeForOffice(officeId, { excludeReportExcluded, missionScope });
    if (!office || !marinas.length) {
      if (routeMatrixStatus !== "idle" || routeMatrixKey) {
        clearRouteMatrix();
        render();
      }
      return;
    }
    refreshRouteMatrix({ force, officeId, excludeReportExcluded, missionScope }).catch(() => {});
  }

  function ensureRouteGeometryForPlan(plan, { force = false } = {}) {
    const wantsPlaybackGeometry = normalizedTripPlaybackDayIndex(plan) >= 0;
    if (!state.showTripRoute && !wantsPlaybackGeometry) {
      if (routeGeometryStatus !== "idle" || routeGeometryKey || routeGeometryFeatures.length) {
        clearRouteGeometry();
      }
      return;
    }
    if (!plan?.office || !plan?.totals?.marinaCount) {
      if (routeGeometryStatus !== "idle" || routeGeometryKey || routeGeometryFeatures.length) {
        clearRouteGeometry();
      }
      return;
    }
    refreshRouteGeometry(plan, { force }).catch(() => {});
  }

  function buildTripPlan(officeId = state.planning.tripOfficeId, { excludeReportExcluded = false, missionScope = "all" } = {}) {
    const { office, marinas } = scopeForOffice(officeId, { excludeReportExcluded, missionScope });
    const scopeKey = routeScopeKey(office, marinas);
    const tripTravelMode = resolveTripTravelMode();
    if (!office || !marinas.length) {
      return {
        office,
        route: [],
        days: [],
        totals: {
          marinaCount: marinas.length,
          availableSelectedCount: state.items.filter((item) => item.selected).length,
          driveMiles: 0,
          mileageCost: 0,
          perDiemCost: 0,
          perDiemDays: 0,
          perDiemRate: 0,
          airfareCost: 0,
          travelCost: 0,
          driveMinutes: 0,
          surveyMinutes: 0,
          totalMinutes: 0,
          hotelNights: 0,
          pricePerMarina: Math.max(0, Number(state.planning.pricePerMarina) || 0),
          marinaRevenue: 0,
          weatherContingencyPct: Math.max(0, Number(state.planning.weatherContingencyPct) || 0),
          baseTripDays: 0,
          travelOnlyDays: 0,
          weatherContingencyDays: 0,
          adjustedTripDays: 0,
          supportDayRate: Math.max(0, Number(state.planning.pricePerMarina) || 0) * 0.5,
          supportDayQty: 0,
          laborEquipmentCost: 0,
          estimateTotal: 0,
        },
      routing: {
        mode: "estimated",
        status: routeMatrixStatus,
        error: routeMatrixError,
        scopeKey,
      },
      routeScopeKey: scopeKey,
      travelMode: tripTravelMode,
    };
  }

    const byId = new Map(marinas.map((item) => [item.id, item]));
    const nodeIndexById = new Map(marinas.map((item, idx) => [item.id, idx + 1]));
    const route = [];
    let current = { lat: office.lat, lng: office.lng };
    let currentNodeIndex = 0;
    const homeAirport = resolveOfficeAirport(office);
    const airMissionGroupById = new Map();
    const airMissionAirportByGroup = new Map();
    function planLeg(nodeIndexA, nodeIndexB, from, to, { forceAir = false, forceDrive = false, originAirport = null, destAirport = null } = {}) {
      const prioritizeAir = isAirMissionPriority(to?.prioritizeAirTravel);
      const fromPrioritizesAir = isAirMissionPriority(from?.prioritizeAirTravel);
      const toMissionGroup = to?.id ? String(airMissionGroupById.get(to.id) || "") : "";
      const fromMissionGroup = from?.id ? String(airMissionGroupById.get(from.id) || "") : "";
      const sameMissionGroup = !!toMissionGroup && !!fromMissionGroup && toMissionGroup === fromMissionGroup;
      const connectivityKnown = routeMatrixStatus === "ready" && routeMatrixKey === scopeKey;
      const disconnected = connectivityKnown && !hasRoutedConnectivity(scopeKey, nodeIndexA, nodeIndexB);
      if (forceAir) return estimateAirLeg(from, to, { originAirport, destAirport });
      if (forceDrive) return getMatrixLeg(nodeIndexA, nodeIndexB, from, to);
      if (sameMissionGroup && nodeIndexA > 0 && nodeIndexB > 0) {
        return getMatrixLeg(nodeIndexA, nodeIndexB, from, to);
      }
      if (tripTravelMode === "air" && nodeIndexA === 0 && nodeIndexB > 0) return estimateAirLeg(from, to, { originAirport: homeAirport });
      if (tripTravelMode === "air" && nodeIndexA > 0 && nodeIndexB === 0) return estimateAirLeg(from, to, { destAirport: homeAirport });
      if (tripTravelMode === "auto" && nodeIndexA > 0 && nodeIndexB === 0 && fromPrioritizesAir) {
        return estimateAirLeg(from, to, {
          originAirport: airMissionAirportByGroup.get(fromMissionGroup) || null,
          destAirport: homeAirport,
        });
      }
      if (tripTravelMode === "auto" && prioritizeAir && toMissionGroup && fromMissionGroup !== toMissionGroup) {
        return estimateAirLeg(from, to, {
          originAirport: nodeIndexA === 0
            ? homeAirport
            : (airMissionAirportByGroup.get(fromMissionGroup) || null),
          destAirport: airMissionAirportByGroup.get(toMissionGroup) || null,
        });
      }
      if (tripTravelMode === "auto" && prioritizeAir && nodeIndexB > 0 && (nodeIndexA === 0 || disconnected)) {
        return estimateAirLeg(from, to, {
          originAirport: nodeIndexA === 0
            ? homeAirport
            : (airMissionAirportByGroup.get(fromMissionGroup) || null),
          destAirport: airMissionAirportByGroup.get(toMissionGroup) || null,
        });
      }
      return getMatrixLeg(nodeIndexA, nodeIndexB, from, to);
    }
    function pushRouteStop(item, fromPoint, leg) {
      route.push({
        item,
        from: fromPoint,
        milesFromPrevious: leg?.miles ?? 0,
        driveMinutesFromPrevious: leg?.driveMinutes ?? 0,
        driveSource: leg?.source || "estimated",
        airfareFromPrevious: leg?.airfare ?? 0,
        originAirport: leg?.originAirport || null,
        destAirport: leg?.destAirport || null,
        missionGroup: item?.id ? String(airMissionGroupById.get(item.id) || "") : "",
        surveyMinutes: Math.max(0, Number(item.surveyHours) || Number(state.planning.defaultSurveyHours) || 0) * 60,
      });
    }
    function appendGreedyRoute(items, startPoint, startNodeIndex, options = {}) {
      const remainingIds = new Set(items.map((item) => item.id));
      let cursorPoint = startPoint;
      let cursorNodeIndex = startNodeIndex;
      let forceAirFirst = options.forceAirFirst === true;
      while (remainingIds.size) {
        let best = null;
        let bestLeg = null;
        let bestCost = Number.POSITIVE_INFINITY;
        remainingIds.forEach((id) => {
          const candidate = byId.get(id);
          const candidateNodeIndex = nodeIndexById.get(id) || 0;
          const leg = planLeg(cursorNodeIndex, candidateNodeIndex, cursorPoint, candidate, {
            forceDrive: options.forceDrive === true,
            forceAir: forceAirFirst,
            originAirport: forceAirFirst ? options.originAirport || null : null,
          });
          const cost = Number(leg?.driveMinutes) || Number.POSITIVE_INFINITY;
          if (cost < bestCost) {
            bestCost = cost;
            best = candidate;
            bestLeg = leg;
          }
        });
        if (!best) break;
        pushRouteStop(best, cursorPoint, bestLeg);
        remainingIds.delete(best.id);
        cursorPoint = best;
        cursorNodeIndex = nodeIndexById.get(best.id) || 0;
        forceAirFirst = false;
      }
      return { point: cursorPoint, nodeIndex: cursorNodeIndex };
    }

    if (tripTravelMode !== "auto") {
      appendGreedyRoute(marinas, current, currentNodeIndex);
    } else {
      const surfaceMarinas = marinas.filter((item) => !isAirMissionPriority(item?.prioritizeAirTravel));
      const airMissionMarinas = marinas.filter((item) => isAirMissionPriority(item?.prioritizeAirTravel));
      if (surfaceMarinas.length) {
        const surfaceResult = appendGreedyRoute(surfaceMarinas, current, currentNodeIndex);
        current = surfaceResult.point;
        currentNodeIndex = surfaceResult.nodeIndex;
      }
      if (airMissionMarinas.length) {
        const missions = buildAirMissionGroups(airMissionMarinas, nodeIndexById, scopeKey);
        missions.forEach((mission, missionIndex) => {
          const missionGroupKey = `air-mission-${missionIndex + 1}`;
          const missionAirport = resolveMissionAirport(mission);
          if (missionAirport) airMissionAirportByGroup.set(missionGroupKey, missionAirport);
          mission.forEach((item) => {
            airMissionGroupById.set(item.id, missionGroupKey);
          });
        });
        current = office;
        currentNodeIndex = 0;
        while (missions.length) {
          let bestMissionIndex = -1;
          let bestMissionStart = null;
          let bestMissionLeg = null;
          let bestMissionCost = Number.POSITIVE_INFINITY;
          missions.forEach((mission, missionIndex) => {
            mission.forEach((candidate) => {
              const candidateNodeIndex = nodeIndexById.get(candidate.id) || 0;
              const leg = planLeg(currentNodeIndex, candidateNodeIndex, current, candidate, {
                forceAir: true,
                originAirport: currentNodeIndex === 0 ? homeAirport : null,
              });
              const cost = Number(leg?.driveMinutes) || Number.POSITIVE_INFINITY;
              if (cost < bestMissionCost) {
                bestMissionCost = cost;
                bestMissionIndex = missionIndex;
                bestMissionStart = candidate;
                bestMissionLeg = leg;
              }
            });
          });
          if (bestMissionIndex < 0 || !bestMissionStart) break;
          const mission = missions.splice(bestMissionIndex, 1)[0] || [];
          pushRouteStop(bestMissionStart, current, bestMissionLeg);
          const missionRemaining = mission.filter((item) => item.id !== bestMissionStart.id);
          const missionResult = appendGreedyRoute(
            missionRemaining,
            bestMissionStart,
            nodeIndexById.get(bestMissionStart.id) || 0,
            { forceDrive: true },
          );
          current = missionResult.point;
          currentNodeIndex = missionResult.nodeIndex;
        }
      }
    }

    normalizeDailyPlanning();
    const maxTotalMinutes = DAILY_TOTAL_HOURS * 60;
    const maxWorkMinutes = Math.max(60, Number(state.planning.maxWorkHoursPerDay) || 7) * 60;
    const maxDriveMinutes = Math.max(0, Number(state.planning.maxDriveHoursPerDay) || 3) * 60;
    const maxMarinas = Math.max(1, Math.round(Number(state.planning.maxMarinasPerDay) || 2));
    const days = [];
    let routeIndex = 0;
    let dayStartPoint = office;
    let dayStartNodeIndex = 0;
    let dayStartMode = "office";
    let hotelNights = 0;
    let finalReturnLeg = null;

    function pushTravelOnlyDays(minutes, { startMode = "office", finalMode = "hotel", note = "Travel only day", miles = 0, airfare = 0, anchorItem = null, source = "estimated", fromPoint = null, toPoint = null, routeStartRatio = 0, routeEndRatio = 1 } = {}) {
      let remaining = Math.max(0, Number(minutes) || 0);
      let remainingMiles = Math.max(0, Number(miles) || 0);
      let remainingAirfare = Math.max(0, Number(airfare) || 0);
      const totalMinutes = remaining || 1;
      let progressedMinutes = 0;
      const baseStartRatio = Math.max(0, Math.min(1, Number(routeStartRatio) || 0));
      const baseEndRatio = Math.max(baseStartRatio, Math.min(1, Number(routeEndRatio) || 1));
      const ratioSpan = Math.max(0, baseEndRatio - baseStartRatio);
      let currentStartMode = startMode;
      let created = 0;
      while (remaining > 0.01) {
        const chunk = Math.min(maxTotalMinutes, remaining);
        const isFinalChunk = (remaining - chunk) <= 0.01;
        const endMode = isFinalChunk ? finalMode : "hotel";
        const chunkMiles = isFinalChunk
          ? remainingMiles
          : Math.max(0, remainingMiles * (chunk / totalMinutes));
        const chunkAirfare = isFinalChunk
          ? remainingAirfare
          : Math.max(0, remainingAirfare * (chunk / totalMinutes));
        days.push({
          day: days.length + 1,
          startMode: currentStartMode,
          stops: [],
          driveMiles: chunkMiles,
          driveMinutes: chunk,
          workMinutes: 0,
          totalMinutes: chunk,
          airfareCost: chunkAirfare,
          marinaCount: 0,
          overnightAfter: endMode === "hotel",
          canReturnHome: endMode === "office",
          returnLeg: null,
          endMode,
          travelOnly: true,
          travelLabel: note,
          travelSource: source || "estimated",
          travelFrom: fromPoint || null,
          travelTo: toPoint || null,
          travelStartRatio: totalMinutes > 0 ? baseStartRatio + (((progressedMinutes / totalMinutes) || 0) * ratioSpan) : baseStartRatio,
          travelEndRatio: totalMinutes > 0 ? baseStartRatio + ((((progressedMinutes + chunk) / totalMinutes) || 1) * ratioSpan) : baseEndRatio,
          overnightAnchorItem: anchorItem || null,
        });
        if (endMode === "hotel") hotelNights += 1;
        currentStartMode = "hotel";
        progressedMinutes += chunk;
        remaining -= chunk;
        remainingMiles = Math.max(0, remainingMiles - chunkMiles);
        remainingAirfare = Math.max(0, remainingAirfare - chunkAirfare);
        created += 1;
      }
      return created;
    }

    function absorbTravelIntoPreviousDay(minutes, { miles = 0, airfare = 0 } = {}) {
      const previousDay = days[days.length - 1];
      if (!previousDay || previousDay.endMode !== "hotel") return { minutes, miles, airfare };
      const spareTotal = Math.max(0, maxTotalMinutes - (Number(previousDay.totalMinutes) || 0));
      const absorbMinutes = Math.min(Math.max(0, Number(minutes) || 0), spareTotal);
      if (absorbMinutes <= 0.01) return { minutes, miles, airfare };
      const totalMinutes = Math.max(0.01, Number(minutes) || 0.01);
      const absorbRatio = absorbMinutes / totalMinutes;
      const absorbMiles = Math.max(0, Number(miles) || 0) * absorbRatio;
      const absorbAirfare = Math.max(0, Number(airfare) || 0) * absorbRatio;
      previousDay.driveMinutes += absorbMinutes;
      previousDay.totalMinutes += absorbMinutes;
      previousDay.driveMiles += absorbMiles;
      previousDay.airfareCost += absorbAirfare;
      return {
        minutes: Math.max(0, (Number(minutes) || 0) - absorbMinutes),
        miles: Math.max(0, (Number(miles) || 0) - absorbMiles),
        airfare: Math.max(0, (Number(airfare) || 0) - absorbAirfare),
      };
    }

    function airTravelLabel(leg) {
      const origin = String(leg?.originAirport?.iata || "AIR").trim().toUpperCase();
      const dest = String(leg?.destAirport?.iata || "AIR").trim().toUpperCase();
      return `Air travel ${origin} -> ${dest}`;
    }

    function shouldStayInFieldInsteadOfReturning(lastStopItem, nextStop) {
      if (!lastStopItem || !nextStop?.item || nextStop?.driveSource === "air" || !state.planning.returnToOffice) return false;
      const lastNodeIndex = nodeIndexById.get(lastStopItem.id) || 0;
      const nextNodeIndex = nodeIndexById.get(nextStop.item.id) || 0;
      const officeReturn = planLeg(lastNodeIndex, 0, lastStopItem, office);
      const officeApproach = planLeg(0, nextNodeIndex, office, nextStop.item);
      const fieldApproach = planLeg(lastNodeIndex, nextNodeIndex, lastStopItem, nextStop.item);
      const bounceMinutes = (officeReturn?.driveMinutes || 0) + (officeApproach?.driveMinutes || 0);
      const fieldMinutes = fieldApproach?.driveMinutes || 0;
      const bounceMiles = (officeReturn?.miles || 0) + (officeApproach?.miles || 0);
      const fieldMiles = fieldApproach?.miles || 0;
      return (bounceMinutes - fieldMinutes) >= FIELD_STAY_LOOKAHEAD_MINUTES
        || (bounceMiles - fieldMiles) >= FIELD_STAY_LOOKAHEAD_MILES;
    }

    function pushAirTravelDay(leg, { startMode = "office", finalMode = "hotel", anchorItem = null, fromPoint = null, toPoint = null } = {}) {
      const endMode = finalMode === "office" ? "office" : "hotel";
      days.push({
        day: days.length + 1,
        startMode,
        stops: [],
        driveMiles: Math.max(0, Number(leg?.miles) || 0),
        driveMinutes: maxTotalMinutes,
        workMinutes: 0,
        totalMinutes: maxTotalMinutes,
        airfareCost: Math.max(0, Number(leg?.airfare) || 0),
        marinaCount: 0,
        overnightAfter: endMode === "hotel",
        canReturnHome: endMode === "office",
        returnLeg: null,
        endMode,
        travelOnly: true,
        travelLabel: airTravelLabel(leg),
        travelSource: "air",
        travelFrom: fromPoint || null,
        travelTo: toPoint || null,
        originAirport: leg?.originAirport || null,
        destAirport: leg?.destAirport || null,
        overnightAnchorItem: anchorItem || null,
      });
      if (endMode === "hotel") hotelNights += 1;
    }

    while (routeIndex < route.length) {
      const pendingStop = route[routeIndex];
      const pendingStopNodeIndex = nodeIndexById.get(pendingStop?.item?.id) || 0;
      if (pendingStop?.driveSource === "air" && !pendingStop?.airTravelCompleted) {
        const pendingMissionAirport = airMissionAirportByGroup.get(String(pendingStop.missionGroup || "")) || pendingStop.destAirport || null;
        const startMissionGroup = dayStartPoint?.id ? String(airMissionGroupById.get(dayStartPoint.id) || "") : "";
        const startMissionAirport = dayStartNodeIndex === 0
          ? homeAirport
          : (airMissionAirportByGroup.get(startMissionGroup) || pendingStop.originAirport || null);
        const startAirportPoint = airportPoint(startMissionAirport);
        const destAirportPoint = airportPoint(pendingMissionAirport);
        let airDayEndPoint = destAirportPoint || null;
        let arrivedAtMarinaFromAir = false;
        let airDayStartPoint = dayStartPoint || startAirportPoint || null;
        if (dayStartPoint && startAirportPoint && !samePoint(dayStartPoint, startAirportPoint)) {
          const airportApproach = estimateLeg(dayStartPoint, startAirportPoint);
          if (airportApproach.driveMinutes > AIRPORT_TRANSFER_SPLIT_MINUTES) {
            pushTravelOnlyDays(airportApproach.driveMinutes, {
              startMode: dayStartMode,
              finalMode: "hotel",
              note: `Travel to airport for "${marinaCodeOrName(pendingStop.item)}"`,
              miles: airportApproach.miles,
              anchorItem: null,
              source: airportApproach.source || "estimated",
              fromPoint: dayStartPoint,
              toPoint: startAirportPoint,
            });
            dayStartPoint = startAirportPoint;
            dayStartNodeIndex = -1;
            dayStartMode = "hotel";
            airDayStartPoint = startAirportPoint;
          }
        }
        if (destAirportPoint && pendingStop.item && !samePoint(destAirportPoint, pendingStop.item)) {
          const airportArrival = estimateLeg(destAirportPoint, pendingStop.item);
          if (airportArrival.driveMinutes <= AIRPORT_TRANSFER_SPLIT_MINUTES) {
            airDayEndPoint = pendingStop.item;
            arrivedAtMarinaFromAir = true;
          }
        }
        pendingStop.originAirport = startMissionAirport;
        pendingStop.destAirport = pendingMissionAirport;
        pushAirTravelDay({
          miles: pendingStop.milesFromPrevious,
          airfare: pendingStop.airfareFromPrevious,
          originAirport: startMissionAirport,
          destAirport: pendingMissionAirport,
        }, {
          startMode: dayStartMode,
          finalMode: "hotel",
          anchorItem: pendingStop.item || null,
          fromPoint: airDayStartPoint || startAirportPoint || null,
          toPoint: airDayEndPoint,
        });
        pendingStop.airTravelCompleted = true;
        pendingStop.arrivedFromAir = arrivedAtMarinaFromAir;
        dayStartPoint = arrivedAtMarinaFromAir ? pendingStop.item : (destAirportPoint || pendingStop.item);
        dayStartNodeIndex = -1;
        dayStartMode = "hotel";
        continue;
      }
      const day = {
        day: days.length + 1,
        startMode: dayStartMode,
        stops: [],
        driveMinutes: 0,
        driveMiles: 0,
        workMinutes: 0,
        totalMinutes: 0,
        airfareCost: 0,
        marinaCount: 0,
        overnightAfter: false,
        canReturnHome: false,
        returnLeg: null,
        endMode: "field",
        travelOnly: false,
        travelLabel: "",
      };
      let cursorPoint = dayStartPoint;
      let cursorNodeIndex = dayStartNodeIndex;

      while (routeIndex < route.length) {
        const nextStop = route[routeIndex];
        const nextNodeIndex = nodeIndexById.get(nextStop.item.id) || 0;
        const approachLeg = nextStop.arrivedFromAir
          ? { miles: 0, driveMinutes: 0, source: "air-arrival", airfare: 0, originAirport: nextStop.originAirport || null, destAirport: nextStop.destAirport || null }
          : planLeg(cursorNodeIndex, nextNodeIndex, cursorPoint, nextStop.item);
        const nextDriveMinutes = day.driveMinutes + (approachLeg?.driveMinutes || 0);
        const nextWorkMinutes = day.workMinutes + nextStop.surveyMinutes;
        const nextTotalMinutes = nextDriveMinutes + nextWorkMinutes;
        const driveOverflow = Math.max(0, nextDriveMinutes - maxDriveMinutes);
        const workOverflow = Math.max(0, nextWorkMinutes - maxWorkMinutes);
        const driveBorrowCapacity = Math.max(0, maxWorkMinutes - nextWorkMinutes);
        const workBorrowCapacity = Math.max(0, maxDriveMinutes - nextDriveMinutes);
        const driveCapViolated = driveOverflow > driveBorrowCapacity;
        const workCapViolated = workOverflow > workBorrowCapacity;
        const returnCandidate = planLeg(nextNodeIndex, 0, nextStop.item, office);
        const nextCanReturnHome = state.planning.returnToOffice
          && ((nextDriveMinutes + (returnCandidate?.driveMinutes || 0)) + nextWorkMinutes) <= maxTotalMinutes
          && (nextTotalMinutes + (returnCandidate?.driveMinutes || 0)) <= maxTotalMinutes;
        const exceedsCaps = day.marinaCount >= maxMarinas
          || workCapViolated
          || driveCapViolated
          || nextTotalMinutes > maxTotalMinutes;
        if (exceedsCaps && !day.stops.length) break;
        if (exceedsCaps && day.stops.length) break;
        if (
          day.stops.length
          && nextStop.driveSource === "air"
          && String(day.stops[day.stops.length - 1]?.missionGroup || "") !== String(nextStop.missionGroup || "")
        ) break;
        if (
          day.stops.length
          && nextStop.driveSource === "air"
          && day.stops.every((stop) => !isAirMissionPriority(stop?.item?.prioritizeAirTravel))
          && day.canReturnHome
        ) break;
        if (day.stops.length && day.canReturnHome && !nextCanReturnHome) break;

        day.stops.push({
          ...nextStop,
          milesFromPrevious: approachLeg?.miles ?? 0,
          driveMinutesFromPrevious: approachLeg?.driveMinutes ?? 0,
          driveSource: approachLeg?.source || "estimated",
          airfareFromPrevious: approachLeg?.airfare ?? 0,
          originAirport: approachLeg?.originAirport || null,
          destAirport: approachLeg?.destAirport || null,
        });
        day.driveMiles += approachLeg?.miles || 0;
        day.driveMinutes = nextDriveMinutes;
        day.workMinutes = nextWorkMinutes;
        day.totalMinutes = nextTotalMinutes;
        day.airfareCost += approachLeg?.airfare || 0;
        day.marinaCount += 1;
        day.canReturnHome = nextCanReturnHome;
        day.returnLeg = nextCanReturnHome ? returnCandidate : null;
        nextStop.arrivedFromAir = false;
        cursorPoint = nextStop.item;
        cursorNodeIndex = nextNodeIndex;
        routeIndex += 1;
        if (exceedsCaps) break;
      }

      if (!day.stops.length && routeIndex < route.length) {
        const forcedStop = route[routeIndex];
        const forcedNodeIndex = nodeIndexById.get(forcedStop.item.id) || 0;
        const forcedLeg = planLeg(dayStartNodeIndex, forcedNodeIndex, dayStartPoint, forcedStop.item);
        const forcedReturn = planLeg(forcedNodeIndex, 0, forcedStop.item, office);
        const forcedLegMinutes = Math.max(0, Number(forcedLeg?.driveMinutes || 0));
        const forcedDayTotal = forcedLegMinutes + forcedStop.surveyMinutes;
        if (forcedDayTotal > maxTotalMinutes && forcedLegMinutes > 0.01) {
          pushTravelOnlyDays(forcedLegMinutes, {
            startMode: dayStartMode,
            finalMode: "hotel",
            note: `Travel to "${marinaCodeOrName(forcedStop.item)}"`,
            miles: Number(forcedLeg?.miles || 0),
            airfare: forcedLeg?.source === "air" ? (forcedLeg?.airfare || 0) : 0,
            anchorItem: forcedStop.item || null,
            source: forcedLeg?.source || "estimated",
            fromPoint: dayStartPoint || null,
            toPoint: forcedStop.item || null,
            routeStartRatio: 0,
            routeEndRatio: 1,
          });
          dayStartPoint = forcedStop.item;
          dayStartNodeIndex = forcedNodeIndex;
          dayStartMode = "hotel";
          continue;
        }
        const surveyDayDriveMinutes = forcedLegMinutes;
        const surveyDayDriveMiles = Math.max(0, Number(forcedLeg?.miles || 0));
        const forcedCanReturnHome = state.planning.returnToOffice
          && (forcedDayTotal + (forcedReturn?.driveMinutes || 0)) <= maxTotalMinutes;
        day.stops.push({
          ...forcedStop,
          milesFromPrevious: surveyDayDriveMiles,
          driveMinutesFromPrevious: surveyDayDriveMinutes,
          driveSource: forcedLeg?.source || "estimated",
          airfareFromPrevious: forcedLeg?.airfare ?? 0,
          originAirport: forcedLeg?.originAirport || null,
          destAirport: forcedLeg?.destAirport || null,
          travelFrom: dayStartPoint || null,
          travelTo: forcedStop.item || null,
          travelStartRatio: 0,
          travelEndRatio: 1,
        });
        day.startMode = dayStartMode;
        day.driveMiles = surveyDayDriveMiles;
        day.driveMinutes = surveyDayDriveMinutes;
        day.workMinutes = forcedStop.surveyMinutes;
        day.totalMinutes = day.driveMinutes + day.workMinutes;
        day.airfareCost = forcedLeg?.airfare || 0;
        day.marinaCount = 1;
        day.canReturnHome = forcedCanReturnHome;
        day.returnLeg = forcedCanReturnHome ? forcedReturn : null;
        routeIndex += 1;
        cursorPoint = forcedStop.item;
        cursorNodeIndex = forcedNodeIndex;
      }

      const hasMoreStops = routeIndex < route.length;
      const nextPendingStop = hasMoreStops ? route[routeIndex] : null;
      const preferFieldStay = hasMoreStops
        && day.stops.length
        && shouldStayInFieldInsteadOfReturning(day.stops[day.stops.length - 1]?.item, nextPendingStop);
      if (day.canReturnHome && day.returnLeg && day.returnLeg.source !== "air" && !preferFieldStay) {
        day.driveMiles += day.returnLeg.miles || 0;
        day.driveMinutes += day.returnLeg.driveMinutes || 0;
        day.totalMinutes += day.returnLeg.driveMinutes || 0;
        day.airfareCost += day.returnLeg.airfare || 0;
        day.endMode = "office";
        dayStartPoint = office;
        dayStartNodeIndex = 0;
        dayStartMode = "office";
        finalReturnLeg = day.returnLeg;
      } else if (
        hasMoreStops
        && route[routeIndex]?.driveSource === "air"
        && day.stops.length
        && day.stops.every((stop) => !isAirMissionPriority(stop?.item?.prioritizeAirTravel))
        && state.planning.returnToOffice
      ) {
        const lastStop = day.stops[day.stops.length - 1];
        const forcedOfficeReturn = planLeg(nodeIndexById.get(lastStop?.item?.id) || 0, 0, lastStop?.item || office, office);
        if (forcedOfficeReturn?.source === "air") {
          const originAirportPoint = airportPoint(forcedOfficeReturn.originAirport);
          const destAirportPoint = airportPoint(forcedOfficeReturn.destAirport);
          let airReturnStartPoint = lastStop?.item || originAirportPoint || null;
          let airReturnEndPoint = office;
          let airReturnFinalMode = "office";
          if (lastStop?.item && originAirportPoint && !samePoint(lastStop.item, originAirportPoint)) {
            const airportApproach = estimateLeg(lastStop.item, originAirportPoint);
            if (airportApproach.driveMinutes > AIRPORT_TRANSFER_SPLIT_MINUTES) {
              pushTravelOnlyDays(airportApproach.driveMinutes, {
                startMode: "hotel",
                finalMode: "hotel",
                note: "Travel to departure airport",
                miles: airportApproach.miles,
                anchorItem: null,
                source: airportApproach.source || "estimated",
                fromPoint: lastStop.item,
                toPoint: originAirportPoint,
              });
              airReturnStartPoint = originAirportPoint;
            }
          }
          if (destAirportPoint && !samePoint(destAirportPoint, office)) {
            const finalGround = estimateLeg(destAirportPoint, office);
            if (finalGround.driveMinutes > AIRPORT_TRANSFER_SPLIT_MINUTES) {
              airReturnEndPoint = destAirportPoint;
              airReturnFinalMode = "hotel";
            }
          }
          pushAirTravelDay(forcedOfficeReturn, {
            startMode: "hotel",
            finalMode: airReturnFinalMode,
            anchorItem: lastStop?.item || null,
            fromPoint: airReturnStartPoint || originAirportPoint || null,
            toPoint: airReturnEndPoint,
          });
          if (airReturnFinalMode === "hotel" && destAirportPoint && !samePoint(destAirportPoint, office)) {
            const finalGround = estimateLeg(destAirportPoint, office);
            pushTravelOnlyDays(finalGround.driveMinutes, {
              startMode: "hotel",
              finalMode: "office",
              note: "Return from arrival airport",
              miles: finalGround.miles,
              anchorItem: null,
              source: finalGround.source || "estimated",
              fromPoint: destAirportPoint,
              toPoint: office,
            });
          }
        } else {
          pushTravelOnlyDays(forcedOfficeReturn?.driveMinutes || 0, {
            startMode: "hotel",
            finalMode: "office",
            note: "Return to office",
            miles: forcedOfficeReturn?.miles || 0,
            airfare: forcedOfficeReturn?.airfare || 0,
            anchorItem: lastStop?.item || null,
            source: forcedOfficeReturn?.source || "estimated",
            fromPoint: lastStop?.item || null,
            toPoint: office,
          });
        }
        day.endMode = "hotel";
        day.overnightAfter = true;
        hotelNights += 1;
        dayStartPoint = office;
        dayStartNodeIndex = 0;
        dayStartMode = "office";
        finalReturnLeg = forcedOfficeReturn;
      } else if (hasMoreStops) {
        day.overnightAfter = true;
        day.endMode = "hotel";
        hotelNights += 1;
        const lastStop = day.stops[day.stops.length - 1];
        dayStartPoint = lastStop?.item || office;
        dayStartNodeIndex = nodeIndexById.get(lastStop?.item?.id) || 0;
        dayStartMode = "hotel";
        finalReturnLeg = null;
      } else if (state.planning.returnToOffice && day.stops.length) {
        const lastStop = day.stops[day.stops.length - 1];
        const fallbackReturn = planLeg(nodeIndexById.get(lastStop?.item?.id) || 0, 0, lastStop?.item || office, office);
        const lastMissionGroup = lastStop?.item?.id ? String(airMissionGroupById.get(lastStop.item.id) || "") : "";
        if (fallbackReturn?.source === "air") {
          fallbackReturn.originAirport = airMissionAirportByGroup.get(lastMissionGroup) || fallbackReturn.originAirport || null;
          fallbackReturn.destAirport = homeAirport || fallbackReturn.destAirport || null;
        }
        day.endMode = "hotel";
        day.overnightAfter = true;
        hotelNights += 1;
        finalReturnLeg = fallbackReturn;
        day.day = days.length + 1;
        days.push(day);
        if (fallbackReturn?.source === "air") {
          const originAirportPoint = airportPoint(fallbackReturn.originAirport);
          const destAirportPoint = airportPoint(fallbackReturn.destAirport);
          let airReturnStartPoint = lastStop?.item || originAirportPoint || null;
          let airReturnEndPoint = office;
          let airReturnFinalMode = "office";
          if (lastStop?.item && originAirportPoint && !samePoint(lastStop.item, originAirportPoint)) {
            const airportApproach = estimateLeg(lastStop.item, originAirportPoint);
            if (airportApproach.driveMinutes > AIRPORT_TRANSFER_SPLIT_MINUTES) {
              pushTravelOnlyDays(airportApproach.driveMinutes, {
                startMode: "hotel",
                finalMode: "hotel",
                note: "Travel to departure airport",
                miles: airportApproach.miles,
                anchorItem: null,
                source: airportApproach.source || "estimated",
                fromPoint: lastStop.item,
                toPoint: originAirportPoint,
              });
              airReturnStartPoint = originAirportPoint;
            }
          }
          if (destAirportPoint && !samePoint(destAirportPoint, office)) {
            const finalGround = estimateLeg(destAirportPoint, office);
            if (finalGround.driveMinutes > AIRPORT_TRANSFER_SPLIT_MINUTES) {
              airReturnEndPoint = destAirportPoint;
              airReturnFinalMode = "hotel";
            }
          }
          pushAirTravelDay(fallbackReturn, {
            startMode: "hotel",
            finalMode: airReturnFinalMode,
            anchorItem: lastStop?.item || null,
            fromPoint: airReturnStartPoint || originAirportPoint || null,
            toPoint: airReturnEndPoint,
          });
          if (airReturnFinalMode === "hotel" && destAirportPoint && !samePoint(destAirportPoint, office)) {
            const finalGround = estimateLeg(destAirportPoint, office);
            pushTravelOnlyDays(finalGround.driveMinutes, {
              startMode: "hotel",
              finalMode: "office",
              note: "Return from arrival airport",
              miles: finalGround.miles,
              anchorItem: null,
              source: finalGround.source || "estimated",
              fromPoint: destAirportPoint,
              toPoint: office,
            });
          }
        } else {
          pushTravelOnlyDays(fallbackReturn?.driveMinutes || 0, {
            startMode: "hotel",
            finalMode: "office",
            note: "Return to office",
            miles: fallbackReturn?.miles || 0,
            airfare: fallbackReturn?.airfare || 0,
            anchorItem: lastStop?.item || null,
            source: fallbackReturn?.source || "estimated",
            fromPoint: lastStop?.item || null,
            toPoint: office,
          });
        }
        dayStartPoint = office;
        dayStartNodeIndex = 0;
        dayStartMode = "office";
        continue;
      } else {
        day.endMode = "field";
        finalReturnLeg = null;
      }

      day.day = days.length + 1;
      days.push(day);
    }

    const totalDriveMinutes = days.reduce((sum, day) => sum + (day.driveMinutes || 0), 0);
    const totalDriveMiles = days.reduce((sum, day) => sum + (day.driveMiles || 0), 0);
    const totalSurveyMinutes = days.reduce((sum, day) => sum + (day.workMinutes || 0), 0);
    const geometryKey = tripRouteGeometryKey(scopeKey, route, days);
    return {
      office,
      route,
      days,
      returnLeg: finalReturnLeg,
      totals: {
        marinaCount: marinas.length,
        availableSelectedCount: state.items.filter((item) => item.selected).length,
        driveMiles: totalDriveMiles,
        mileageCost: totalDriveMiles * FEDERAL_MILEAGE_RATE_2026,
        perDiemCost: 0,
        airfareCost: days.reduce((sum, day) => sum + (Number(day.airfareCost) || 0), 0),
        travelCost: totalDriveMiles * FEDERAL_MILEAGE_RATE_2026,
        driveMinutes: totalDriveMinutes,
        surveyMinutes: totalSurveyMinutes,
        totalMinutes: totalDriveMinutes + totalSurveyMinutes,
        hotelNights,
        weatherContingencyPct: Math.max(0, Number(state.planning.weatherContingencyPct) || 0),
        baseTripDays: days.length,
        weatherContingencyDays: computeWeatherContingencyDays(days.length),
        adjustedTripDays: days.length + computeWeatherContingencyDays(days.length),
      },
      routing: {
        mode: routeMatrixStatus === "ready" && routeMatrixKey === scopeKey ? "routed" : "estimated",
        status: routeMatrixStatus,
        error: routeMatrixError,
        scopeKey,
      },
      routeScopeKey: geometryKey,
      travelMode: tripTravelMode,
    };
  }

  function snapshotMapView() {
    const center = map.getCenter?.();
    const bounds = map.getBounds?.();
    return {
      center: center ? { lat: Number(center.lat), lng: Number(center.lng) } : null,
      zoom: Number(map.getZoom?.() ?? state.defaultZoom),
      bearing: typeof map.getBearing === "function" ? Number(map.getBearing() || 0) : 0,
      bounds: bounds?.isValid?.()
        ? {
            west: Number(bounds.getWest()),
            south: Number(bounds.getSouth()),
            east: Number(bounds.getEast()),
            north: Number(bounds.getNorth()),
          }
        : null,
    };
  }

  function approximateMilesToLat(miles) {
    return miles / 69;
  }

  function approximateMilesToLng(miles, lat) {
    const cosLat = Math.max(0.2, Math.cos((Number(lat || 0) * Math.PI) / 180));
    return miles / (69 * cosLat);
  }

  function smartFrameBounds(item) {
    if (!item) return null;
    const baseMiles = 0.18;
    const searchMiles = 0.4;
    const latRadius = approximateMilesToLat(baseMiles);
    const lngRadius = approximateMilesToLng(baseMiles, item.lat);
    const nearby = state.items.filter((other) => {
      if (!other || other.id === item.id) return false;
      return milesBetween(item.lat, item.lng, other.lat, other.lng) <= searchMiles;
    });
    const points = [[Number(item.lat), Number(item.lng)], ...nearby.map((other) => [Number(other.lat), Number(other.lng)])];
    let bounds = L.latLngBounds(points);
    if (!bounds.isValid()) {
      bounds = L.latLngBounds(
        [Number(item.lat) - latRadius, Number(item.lng) - lngRadius],
        [Number(item.lat) + latRadius, Number(item.lng) + lngRadius],
      );
    }
    const south = Math.min(bounds.getSouth(), Number(item.lat) - latRadius);
    const north = Math.max(bounds.getNorth(), Number(item.lat) + latRadius);
    const west = Math.min(bounds.getWest(), Number(item.lng) - lngRadius);
    const east = Math.max(bounds.getEast(), Number(item.lng) + lngRadius);
    return L.latLngBounds([south, west], [north, east]).pad(0.16);
  }

  function applySmartAutoView(item) {
    const bounds = smartFrameBounds(item);
    if (!bounds?.isValid?.()) {
      suppressAutoSaveFor();
      map.setView([Number(item.lat), Number(item.lng)], Math.max(3, (Number(state.defaultZoom) || 18) - 1), { animate: false });
      return;
    }
    suppressAutoSaveFor();
    map.fitBounds(bounds, { animate: false, padding: [24, 24] });
    const fittedBounds = map.getBounds?.();
    const center = map.getCenter?.();
    const zoom = Number(map.getZoom?.() ?? state.defaultZoom);
    if (fittedBounds?.isValid?.() && center) {
      const latSpan = Math.max(0.0005, Number(fittedBounds.getNorth()) - Number(fittedBounds.getSouth()));
      const shiftedCenter = [Number(center.lat) + (latSpan * 0.12), Number(center.lng)];
      suppressAutoSaveFor();
      map.setView(shiftedCenter, zoom, { animate: false });
    }
  }

  function applySavedView(item) {
    if (!item) return;
    const view = item.view;
    if (view?.bounds) {
      const bounds = L.latLngBounds(
        [Number(view.bounds.south), Number(view.bounds.west)],
        [Number(view.bounds.north), Number(view.bounds.east)],
      );
      if (bounds.isValid()) {
        suppressAutoSaveFor();
        map.fitBounds(bounds, { animate: false });
      } else {
        suppressAutoSaveFor();
        map.setView([Number(item.lat), Number(item.lng)], Number(view.zoom) || state.defaultZoom, { animate: false });
      }
    } else if (view?.center && Number.isFinite(Number(view.center.lat)) && Number.isFinite(Number(view.center.lng))) {
      suppressAutoSaveFor();
      map.setView([Number(view.center.lat), Number(view.center.lng)], Number(view.zoom) || state.defaultZoom, { animate: false });
    } else {
      applySmartAutoView(item);
    }
    if (typeof map.setBearing === "function") {
      map.setBearing(Number(view?.bearing) || 0);
    }
  }

  function saveCurrentViewForActive() {
    if (!state.editMarinaViews) return;
    const item = currentItem();
    if (!item) return;
    item.view = snapshotMapView();
    persistLocalState();
    render();
  }

  function saveCurrentViewForActiveSilently() {
    if (!state.editMarinaViews) return;
    const item = currentItem();
    if (!item) return;
    item.view = snapshotMapView();
    persistLocalState();
  }

  function queueAutoSaveCurrentView() {
    if (exporting || suppressAutoSaveView || !state.editMarinaViews) return;
    if (Date.now() < suppressAutoSaveUntil) return;
    const item = currentItem();
    if (!item) return;
    window.clearTimeout(autoSaveViewTimer);
    autoSaveViewTimer = window.setTimeout(() => {
      if (suppressAutoSaveView || !state.editMarinaViews) {
        autoSaveViewTimer = 0;
        return;
      }
      saveCurrentViewForActiveSilently();
      autoSaveViewTimer = 0;
    }, 250);
  }

  function flattenItems(featureCollection) {
    const features = Array.isArray(featureCollection?.features) ? featureCollection.features : [];
    const items = [];
    features.forEach((feature, featureIndex) => {
      const geometry = feature?.geometry;
      const type = String(geometry?.type || "");
      const pushItem = (lng, lat, suffix = "") => {
        if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return;
        const name = pointName(feature, featureIndex);
        items.push({
          id: `pt-${featureIndex + 1}${suffix}`,
          name: suffix ? `${name} ${suffix.replace(/^-/, "")}` : name,
          lat: Number(lat),
          lng: Number(lng),
          properties: { ...(feature?.properties || {}) },
          selected: true,
          view: null,
          coveredSlips: false,
          excludeFromReport: false,
          noFlyZone: false,
          prioritizeAirTravel: false,
        });
      };
      if (type === "Point") {
        const [lng, lat] = Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
        pushItem(lng, lat);
      }
      if (type === "MultiPoint") {
        const coords = Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
        coords.forEach((coord, pointIndex) => {
          const [lng, lat] = Array.isArray(coord) ? coord : [];
          pushItem(lng, lat, `-${pointIndex + 1}`);
        });
      }
    });
    return items;
  }

  function dedupeItems(items) {
    const seen = new Set();
    return items.filter((item) => {
      const key = `${Number(item.lat).toFixed(7)}|${Number(item.lng).toFixed(7)}|${String(item.name || "").trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  async function parseDatasetFile(file) {
    const name = String(file?.name || "dataset");
    const lower = name.toLowerCase();
    if (lower.endsWith(".geojson") || lower.endsWith(".json")) {
      const text = await file.text();
      return {
        datasetName: stripKnownSuffix(name),
        featureCollection: normalizeFeatureCollection(JSON.parse(text)),
      };
    }
    if (lower.endsWith(".kml")) {
      const text = await file.text();
      const dom = new DOMParser().parseFromString(text, "text/xml");
      return {
        datasetName: stripKnownSuffix(name),
        featureCollection: normalizeFeatureCollection(toGeoJSON.kml(dom)),
        kmlItems: extractItemsFromKmlDom(dom),
      };
    }
    if (lower.endsWith(".kmz")) {
      const buff = await file.arrayBuffer();
      const zip = await JSZip.loadAsync(buff);
      const kmlEntry = Object.values(zip.files).find((entry) => /\.kml$/i.test(entry.name));
      if (!kmlEntry) throw new Error("KMZ has no KML payload.");
      const kmlText = await kmlEntry.async("string");
      const dom = new DOMParser().parseFromString(kmlText, "text/xml");
      return {
        datasetName: stripKnownSuffix(name),
        featureCollection: normalizeFeatureCollection(toGeoJSON.kml(dom)),
        kmlItems: extractItemsFromKmlDom(dom),
      };
    }
    throw new Error(`Unsupported file: ${name}`);
  }

  async function parseDatasetUrl(url, name = DEFAULT_DATASET_NAME) {
    const response = await fetch(url, { headers: { Accept: "application/vnd.google-earth.kml+xml,text/xml,application/json" } });
    if (!response.ok) throw new Error(`Dataset HTTP ${response.status}`);
    const lower = String(name || url || "dataset").toLowerCase();
    if (lower.endsWith(".geojson") || lower.endsWith(".json")) {
      const text = await response.text();
      return {
        datasetName: stripKnownSuffix(name),
        featureCollection: normalizeFeatureCollection(JSON.parse(text)),
      };
    }
    if (lower.endsWith(".kml")) {
      const text = await response.text();
      const dom = new DOMParser().parseFromString(text, "text/xml");
      return {
        datasetName: stripKnownSuffix(name),
        featureCollection: normalizeFeatureCollection(toGeoJSON.kml(dom)),
        kmlItems: extractItemsFromKmlDom(dom),
      };
    }
    if (lower.endsWith(".kmz")) {
      const buff = await response.arrayBuffer();
      const zip = await JSZip.loadAsync(buff);
      const kmlEntry = Object.values(zip.files).find((entry) => /\.kml$/i.test(entry.name));
      if (!kmlEntry) throw new Error("KMZ has no KML payload.");
      const kmlText = await kmlEntry.async("string");
      const dom = new DOMParser().parseFromString(kmlText, "text/xml");
      return {
        datasetName: stripKnownSuffix(name),
        featureCollection: normalizeFeatureCollection(toGeoJSON.kml(dom)),
        kmlItems: extractItemsFromKmlDom(dom),
      };
    }
    throw new Error(`Unsupported dataset URL: ${name}`);
  }

  function columnLabelToIndex(label) {
    let value = 0;
    for (let i = 0; i < label.length; i += 1) value = (value * 26) + (label.charCodeAt(i) - 64);
    return value - 1;
  }

  function parseWorksheetRows(xmlText, sharedStrings) {
    const ns = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
    const dom = new DOMParser().parseFromString(xmlText, "text/xml");
    const rows = Array.from(dom.getElementsByTagNameNS(ns, "row"));
    return rows.map((rowEl) => {
      const row = [];
      Array.from(rowEl.getElementsByTagNameNS(ns, "c")).forEach((cellEl) => {
        const ref = String(cellEl.getAttribute("r") || "");
        const match = ref.match(/[A-Z]+/);
        const colIndex = match ? columnLabelToIndex(match[0]) : row.length;
        const type = String(cellEl.getAttribute("t") || "");
        let value = "";
        if (type === "inlineStr") {
          value = Array.from(cellEl.getElementsByTagNameNS(ns, "t")).map((node) => node.textContent || "").join("");
        } else {
          const vEl = cellEl.getElementsByTagNameNS(ns, "v")[0];
          value = vEl?.textContent ?? "";
          if (type === "s" && value !== "") value = sharedStrings[Number(value)] ?? value;
        }
        row[colIndex] = String(value ?? "");
      });
      return row.map((value) => String(value ?? ""));
    });
  }

  function rowsToObjects(rows, headerRowIndex) {
    const headerRow = Array.isArray(rows[headerRowIndex]) ? rows[headerRowIndex] : [];
    const headers = headerRow.map((value, index) => {
      const clean = String(value || "").replace(/\s+/g, " ").trim();
      return clean || `Column ${index + 1}`;
    });
    const out = [];
    rows.slice(headerRowIndex + 1).forEach((row) => {
      const values = Array.isArray(row) ? row : [];
      if (!values.some((value) => String(value || "").trim())) return;
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = String(values[index] ?? "").trim();
      });
      out.push(obj);
    });
    return out;
  }

  async function parsePortfolioWorkbookUrl(url, name = DEFAULT_PORTFOLIO_NAME) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Workbook HTTP ${response.status}`);
    const archive = await JSZip.loadAsync(await response.arrayBuffer());
    const workbookXml = await archive.file("xl/workbook.xml")?.async("string");
    const relsXml = await archive.file("xl/_rels/workbook.xml.rels")?.async("string");
    if (!workbookXml || !relsXml) throw new Error("Workbook structure is invalid.");
    const ns = {
      a: "http://schemas.openxmlformats.org/spreadsheetml/2006/main",
      r: "http://schemas.openxmlformats.org/officeDocument/2006/relationships",
      rel: "http://schemas.openxmlformats.org/package/2006/relationships",
    };
    const workbookDom = new DOMParser().parseFromString(workbookXml, "text/xml");
    const relsDom = new DOMParser().parseFromString(relsXml, "text/xml");
    const relMap = new Map(
      Array.from(relsDom.getElementsByTagNameNS(ns.rel, "Relationship")).map((relEl) => [
        String(relEl.getAttribute("Id") || ""),
        String(relEl.getAttribute("Target") || ""),
      ]),
    );
    const sharedStringsXml = await archive.file("xl/sharedStrings.xml")?.async("string");
    const sharedStrings = sharedStringsXml
      ? Array.from(new DOMParser().parseFromString(sharedStringsXml, "text/xml").getElementsByTagNameNS(ns.a, "si")).map((siEl) => (
          Array.from(siEl.getElementsByTagNameNS(ns.a, "t")).map((tEl) => tEl.textContent || "").join("")
        ))
      : [];
    const sheetNodes = Array.from(workbookDom.getElementsByTagNameNS(ns.a, "sheet"));
    const sheets = {};
    for (const sheetEl of sheetNodes) {
      const sheetName = String(sheetEl.getAttribute("name") || "");
      const rid = String(sheetEl.getAttributeNS(ns.r, "id") || sheetEl.getAttribute("r:id") || "");
      const target = relMap.get(rid);
      if (!sheetName || !target) continue;
      const normalizedTarget = target.startsWith("xl/") ? target : `xl/${target.replace(/^\//, "")}`;
      const sheetXml = await archive.file(normalizedTarget)?.async("string");
      if (!sheetXml) continue;
      sheets[sheetName] = parseWorksheetRows(sheetXml, sharedStrings);
    }
    return {
      workbookName: stripKnownSpreadsheetSuffix(name),
      detailRows: rowsToObjects(sheets["Property Detail"] || [], 3).filter((row) => row["Property (1)"] && !/^subtotal|total$/i.test(row["Property (1)"])),
      profileRows: rowsToObjects(sheets["Property Profile"] || [], 1).filter((row) => row["Marina"]),
      countRows: rowsToObjects(sheets["Marina Count"] || [], 0).filter((row) => row["Property Name"] && !/^\d+$/.test(row["Property Name"])),
    };
  }

  function syncPortfolioToItems(portfolio) {
    if (!portfolio || !state.items.length) return { matched: 0, total: state.items.length };
    const detailMap = new Map();
    const profileMap = new Map();
    const countMap = new Map();
    portfolio.detailRows.forEach((row) => detailMap.set(normalizeMarinaKey(row["Property (1)"]), row));
    portfolio.profileRows.forEach((row) => profileMap.set(normalizeMarinaKey(row["Marina"]), row));
    portfolio.countRows.forEach((row) => countMap.set(normalizeMarinaKey(row["Property Name"]), row));
    let matched = 0;
    state.items.forEach((item) => {
      const key = normalizeMarinaKey(item.name);
      const detail = detailMap.get(key) || null;
      const profile = profileMap.get(key) || null;
      const counts = countMap.get(key) || null;
      const propertyCount = pickNumericField(counts, [
        "Property Count",
        "Property Counts",
        "Count",
        "Counts",
        "Properties",
        "Property",
      ]);
      const primaryName = detail?.["Property (1)"] || profile?.["Marina"] || counts?.["Property Name"] || "";
      const propCity = String(item.properties?.city || item.properties?.City || "").trim();
      const propState = String(item.properties?.state || item.properties?.State || "").trim();
      item.portfolio = (detail || profile || counts)
        ? {
            matchedName: primaryName,
            code: detail?.Code || "",
            city: detail?.["City "] || propCity || profile?.["City, State"] || "",
            stateCountry: detail?.["State/Country"] || propState || "",
            archetype: detail?.Archetype || "",
            wetSlips: detail?.["Wet Slips"] || counts?.Slips || profile?.["Wet Slips"] || "",
            dryStorage: detail?.["Dry Rack Storage(10)"] || counts?.Storage || profile?.["Dry Storage"] || "",
            landStorage: detail?.["Approximate Land Storage"] || "",
            coreAttributes: profile?.["Core Attributes"] || "",
            amenities: profile?.Amenities || "",
            fuelDock: profile?.["Fuel Dock"] || "",
            watersideRestaurant: profile?.["Waterside Restaurant"] || "",
            propertyCount,
          }
        : null;
      if (item.portfolio) matched += 1;
    });
    return { matched, total: state.items.length };
  }

  function pickNumericField(row, candidateKeys = []) {
    if (!row || typeof row !== "object") return null;
    for (const key of candidateKeys) {
      if (!(key in row)) continue;
      const raw = String(row[key] ?? "").trim();
      if (!raw) continue;
      const value = Number.parseFloat(raw.replace(/[^0-9.\-]+/g, ""));
      if (Number.isFinite(value)) return value;
    }
    return null;
  }

  function filterZeroPropertyCountItems() {
    if (!Array.isArray(state.items) || !state.items.length) return 0;
    const activeId = currentItem()?.id || "";
    const kept = state.items.filter((item) => {
      const count = item?.portfolio?.propertyCount;
      return !(Number.isFinite(count) && count <= 0);
    });
    const removed = state.items.length - kept.length;
    if (!removed) return 0;
    state.items = kept;
    const nextActiveIndex = kept.findIndex((item) => item.id === activeId);
    state.activeIndex = nextActiveIndex >= 0
      ? nextActiveIndex
      : (kept.length ? Math.max(0, Math.min(state.activeIndex, kept.length - 1)) : -1);
    return removed;
  }

  async function applyParsedDataset(parsed, sourceName) {
    const normalized = app.services.io.normalizeImportFeatureCollection(parsed.featureCollection, {
      sourceCRS: null,
      targetCRS: "EPSG:4326",
    });
    const fromGeoJson = flattenItems(normalized.featureCollection);
    const fromKmlDom = Array.isArray(parsed.kmlItems) ? parsed.kmlItems : [];
    const items = dedupeItems(fromKmlDom.length ? [...fromKmlDom, ...fromGeoJson] : fromGeoJson).map((item) => normalizeItem(item));
    if (!items.length) throw new Error("No point features were found in the dataset.");
    state = {
      ...state,
      datasetName: parsed.datasetName || stripKnownSuffix(sourceName),
      prefix: sanitizeFilePart(parsed.datasetName || stripKnownSuffix(sourceName) || "marina", "marina"),
      items,
      activeIndex: items.length ? 0 : -1,
    };
    recomputePlanning();
    if (portfolioWorkbook) {
      syncPortfolioToItems(portfolioWorkbook);
      filterZeroPropertyCountItems();
      recomputePlanning();
    }
    render();
    setStatus(`Loaded ${items.length} point${items.length === 1 ? "" : "s"} from ${sourceName}.`);
    app.ui.toast(`Ports and Harbors Toolset loaded ${items.length} point${items.length === 1 ? "" : "s"}.`);
    await focusItem(0, { saveCurrent: false });
  }

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function hasPendingTileWork() {
    try {
      const tileLayers = [];
      map.eachLayer((layer) => {
        if (layer?._loading) tileLayers.push(layer);
      });
      if (tileLayers.length) return true;
      const imgs = map.getContainer()?.querySelectorAll?.(".leaflet-tile-container img.leaflet-tile, .leaflet-overlay-pane img");
      for (let i = 0; i < (imgs?.length || 0); i += 1) {
        const img = imgs[i];
        if (!img.complete) return true;
      }
    } catch {}
    return false;
  }

  async function waitForMapToSettle({ timeoutMs = 9000, stableMs = 450, pollMs = 90 } = {}) {
    const started = Date.now();
    let stableSince = 0;
    while ((Date.now() - started) < timeoutMs) {
      const pending = hasPendingTileWork();
      if (!pending) {
        if (!stableSince) stableSince = Date.now();
        if ((Date.now() - stableSince) >= stableMs) return true;
      } else {
        stableSince = 0;
      }
      await delay(pollMs);
    }
    return false;
  }

  function ensureCaptureLayer() {
    if (!captureLayer) captureLayer = L.layerGroup().addTo(map);
    return captureLayer;
  }

  function ensureLegendControl() {
    if (legendControlEl) return legendControlEl;
    const host = map.getContainer?.()?.parentElement || map.getContainer?.();
    if (!host) return null;
    const el = document.createElement("div");
    el.className = "imggen-map-legend";
    el.style.position = "absolute";
    el.style.left = "12px";
    el.style.bottom = "12px";
    el.style.zIndex = "820";
    el.style.pointerEvents = "none";
    el.style.padding = "10px 12px";
    el.style.borderRadius = "10px";
    el.style.background = "rgba(15,23,42,0.9)";
    el.style.color = "#e5e7eb";
    el.style.border = "1px solid rgba(148,163,184,0.3)";
    el.style.boxShadow = "0 10px 24px rgba(2,6,23,0.45)";
    el.style.font = "12px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif";
    host.appendChild(el);
    legendControlEl = el;
    return legendControlEl;
  }

  function updateLegend() {
    const el = ensureLegendControl();
    if (!el) return;
    el.style.display = state.showLegend ? "block" : "none";
    if (!state.showLegend) return;
    const officeRows = state.selectedOfficeIds.map((id) => {
      const office = state.offices.find((entry) => entry.id === id);
      const color = selectedOfficeColorMap().get(id);
      if (!office || !color) return "";
      return `
        <div style="display:flex; align-items:center; gap:8px;">
          <span style="width:12px; height:12px; border-radius:2px; background:${color.fill}; border:2px solid ${color.stroke}; display:inline-block;"></span>
          <span>${esc(office.name)}</span>
        </div>
      `;
    }).join("");
    el.innerHTML = `
      <div style="font-weight:700; margin-bottom:6px;">Legend</div>
      <div style="display:grid; gap:4px;">
        <div style="display:flex; align-items:center; gap:8px;">
          <span style="width:12px; height:12px; border-radius:999px; background:#60a5fa; border:2px solid #dbeafe; display:inline-block;"></span>
          <span>Marina</span>
        </div>
        <div style="display:flex; align-items:center; gap:8px;">
          <span style="width:12px; height:12px; border-radius:999px; background:#ef4444; border:2px solid #ffffff; display:inline-block;"></span>
          <span>Selected marina</span>
        </div>
        <div style="display:flex; align-items:center; gap:8px;">
          <span style="width:12px; height:12px; border-radius:2px; background:#22c55e; border:2px solid #dcfce7; display:inline-block;"></span>
          <span>Office</span>
        </div>
        ${officeRows}
      </div>
    `;
  }

  function drawPlanningOverlay(item, { items = null, routeScopeKey: routeScopeKeyOverride = "", tripPlan = null, tripDayIndex = -1 } = {}) {
    const layer = ensureCaptureLayer();
    layer.clearLayers();
    const officeColors = selectedOfficeColorMap();
    const labelOffsetMap = new Map();
    const marinaGroups = [];
    const screenThreshold = 28;
    const effectiveTripDay = Array.isArray(tripPlan?.days) && tripDayIndex >= 0 ? tripPlan.days[tripDayIndex] || null : null;
    const highlightedMarinaIds = new Set(
      Array.isArray(effectiveTripDay?.stops)
        ? effectiveTripDay.stops.map((stop) => stop?.item?.id).filter(Boolean)
        : [],
    );
    const explicitItems = Array.isArray(items) ? items : null;
    const visibleOfficeFilter = explicitItems ? [] : state.visibleOfficeMarinaIds;
    const overlayItems = explicitItems || (
      visibleOfficeFilter.length
        ? state.items.filter((marina) => visibleOfficeFilter.includes(marina.officeId) || (!!item && marina.id === item.id))
        : state.items
    );
    const effectiveRouteScopeKey = String(routeScopeKeyOverride || currentTripRouteScopeKey());
    if ((state.showTripRoute || effectiveTripDay) && routeGeometryKey === effectiveRouteScopeKey && routeGeometryFeatures.length) {
      routeGeometryFeatures.forEach((feature) => {
        const isTripDayFeature = !!effectiveTripDay && Number(feature.day) === Number(effectiveTripDay.day);
        if (effectiveTripDay && !state.showTripRoute && !isTripDayFeature) return;
        L.polyline(feature.latlngs, {
          color: isTripDayFeature ? "#f8fafc" : routeColorForDay(feature.day),
          weight: isTripDayFeature ? (feature.segment === "return" ? 5 : 6) : (feature.segment === "return" ? 1.25 : 1.75),
          opacity: isTripDayFeature ? 0.98 : (state.showTripRoute ? (feature.source === "air" ? 0.8 : 0.72) : 0.18),
          dashArray: feature.source === "air"
            ? "10 6"
            : feature.geometrySource === "estimated"
              ? "6 6"
              : null,
          pane: "overlayPane",
        }).addTo(layer);
      });
    }
    overlayItems.forEach((marina) => {
      const point = map.latLngToContainerPoint([Number(marina.lat), Number(marina.lng)]);
      let group = marinaGroups.find((entry) => entry.points.some((other) => other.point.distanceTo(point) <= screenThreshold));
      if (!group) {
        group = { points: [] };
        marinaGroups.push(group);
      }
      group.points.push({ marina, point });
    });
    marinaGroups.forEach((group) => {
      const count = group.points.length;
      group.points.forEach(({ marina }, index) => {
        if (count <= 1) {
          labelOffsetMap.set(marina.id, { x: 10, y: -14 });
          return;
        }
        const angle = (-Math.PI / 2) + ((Math.PI * 2 * index) / count);
        const radius = count <= 4 ? 18 : 24;
        labelOffsetMap.set(marina.id, {
          x: Math.round(Math.cos(angle) * radius),
          y: Math.round(Math.sin(angle) * radius),
        });
      });
    });
    overlayItems.forEach((marina) => {
      const isActive = !!item && marina.id === item.id && state.marker;
      const isTripDayMarina = highlightedMarinaIds.has(marina.id);
      const officeColor = marina.officeId ? officeColors.get(marina.officeId) : null;
      const fillColor = isActive ? "#ef4444" : (isTripDayMarina ? "#f59e0b" : (officeColor?.fill || "#60a5fa"));
      const strokeColor = isActive ? "#ffffff" : (isTripDayMarina ? "#fef3c7" : (officeColor?.stroke || "#dbeafe"));
      const codeLabel = String(marina?.portfolio?.code || "").trim();
      const labelOffset = labelOffsetMap.get(marina.id) || { x: 10, y: -14 };
      L.circleMarker([Number(marina.lat), Number(marina.lng)], {
        radius: isActive ? 7 : (isTripDayMarina ? 6 : 4),
        color: strokeColor,
        weight: isActive ? 2 : (isTripDayMarina ? 2.5 : 1.5),
        fillColor,
        fillOpacity: isActive ? 0.95 : (isTripDayMarina ? 0.95 : 0.78),
        pane: "markerPane",
      }).addTo(layer);
      if (codeLabel && state.showMarinaCodes) {
        L.marker([Number(marina.lat), Number(marina.lng)], {
          interactive: false,
          keyboard: false,
          icon: L.divIcon({
            className: "imggen-code-label",
            html: `<div style="background:${isActive ? "rgba(127,29,29,0.92)" : (isTripDayMarina ? "rgba(146,64,14,0.92)" : "rgba(15,23,42,0.82)")}; color:#fff; padding:2px 6px; border-radius:999px; font:700 10px/1.1 system-ui,sans-serif; white-space:nowrap; border:1px solid ${isActive ? "#fca5a5" : (isTripDayMarina ? "#fcd34d" : "rgba(255,255,255,0.18)")}; box-shadow:0 3px 10px rgba(15,23,42,0.25);">${esc(codeLabel)}</div>`,
            iconAnchor: [-labelOffset.x, -labelOffset.y],
          }),
        }).addTo(layer);
      }
      if (isTripDayMarina && !isActive) {
        L.circleMarker([Number(marina.lat), Number(marina.lng)], {
          radius: 13,
          color: "#fbbf24",
          weight: 2,
          fill: false,
          opacity: 0.85,
          pane: "markerPane",
        }).addTo(layer);
      }
      if (isActive && state.marker) {
        L.circleMarker([Number(marina.lat), Number(marina.lng)], {
          radius: 16,
          color: "#ef4444",
          weight: 2,
          fill: false,
          opacity: 0.9,
          pane: "markerPane",
        }).addTo(layer);
      }
    });
    state.offices.forEach((office) => {
      const officeColor = officeColors.get(office.id);
      L.marker([Number(office.lat), Number(office.lng)], {
        interactive: false,
        keyboard: false,
        icon: L.divIcon({
          className: "imggen-office-marker",
          html: `
            <div style="display:flex; align-items:center; gap:6px;">
              <span style="width:12px; height:12px; border-radius:2px; background:${officeColor?.fill || "#22c55e"}; border:2px solid ${officeColor?.stroke || "#dcfce7"}; display:inline-block; box-shadow:0 2px 8px rgba(0,0,0,0.35);"></span>
              <span style="background:rgba(15,23,42,0.88); color:${officeColor?.text || "#dcfce7"}; padding:3px 7px; border-radius:999px; font:600 11px/1.2 system-ui,sans-serif; white-space:nowrap; border:1px solid ${officeColor?.fill || "rgba(34,197,94,0.4)"};">${esc(office.name)}</span>
            </div>
          `,
          iconAnchor: [6, 6],
        }),
      }).addTo(layer);
    });
    updateLegend();
  }

  async function captureCurrentMap({ includeMetadata = false } = {}) {
    const html2canvas = (await import("html2canvas")).default;
    const canvas = await html2canvas(map.getContainer(), {
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      scale: Math.max(1, Math.min(4, Number(state.scale) || 2)),
      ignoreElements(element) {
        const classes = String(element?.className || "");
        if (classes.includes("leaflet-control")) return true;
        if (classes.includes("map-measure-control")) return true;
        if (classes.includes("map-search-control")) return true;
        if (classes.includes("map-rotate-control")) return true;
        return false;
      },
    });
    const dataUrl = state.format === "png"
      ? canvas.toDataURL("image/png")
      : canvas.toDataURL("image/jpeg", 0.92);
    if (!includeMetadata) return dataUrl;
    return {
      dataUrl,
      width: Number(canvas.width) || 0,
      height: Number(canvas.height) || 0,
    };
  }

  async function withStableReportViewport(task) {
    const docEl = document.documentElement;
    const previousInlineRightPanelWidth = docEl.style.getPropertyValue("--rightPanelW");
    const computedRightPanelWidth = getComputedStyle(docEl).getPropertyValue("--rightPanelW").trim();
    const needsResize = computedRightPanelWidth !== REPORT_CAPTURE_RIGHT_PANEL_WIDTH;
    try {
      if (needsResize) {
        docEl.style.setProperty("--rightPanelW", REPORT_CAPTURE_RIGHT_PANEL_WIDTH);
        map.invalidateSize(false);
        await delay(120);
        await waitForMapToSettle({ timeoutMs: 12000, stableMs: 700, pollMs: 100 });
      }
      return await task();
    } finally {
      if (needsResize) {
        if (previousInlineRightPanelWidth) docEl.style.setProperty("--rightPanelW", previousInlineRightPanelWidth);
        else docEl.style.removeProperty("--rightPanelW");
        map.invalidateSize(false);
        await delay(120);
        await waitForMapToSettle({ timeoutMs: 12000, stableMs: 700, pollMs: 100 });
      }
    }
  }

  async function captureReportMarinaImages(items, { onProgress = null } = {}) {
    const captures = [];
    const total = items.length;
    setCaptureProgress(0, total);
    for (let index = 0; index < total; index += 1) {
      const marina = items[index];
      const stateIndex = state.items.findIndex((entry) => entry.id === marina.id);
      const preparingMessage = `Screenshots ${captures.length}/${total} | Preparing ${index + 1}/${total}: ${marina.name}`;
      setStatus(preparingMessage);
      if (typeof onProgress === "function") onProgress(preparingMessage);
      if (stateIndex >= 0) {
        await focusItem(stateIndex, { saveCurrent: false, overlayItems: items });
        await warmVisibleLayersForCapture({ extraDelayMs: 350 });
      } else {
        applySavedView(marina);
        map.invalidateSize(false);
        await delay(100);
        drawPlanningOverlay(marina, { items });
        await waitForMapToSettle();
        await warmVisibleLayersForCapture({ extraDelayMs: 350 });
      }
      const capturingMessage = `Screenshots ${captures.length}/${total} | Capturing ${index + 1}/${total}: ${marina.name}`;
      setStatus(capturingMessage);
      if (typeof onProgress === "function") onProgress(capturingMessage);
      const dataUrl = await captureCurrentMap();
      captures.push({
        id: marina.id,
        name: marina.name,
        code: String(marina?.portfolio?.code || marina.name || "").trim(),
        dataUrl,
      });
      setCaptureProgress(captures.length, total);
      const capturedMessage = `Screenshots ${captures.length}/${total} captured`;
      setStatus(capturedMessage);
      if (typeof onProgress === "function") onProgress(capturedMessage);
    }
    return captures;
  }

  function findEsriImageryLayerIds() {
    return app.services.layers.listLayers()
      .filter((layer) => layer?.toolId === "overlay-manager" && layer?.kind === "basemap" && String(layer?.id || "").includes("esri-imagery"))
      .map((layer) => String(layer.id));
  }

  function setLayerVisibility(ids, visible) {
    ids.forEach((id) => {
      try { app.services.layers.setVisible(id, !!visible); } catch {}
    });
  }

  function captureLayerVisibility(ids) {
    const current = new Map();
    const layers = app.services.layers.listLayers();
    ids.forEach((id) => {
      const layer = layers.find((entry) => entry.id === id);
      current.set(id, !!layer?.visible);
    });
    return current;
  }

  function restoreLayerVisibility(snapshot) {
    snapshot.forEach((visible, id) => {
      try { app.services.layers.setVisible(id, !!visible); } catch {}
    });
  }

  async function warmVisibleLayersForCapture({ extraDelayMs = 600 } = {}) {
    try { map.invalidateSize({ pan: false, debounceMoveend: false }); } catch {}
    await delay(180);
    await waitForMapToSettle({ timeoutMs: 14000, stableMs: 900, pollMs: 120 });
    if (extraDelayMs > 0) await delay(extraDelayMs);
  }

  function reportEsc(value) {
    return String(value ?? "").replace(/[&<>"']/g, (ch) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    }[ch]));
  }

  function reclaimHydrotoolsFocus() {
    const focusMain = () => {
      try { window.focus?.(); } catch (_) {}
    };
    focusMain();
    try { setTimeout(focusMain, 0); } catch (_) {}
    try { setTimeout(focusMain, 60); } catch (_) {}
    try { setTimeout(focusMain, 180); } catch (_) {}
    try { requestAnimationFrame(focusMain); } catch (_) {}
  }

  function excelCell(value) {
    const text = String(value ?? "");
    return text
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }

  function downloadBlob(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 300);
  }

  async function waitForReportDocumentReady(doc) {
    if (!doc) return;
    if (doc.fonts?.ready) {
      try { await doc.fonts.ready; } catch (_) {}
    }
    const images = Array.from(doc.images || []);
    await Promise.all(images.map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener("load", done, { once: true });
        img.addEventListener("error", done, { once: true });
      });
    }));
    await delay(120);
  }

  const REPORT_FIRST_PAGE_LAYOUT = Object.freeze({
    summaryColumns: "minmax(320px, 0.95fr) minmax(0, 2.05fr)",
    summaryGap: "18px",
    metaMinHeight: "96px",
    metaMarginBottom: "16px",
    mapPanePaddingTop: "90px",
    mapTitleFontSize: "24px",
    mapTitleMarginBottom: "12px",
    printSummaryColumns: "minmax(82mm, 1fr) minmax(0, 2fr)",
    printSummaryGap: "12px",
    printMetaMarginBottom: "12px",
    printMapTitleFontSize: "18px",
    printMapTitleMarginBottom: "8px",
  });

  function reportFirstPageLayoutCss({ important = false } = {}) {
    const bang = important ? " !important" : "";
    return `
      .summary-layout { display:grid${bang}; grid-template-columns:${REPORT_FIRST_PAGE_LAYOUT.summaryColumns}${bang}; gap:${REPORT_FIRST_PAGE_LAYOUT.summaryGap}${bang}; align-items:start${bang}; }
      .summary-pane, .map-pane { min-width:0${bang}; }
      .meta { min-height:${REPORT_FIRST_PAGE_LAYOUT.metaMinHeight}${bang}; margin-bottom:${REPORT_FIRST_PAGE_LAYOUT.metaMarginBottom}${bang}; }
      .map-pane { padding-top:${REPORT_FIRST_PAGE_LAYOUT.mapPanePaddingTop}${bang}; }
      .map-wrap { display:flex${bang}; flex-direction:column${bang}; }
      .map-pane-title { text-align:center${bang}; font-size:${REPORT_FIRST_PAGE_LAYOUT.mapTitleFontSize}${bang}; line-height:1.15${bang}; margin:0 0 ${REPORT_FIRST_PAGE_LAYOUT.mapTitleMarginBottom}${bang}; color:var(--accent-strong)${bang}; letter-spacing:0.02em${bang}; }
      .map-frame { position:relative${bang}; display:flex${bang}; align-items:center${bang}; justify-content:center${bang}; overflow:hidden${bang}; aspect-ratio:var(--mapAspectRatio)${bang}; min-height:0${bang}; }
      .map-wrap img { width:100%${bang}; height:auto${bang}; max-width:100%${bang}; max-height:none${bang}; object-fit:contain${bang}; margin:0 auto${bang}; }
    `;
  }

  function reportFirstPagePrintLayoutCss({ important = false } = {}) {
    const bang = important ? " !important" : "";
    return `
      .summary-layout { grid-template-columns:${REPORT_FIRST_PAGE_LAYOUT.printSummaryColumns}${bang}; gap:${REPORT_FIRST_PAGE_LAYOUT.printSummaryGap}${bang}; align-items:start${bang}; }
      .meta { min-height:${REPORT_FIRST_PAGE_LAYOUT.metaMinHeight}${bang}; margin-bottom:${REPORT_FIRST_PAGE_LAYOUT.printMetaMarginBottom}${bang}; }
      .map-pane { padding-top:${REPORT_FIRST_PAGE_LAYOUT.mapPanePaddingTop}${bang}; }
      .map-pane, .map-wrap, .map-frame { height:auto${bang}; }
      .map-frame { min-height:0${bang}; aspect-ratio:var(--mapAspectRatio)${bang}; }
      .map-pane-title { font-size:${REPORT_FIRST_PAGE_LAYOUT.printMapTitleFontSize}${bang}; margin:0 0 ${REPORT_FIRST_PAGE_LAYOUT.printMapTitleMarginBottom}${bang}; }
      .map-wrap img { width:100%${bang}; height:auto${bang}; max-width:100%${bang}; max-height:none${bang}; object-fit:contain${bang}; margin:0 auto${bang}; }
    `;
  }

  async function renderReportHtmlToCanvases(report, { statusLabel = "", onProgress = null } = {}) {
    const html2canvas = (await import("html2canvas")).default;
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.left = "-20000px";
    iframe.style.top = "0";
    iframe.style.width = "1400px";
    iframe.style.height = "2200px";
    iframe.style.opacity = "0";
    iframe.style.pointerEvents = "none";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    try {
      const doc = iframe.contentDocument;
      if (!doc) throw new Error("Unable to create report document.");
      doc.open();
      doc.write(report.html);
      doc.close();
      const exportStyle = doc.createElement("style");
      exportStyle.textContent = `
        .no-print { display: none !important; }
        .page { min-height: auto !important; padding: 0 !important; }
        ${reportFirstPageLayoutCss({ important: true }).replaceAll("var(--accent-strong)", "#0f2d52")}
      `;
      doc.head.appendChild(exportStyle);
      await new Promise((resolve) => {
        const win = iframe.contentWindow;
        if (!win) return resolve();
        if (doc.readyState === "complete") return resolve();
        win.addEventListener("load", () => resolve(), { once: true });
        setTimeout(resolve, 400);
      });
      await waitForReportDocumentReady(doc);
      const pageNodes = Array.from(doc.querySelectorAll(".page"));
      if (!pageNodes.length) throw new Error("Report pages were not rendered.");
      const canvases = [];
      for (let index = 0; index < pageNodes.length; index += 1) {
        if (statusLabel) {
          const renderMessage = `${statusLabel} Rendering page ${index + 1}/${pageNodes.length}...`;
          setStatus(renderMessage);
          if (typeof onProgress === "function") onProgress(renderMessage);
        }
        const node = pageNodes[index];
        const canvas = await html2canvas(node, {
          backgroundColor: "#ffffff",
          useCORS: true,
          logging: false,
          scale: 2,
          windowWidth: Math.max(node.scrollWidth, 1400),
          windowHeight: Math.max(node.scrollHeight, 1800),
        });
        canvases.push(canvas);
      }
      return canvases;
    } finally {
      iframe.remove();
    }
  }

  async function createLandscapeLetterPdf() {
    const { jsPDF } = await import("jspdf");
    return new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "letter",
      compress: true,
    });
  }

  function appendCanvasesToPdf(pdf, canvases = [], { pageIndex = 0 } = {}) {
    if (!pdf) throw new Error("PDF document is required.");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 28;
    const contentWidth = Math.max(1, pdfWidth - (margin * 2));
    const contentHeight = Math.max(1, pdfHeight - (margin * 2));
    let nextPageIndex = Math.max(0, Number(pageIndex) || 0);
    canvases.forEach((canvas) => {
      const sliceHeightPx = Math.max(1, Math.floor(canvas.width * (contentHeight / contentWidth)));
      let offsetY = 0;
      while (offsetY < canvas.height) {
        const chunkHeight = Math.min(sliceHeightPx, canvas.height - offsetY);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = chunkHeight;
        const sliceCtx = sliceCanvas.getContext("2d");
        if (!sliceCtx) throw new Error("Unable to render PDF page.");
        sliceCtx.drawImage(
          canvas,
          0,
          offsetY,
          canvas.width,
          chunkHeight,
          0,
          0,
          canvas.width,
          chunkHeight,
        );
        if (nextPageIndex > 0) pdf.addPage("letter", "landscape");
        pdf.setPage(nextPageIndex + 1);
        const drawWidth = contentWidth;
        const drawHeight = Math.min(contentHeight, drawWidth * (sliceCanvas.height / sliceCanvas.width));
        const drawX = margin;
        const drawY = margin;
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
        pdf.addImage(sliceCanvas.toDataURL("image/jpeg", 0.92), "JPEG", drawX, drawY, drawWidth, drawHeight, undefined, "FAST");
        offsetY += chunkHeight;
        nextPageIndex += 1;
      }
    });
    return nextPageIndex;
  }

  async function reportHtmlToPdfBlob(report, { statusLabel = "", onProgress = null } = {}) {
    const canvases = await renderReportHtmlToCanvases(report, { statusLabel, onProgress });
    const pdf = await createLandscapeLetterPdf();
    appendCanvasesToPdf(pdf, canvases);
    return pdf.output("blob");
  }

  function downloadExcelWorkbook(fileName, sheets = []) {
    const blob = createExcelWorkbookBlob(sheets);
    downloadBlob(blob, fileName);
  }

  function createExcelWorkbookBlob(sheets = []) {
    const worksheetHtml = sheets.map((sheet) => `
      <table data-sheet-name="${excelCell(sheet.name || "Sheet1")}">
        <thead>
          <tr>${(sheet.columns || []).map((column) => `<th>${excelCell(column)}</th>`).join("")}</tr>
        </thead>
        <tbody>
          ${(sheet.rows || []).map((row) => {
            const rowObject = Array.isArray(row) ? { cells: row } : (row || {});
            const className = String(rowObject.className || "").trim();
            const cells = Array.isArray(rowObject.cells) ? rowObject.cells : [];
            return `<tr${className ? ` class="${excelCell(className)}"` : ""}>${cells.map((cell) => `<td>${excelCell(cell)}</td>`).join("")}</tr>`;
          }).join("")}
        </tbody>
      </table>
    `).join("");
    const html = `<!doctype html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
  <head>
    <meta charset="utf-8" />
    <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets>${sheets.map((sheet) => `<x:ExcelWorksheet><x:Name>${excelCell(sheet.name || "Sheet1")}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet>`).join("")}</x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
    <style>
      td,th{border:1px solid #cbd5e1;padding:4px 6px;}
      table{border-collapse:collapse;}
      tr.work-row td{background:#f9fbfe;}
      tr.travel-row td{background:#fef2f2;color:#7f1d1d;}
    </style>
  </head>
  <body>${worksheetHtml}</body>
</html>`;
    return new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
  }

  function marinaLocationSummary(item) {
    const p = item?.portfolio || {};
    const cityText = String(p.city || "").trim();
    const stateText = String(p.stateCountry || "").trim();
    const cityLooksLikeStateOnly = /^[A-Z]{2,3}$/.test(cityText);
    const hasRealCity = !!cityText && !cityLooksLikeStateOnly;
    if (!hasRealCity) return "";
    return stateText && cityText.toLowerCase().includes(stateText.toLowerCase())
      ? cityText
      : [cityText, stateText].filter(Boolean).join(", ");
  }

  function marinaCodeOrName(item) {
    const code = String(item?.portfolio?.code || "").trim();
    return code || String(item?.name || "marina").trim() || "marina";
  }

  function buildOfficeTripTableRows(plan) {
    return (plan?.days || []).map((day) => ({
      className: day.travelOnly ? "travel-row" : "work-row",
      cells: [
        day.day,
        day.travelOnly || !Array.isArray(day.stops) || !day.stops.length
          ? (day.travelLabel || "-")
          : Array.from(new Set(day.stops.map((stop) => String(stop?.item?.portfolio?.code || "").trim()).filter(Boolean))).join(", ") || "-",
        day.travelOnly || !Array.isArray(day.stops) || !day.stops.length
          ? "-"
          : Array.from(new Set(day.stops.map((stop) => String(stop?.item?.portfolio?.archetype || "").trim()).filter(Boolean))).join(", ") || "-",
        day.travelOnly || !Array.isArray(day.stops) || !day.stops.length
          ? "-"
          : (() => {
              let total = 0;
              let found = false;
              day.stops.forEach((stop) => {
                const raw = String(stop?.item?.portfolio?.wetSlips || "").trim();
                const value = Number.parseFloat(raw.replace(/[^0-9.\-]+/g, ""));
                if (Number.isFinite(value)) {
                  total += value;
                  found = true;
                }
              });
              return found ? String(Math.round(total)) : "-";
            })(),
        day.travelOnly || !Array.isArray(day.stops) || !day.stops.length
          ? "-"
          : (day.stops.some((stop) => stop?.item?.coveredSlips === true) ? "Yes" : "No"),
        day.travelOnly || !Array.isArray(day.stops) || !day.stops.length
          ? "-"
          : (day.stops.some((stop) => stop?.item?.noFlyZone === true) ? "Yes" : "No"),
        day.startMode === "hotel" ? "Hotel / field" : "Office / home",
        day.travelOnly
          ? (day.travelLabel || "Travel only day")
          : (day.stops.map((stop) => stop.item.name).join(", ") || "-"),
        day.travelOnly
          ? ""
          : Array.from(new Set(day.stops.map((stop) => marinaLocationSummary(stop.item)).filter(Boolean))).slice(0, 3).join(" | "),
        formatMilesPrecise(day.driveMiles),
        formatDriveMinutes(day.driveMinutes),
        day.perDiemCost ? formatCurrency(day.perDiemCost) : "-",
        formatDriveMinutes(day.workMinutes),
        formatDriveMinutes(day.totalMinutes || (day.driveMinutes + day.workMinutes)),
        day.travelOnly
          ? (day.endMode === "office" ? "Travel only / office arrival" : "Travel only / overnight")
          : (day.endMode === "office" ? "Return home" : day.endMode === "hotel" ? "Hotel / overnight" : "Field end"),
      ],
    }));
  }

  function exportFullDatabaseExcel() {
    const items = state.items.filter((item) => item.excludeFromReport !== true);
    const columns = [
      "Name",
      "Code",
      "Office",
      "Latitude",
      "Longitude",
      "Location",
      "Type",
      "Wet Slips",
      "Dry Storage",
      "Covered Slips",
      "No Fly Zone",
      "Prioritize Air Travel",
      "Excluded",
      "Survey Hours",
      "Property Count",
      "Neighborhood",
      "Cluster",
    ];
    const rows = items.map((item) => [
      item.name,
      item?.portfolio?.code || "",
      item.officeName || "",
      item.lat,
      item.lng,
      marinaLocationSummary(item),
      item?.portfolio?.archetype || "",
      item?.portfolio?.wetSlips || "",
      item?.portfolio?.dryStorage || "",
      item.coveredSlips ? "Yes" : "No",
      item.noFlyZone ? "Yes" : "No",
      item.prioritizeAirTravel ? "Yes" : "No",
      item.excludeFromReport ? "Yes" : "No",
      item.surveyHours ?? state.planning.defaultSurveyHours,
      item?.portfolio?.propertyCount ?? "",
      item.neighborhoodId || "",
      item.clusterLabel || "",
    ]);
    downloadExcelWorkbook(`${sanitizeFilePart(state.datasetName || "marina-database", "marina-database")}.xls`, [
      { name: "Marinas", columns, rows },
    ]);
    setStatus(`Exported ${items.length} marinas to Excel.`);
    app.ui.toast(`Exported ${items.length} marinas to Excel.`);
  }

  async function exportOfficeTripTableExcel(officeId) {
    const { office, marinas } = scopeForOffice(officeId, { excludeReportExcluded: true });
    if (!office || !marinas.length) {
      app.ui.toast("No reportable marinas are assigned to that office.");
      return;
    }
    const plan = await enrichPlanCosts(buildTripPlan(officeId, { excludeReportExcluded: true }), office);
    const columns = ["Day", "Code", "Type", "Wet Slips", "Covered Slips", "No Fly Zone", "Start", "Marinas", "Cities", "Mileage", "Drive", "Per Diem", "Survey", "Total", "End"];
    const summaryRows = [
      ["Office", office.name],
      ["Marinas", plan.totals.marinaCount],
      ["Trip Days", plan.totals.baseTripDays ?? plan.days.length],
      ["Travel Mode", String(plan.travelMode || "drive").toUpperCase()],
      ["Weather Contingency", `${Number(plan.totals.weatherContingencyPct || 0).toFixed(0)}%`],
      ["Weather Days", plan.totals.weatherContingencyDays || 0],
      ["Trip Days w/ Weather", plan.totals.adjustedTripDays ?? plan.days.length],
      ["Travel Only Days", plan.totals.travelOnlyDays || 0],
      ["Hotel Nights", plan.totals.hotelNights],
      ["Price per Marina", formatCurrency(plan.totals.pricePerMarina)],
      ["Marina Revenue", formatCurrency(plan.totals.marinaRevenue)],
      ["Weather/Travel Contingency Day Rate", formatCurrency(plan.totals.supportDayRate || 0)],
      ["Weather/Travel Contingency Days", plan.totals.supportDayQty || 0],
      ["Weather/Travel Contingency Cost", formatCurrency(plan.totals.laborEquipmentCost || 0)],
      ["Mileage", formatMilesPrecise(plan.totals.driveMiles)],
      ["Mileage Rate", `${formatCurrency(plan.costs?.mileageRate || FEDERAL_MILEAGE_RATE_2026)} / mi`],
      ["Mileage Cost", formatCurrency(plan.totals.mileageCost)],
      ["Airfare", formatCurrency(plan.totals.airfareCost || 0)],
      ["Per Diem", formatCurrency(plan.totals.perDiemCost)],
      ["Per Diem Source", plan.totals.perDiemSource || "Not used"],
      ["Per Diem Fallback Reason", plan.totals.perDiemReason || "-"],
      ["Travel Cost", formatCurrency(plan.totals.travelCost)],
      ["Estimate Total", formatCurrency(plan.totals.estimateTotal)],
      ["Drive Time", formatDriveMinutes(plan.totals.driveMinutes)],
      ["Survey Time", formatDriveMinutes(plan.totals.surveyMinutes)],
      ["Total Time", formatDriveMinutes(plan.totals.totalMinutes)],
      ["Routing", routingStatusLabel(plan.routing)],
    ];
    downloadExcelWorkbook(`${sanitizeFilePart(`${office.name}-trip-table`, "office-trip-table")}.xls`, [
      { name: "Summary", columns: ["Metric", "Value"], rows: summaryRows },
      { name: "Daily Plan", columns, rows: buildOfficeTripTableRows(plan) },
    ]);
    setStatus(`Exported ${office.name} trip table to Excel.`);
    app.ui.toast(`Exported ${office.name} trip table.`);
  }

  function tripReportHtml({ office, plan, mapImageDataUrl, mapImageWidth = 0, mapImageHeight = 0, marinaImages = [], reportTitle = "" }) {
    const generatedAt = new Date().toLocaleString();
    const safeMapWidth = Math.max(1, Number(mapImageWidth) || 1);
    const safeMapHeight = Math.max(1, Number(mapImageHeight) || 1);
    const mapAspectRatio = `${safeMapWidth} / ${safeMapHeight}`;
    function dayCodeSummary(day) {
      if (day.travelOnly || !Array.isArray(day.stops) || !day.stops.length) return day.travelLabel || "-";
      const codes = day.stops
        .map((stop) => String(stop?.item?.portfolio?.code || "").trim())
        .filter(Boolean);
      return codes.length ? Array.from(new Set(codes)).join(", ") : "-";
    }
    function dayTypeSummary(day) {
      if (day.travelOnly || !Array.isArray(day.stops) || !day.stops.length) return "-";
      const types = day.stops
        .map((stop) => String(stop?.item?.portfolio?.archetype || "").trim())
        .filter(Boolean);
      return types.length ? Array.from(new Set(types)).join(", ") : "-";
    }
    function dayWetSlipSummary(day) {
      if (day.travelOnly || !Array.isArray(day.stops) || !day.stops.length) return "-";
      let total = 0;
      let found = false;
      day.stops.forEach((stop) => {
        const raw = String(stop?.item?.portfolio?.wetSlips || "").trim();
        const value = Number.parseFloat(raw.replace(/[^0-9.\-]+/g, ""));
        if (Number.isFinite(value)) {
          total += value;
          found = true;
        }
      });
      return found ? String(Math.round(total)) : "-";
    }
    function dayCoveredSlipSummary(day) {
      if (day.travelOnly || !Array.isArray(day.stops) || !day.stops.length) return "-";
      return day.stops.some((stop) => stop?.item?.coveredSlips === true) ? "Yes" : "No";
    }
    function dayMileageSummary(day) {
      return formatMilesPrecise(day.driveMiles);
    }
    function dayPerDiemSummary(day) {
      return day.perDiemCost ? formatCurrency(day.perDiemCost) : "-";
    }
    function dayPortfolioSummary(day) {
      if (day.travelOnly || !Array.isArray(day.stops) || !day.stops.length) return "";
      const cities = new Set();
      day.stops.forEach((stop) => {
        const p = stop?.item?.portfolio;
        if (!p) return;
        const cityText = String(p.city || "").trim();
        const stateText = String(p.stateCountry || "").trim();
        const cityLooksLikeStateOnly = /^[A-Z]{2,3}$/.test(cityText);
        const hasRealCity = !!cityText && !cityLooksLikeStateOnly;
        const cityLabel = !hasRealCity
          ? ""
          : stateText && cityText.toLowerCase().includes(stateText.toLowerCase())
            ? cityText
            : [cityText, stateText].filter(Boolean).join(", ");
        if (cityLabel) cities.add(cityLabel);
      });
      return cities.size ? Array.from(cities).slice(0, 3).join(" | ") : "";
    }
    const rows = plan.days.map((day) => `
      <tr class="${day.travelOnly ? "travel-row" : "work-row"}">
        <td>${day.day}</td>
        <td>${reportEsc(dayCodeSummary(day))}</td>
        <td>${reportEsc(dayTypeSummary(day))}</td>
        <td>${reportEsc(dayWetSlipSummary(day))}</td>
        <td>${reportEsc(dayCoveredSlipSummary(day))}</td>
        <td>${reportEsc(day.startMode === "hotel" ? "Hotel / field" : "Office / home")}</td>
        <td>
          <div>${reportEsc(day.travelOnly ? (day.travelLabel || "Travel only day") : (day.stops.map((stop) => stop.item.name).join(", ") || "-"))}</div>
          ${dayPortfolioSummary(day) ? `<div class="subline">${reportEsc(dayPortfolioSummary(day))}</div>` : ""}
        </td>
        <td>${reportEsc(dayMileageSummary(day))}</td>
        <td>${reportEsc(formatDriveMinutes(day.driveMinutes))}</td>
        <td>${reportEsc(dayPerDiemSummary(day))}</td>
        <td>${reportEsc(formatDriveMinutes(day.workMinutes))}</td>
        <td>${reportEsc(formatDriveMinutes(day.totalMinutes || (day.driveMinutes + day.workMinutes)))}</td>
        <td>${reportEsc(day.travelOnly ? (day.endMode === "office" ? "Travel only / office arrival" : "Travel only / overnight") : (day.endMode === "office" ? "Return home" : day.endMode === "hotel" ? "Hotel / overnight" : "Field end"))}</td>
      </tr>
    `).join("");
    const imagePages = [];
    const marinaImagesPerPage = 6;
    for (let index = 0; index < marinaImages.length; index += marinaImagesPerPage) {
      imagePages.push(marinaImages.slice(index, index + marinaImagesPerPage));
    }
    const imagePagesHtml = imagePages.map((pageItems, pageIndex) => `
      <div class="page images-page">
        <h2 class="page-title">${pageIndex === 0 ? "Marina Images" : `Marina Images ${pageIndex + 1}`}</h2>
        <div class="image-grid">
          ${pageItems.map((entry) => `
            <figure class="image-card">
              <div class="image-card-label">${reportEsc(entry.code || entry.name)}</div>
              <img src="${entry.dataUrl}" alt="${reportEsc(entry.name)}" />
            </figure>
          `).join("")}
        </div>
      </div>
    `).join("");
    const routingLabel = routingStatusLabel(plan.routing);
    const perDiemSourceLabel = plan.totals.perDiemSource || "Not used";
    const perDiemReasonLabel = plan.totals.perDiemReason || "";
    const resolvedReportTitle = reportTitle || `Trip Report: ${office?.name || "Office"} Office Proximity`;
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${reportEsc(resolvedReportTitle)}</title>
    <style>
      :root {
        color-scheme: light;
        --ink:#10233f;
        --ink-soft:#415a77;
        --ink-faint:#6b7d94;
        --panel:#f6f9fc;
        --panel-strong:#e6eef7;
        --panel-accent:#d8e5f3;
        --border:#bfd0e2;
        --accent:#173b67;
        --accent-strong:#0f2d52;
        --accent-soft:#2f5f95;
      }
      @page { size: landscape; margin: 10mm; }
      body { font: 13px/1.45 "Segoe UI", Arial, sans-serif; color:var(--ink); margin:0; background:#fff; }
      h1,h2 { margin:0 0 8px; color:var(--accent-strong); letter-spacing:0.01em; }
      .page { min-height:100vh; box-sizing:border-box; padding:18px; }
      .page + .page { page-break-before: always; }
      .report-title { text-align:center; font-size:32px; line-height:1.1; margin:0 0 18px; color:var(--accent-strong); letter-spacing:0.02em; }
      .page-title { text-align:center; font-size:24px; line-height:1.15; margin:0 0 14px; color:var(--accent-strong); letter-spacing:0.02em; }
      .meta { color:var(--ink-soft); }
      ${reportFirstPageLayoutCss()}
      .estimate-block { border:1px solid var(--border); border-radius:12px; background:linear-gradient(180deg, #fbfdff 0%, var(--panel) 100%); overflow:hidden; margin-top:14px; box-shadow:0 10px 22px rgba(16,35,63,0.05); }
      .estimate-head { padding:10px 12px; background:linear-gradient(180deg, var(--panel-accent) 0%, var(--panel-strong) 100%); font:700 12px/1.2 "Segoe UI", Arial, sans-serif; color:var(--accent-strong); text-transform:uppercase; letter-spacing:0.06em; }
      .estimate-body { padding:8px 12px; }
      .estimate-grid { display:grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap:8px 14px; }
      .estimate-line { display:flex; justify-content:space-between; gap:12px; padding:4px 0; border-bottom:1px solid var(--panel-accent); }
      .estimate-line:last-child { border-bottom:none; }
      .estimate-label { color:var(--ink-soft); }
      .estimate-value { font-weight:700; text-align:right; color:var(--accent-strong); }
      .estimate-table { width:100%; border-collapse:collapse; font-size:12px; }
      .estimate-table th, .estimate-table td { padding:6px 4px; border-bottom:1px solid var(--panel-accent); text-align:left; }
      .estimate-table th { color:var(--ink-faint); font-size:11px; background:transparent; text-transform:uppercase; letter-spacing:0.04em; }
      .estimate-table td.num, .estimate-table th.num { text-align:right; }
      .estimate-table tr.total-row td { border-top:2px solid var(--border); border-bottom:none; font-weight:700; color:var(--accent-strong); }
      .map-wrap img { border:1px solid var(--border); border-radius:12px; display:block; background:var(--panel); box-shadow:0 12px 28px rgba(16,35,63,0.08); }
      table { width:100%; border-collapse:collapse; margin-top:10px; }
      th, td { border:1px solid var(--border); padding:5px 6px; vertical-align:top; text-align:center; }
      th { background:linear-gradient(180deg, var(--panel-accent) 0%, var(--panel-strong) 100%); color:var(--accent-strong); font-size:11px; line-height:1.15; text-transform:uppercase; letter-spacing:0.04em; }
      td { font-size:10.5px; line-height:1.2; color:var(--ink); }
      .subline { margin-top:3px; color:var(--ink-soft); font-size:9.5px; line-height:1.15; text-align:center; }
      .table-page tbody tr.work-row td { background:#fbfdff; }
      .table-page tbody tr.work-row:nth-child(even) td { background:#f6faff; }
      .table-page tbody tr.travel-row td { background:#fef2f2; color:#7f1d1d; }
      .table-page table { table-layout:fixed; }
      .table-page th:nth-child(1), .table-page td:nth-child(1) { width:4%; }
      .table-page th:nth-child(2), .table-page td:nth-child(2) { width:7%; }
      .table-page th:nth-child(3), .table-page td:nth-child(3) { width:7%; }
      .table-page th:nth-child(4), .table-page td:nth-child(4) { width:6%; }
      .table-page th:nth-child(5), .table-page td:nth-child(5) { width:7%; }
      .table-page th:nth-child(6), .table-page td:nth-child(6) { width:8%; }
      .table-page th:nth-child(7), .table-page td:nth-child(7) { width:17%; }
      .table-page th:nth-child(8), .table-page td:nth-child(8) { width:8%; }
      .table-page th:nth-child(9), .table-page td:nth-child(9) { width:8%; }
      .table-page th:nth-child(10), .table-page td:nth-child(10) { width:6%; }
      .table-page th:nth-child(11), .table-page td:nth-child(11) { width:7%; }
      .table-page th:nth-child(12), .table-page td:nth-child(12) { width:7%; }
      .table-page th:nth-child(13), .table-page td:nth-child(13) { width:8%; }
      .images-page {
        page-break-before: always;
        break-before: page;
        page-break-inside: avoid;
        break-inside: avoid-page;
      }
      .image-grid { display:grid; grid-template-columns:repeat(3, minmax(0, 1fr)); gap:12px; margin-top:12px; }
      .image-card {
        margin:0;
        border:1px solid var(--border);
        border-radius:10px;
        overflow:hidden;
        background:var(--panel);
        page-break-inside:avoid;
        break-inside: avoid-page;
        box-shadow:0 10px 22px rgba(16,35,63,0.05);
      }
      .image-card-label { padding:6px 8px; font:700 11px/1.2 "Segoe UI", Arial, sans-serif; background:linear-gradient(180deg, var(--panel-accent) 0%, var(--panel-strong) 100%); color:var(--accent-strong); }
      .image-card img { display:block; width:100%; height:auto; }
      @media print {
        .no-print { display:none; }
        .page { min-height:auto; padding:0; }
        .summary-pane { max-width:none; }
        .estimate-block { margin-top:10px; }
        .estimate-head { padding:8px 10px; font-size:11px; }
        .estimate-body { padding:6px 10px; }
        .estimate-grid { grid-template-columns: 1fr; gap:4px 0; }
        .estimate-line { padding:3px 0; }
        ${reportFirstPagePrintLayoutCss()}
        .images-page {
          page-break-before:always;
          break-before:page;
          page-break-inside: avoid;
          break-inside: avoid-page;
        }
        .report-title { font-size:24px; margin:0 0 10px; }
        .page-title { font-size:18px; margin:0 0 8px; }
        .image-grid { gap:10px; }
      }
    </style>
  </head>
  <body>
    <div class="no-print" style="margin:12px 18px;">
      <button onclick="window.print()">Print</button>
    </div>
    <div class="page">
      <h1 class="report-title">${reportEsc(resolvedReportTitle)}</h1>
      <h2 class="page-title">Summary</h2>
      <div class="summary-layout">
        <div class="summary-pane">
          <div class="meta">
            Office: <strong>${reportEsc(office?.name || "Unknown")}</strong><br />
            Generated: ${reportEsc(generatedAt)}<br />
            Routing: ${reportEsc(routingLabel)}<br />
            Travel Mode: ${reportEsc(String(plan.travelMode || "drive").toUpperCase())}<br />
            Per Diem: ${reportEsc(perDiemSourceLabel)}${perDiemReasonLabel ? `<br />Per Diem Fallback: ${reportEsc(perDiemReasonLabel)}` : ""}
          </div>
          <div class="estimate-block">
            <div class="estimate-head">Summary</div>
            <div class="estimate-body estimate-grid">
              <div class="estimate-line"><span class="estimate-label">Marinas</span><span class="estimate-value">${plan.totals.marinaCount}</span></div>
              <div class="estimate-line"><span class="estimate-label">Trip Days</span><span class="estimate-value">${plan.totals.baseTripDays ?? plan.days.length}</span></div>
              <div class="estimate-line"><span class="estimate-label">Weather Contingency</span><span class="estimate-value">${reportEsc(`${Number(plan.totals.weatherContingencyPct || 0).toFixed(0)}%`)}</span></div>
              <div class="estimate-line"><span class="estimate-label">Weather Days</span><span class="estimate-value">${plan.totals.weatherContingencyDays || 0}</span></div>
              <div class="estimate-line"><span class="estimate-label">Trip Days w/ Weather</span><span class="estimate-value">${plan.totals.adjustedTripDays ?? plan.days.length}</span></div>
              <div class="estimate-line"><span class="estimate-label">Travel Only Days</span><span class="estimate-value">${plan.totals.travelOnlyDays || 0}</span></div>
              <div class="estimate-line"><span class="estimate-label">Travel Mode</span><span class="estimate-value">${reportEsc(String(plan.travelMode || "drive").toUpperCase())}</span></div>
              <div class="estimate-line"><span class="estimate-label">Hotel Nights</span><span class="estimate-value">${plan.totals.hotelNights}</span></div>
              <div class="estimate-line"><span class="estimate-label">Mileage</span><span class="estimate-value">${reportEsc(formatMilesPrecise(plan.totals.driveMiles))}</span></div>
              <div class="estimate-line"><span class="estimate-label">Airfare</span><span class="estimate-value">${reportEsc(formatCurrency(plan.totals.airfareCost || 0))}</span></div>
              <div class="estimate-line"><span class="estimate-label">Drive Time</span><span class="estimate-value">${reportEsc(formatDriveMinutes(plan.totals.driveMinutes))}</span></div>
              <div class="estimate-line"><span class="estimate-label">Survey Time</span><span class="estimate-value">${reportEsc(formatDriveMinutes(plan.totals.surveyMinutes))}</span></div>
              <div class="estimate-line"><span class="estimate-label">Total Time</span><span class="estimate-value">${reportEsc(formatDriveMinutes(plan.totals.totalMinutes))}</span></div>
              <div class="estimate-line"><span class="estimate-label">Routing</span><span class="estimate-value">${reportEsc(plan.routing?.mode || "estimated")}</span></div>
              <div class="estimate-line"><span class="estimate-label">Per Diem Source</span><span class="estimate-value">${reportEsc(perDiemSourceLabel)}</span></div>
              <div class="estimate-line"><span class="estimate-label">Per Diem Fallback</span><span class="estimate-value">${reportEsc(perDiemReasonLabel || "-")}</span></div>
            </div>
          </div>
          <div class="estimate-block">
            <div class="estimate-head">Cost Estimate</div>
            <div class="estimate-body">
              <table class="estimate-table">
                <thead>
                  <tr>
                    <th>Line Item</th>
                    <th class="num">Rate</th>
                    <th class="num">Qty.</th>
                    <th class="num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Marina Survey</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.pricePerMarina))}</td>
                    <td class="num">${plan.totals.marinaCount}</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.marinaRevenue))}</td>
                  </tr>
                  <tr>
                    <td>Mileage</td>
                    <td class="num">${reportEsc(formatCurrency(plan.costs?.mileageRate || FEDERAL_MILEAGE_RATE_2026))}/mi</td>
                    <td class="num">${reportEsc(formatMilesPrecise(plan.totals.driveMiles))}</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.mileageCost))}</td>
                  </tr>
                  <tr>
                    <td>Airfare</td>
                    <td class="num">Estimated</td>
                    <td class="num">${plan.totals.airfareCost > 0 ? "1" : "0"}</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.airfareCost || 0))}</td>
                  </tr>
                  <tr>
                    <td>Per Diem</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.perDiemRate || 0))}/day</td>
                    <td class="num">${plan.totals.perDiemDays || 0}</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.perDiemCost))}</td>
                  </tr>
                  <tr>
                    <td>Weather Contingency</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.supportDayRate || 0))}/day</td>
                    <td class="num">${plan.totals.weatherContingencyDays || 0}</td>
                    <td class="num">${reportEsc(formatCurrency((plan.totals.supportDayRate || 0) * (plan.totals.weatherContingencyDays || 0)))}</td>
                  </tr>
                  <tr>
                    <td>Travel Days</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.supportDayRate || 0))}/day</td>
                    <td class="num">${plan.totals.travelOnlyDays || 0}</td>
                    <td class="num">${reportEsc(formatCurrency((plan.totals.supportDayRate || 0) * (plan.totals.travelOnlyDays || 0)))}</td>
                  </tr>
                  <tr class="total-row">
                    <td>Estimate Total</td>
                    <td class="num"></td>
                    <td class="num"></td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.estimateTotal))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="map-pane">
          <div class="map-wrap">
            <h2 class="map-pane-title">Coverage Map</h2>
            <div class="map-frame" style="--mapAspectRatio:${mapAspectRatio};">
              <img src="${mapImageDataUrl}" alt="Trip coverage map" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="page table-page">
      <h2 class="page-title">Daily Detail</h2>
      <table>
        <thead>
          <tr>
            <th>Day</th>
            <th>Code</th>
            <th>Type</th>
            <th>Wet Slips</th>
            <th>Covered Slips</th>
            <th>Start</th>
            <th>Marinas</th>
            <th>Mileage</th>
            <th>Drive</th>
            <th>Per Diem</th>
            <th>Survey</th>
            <th>Total</th>
            <th>End</th>
          </tr>
        </thead>
        <tbody>${rows || `<tr><td colspan="13">No trip days available.</td></tr>`}</tbody>
      </table>
    </div>
    ${imagePagesHtml || `<div class="page images-page"><h2 class="page-title">Marina Images</h2><div>No marina images captured.</div></div>`}
  </body>
</html>`;
  }

  async function printOfficeTripReport(officeId, reportWindow, { missionScope = "all" } = {}) {
    const report = await generateOfficeTripReportHtml(officeId, { missionScope });
    if (!ENABLE_REPORT_POPUPS) {
      throw new Error("Report popups are temporarily disabled.");
    }
    const win = reportWindow || window.open("", "_blank");
    if (!win) throw new Error("Popup blocked");
    win.document.open();
    win.document.write(report.html);
    win.document.close();
    setStatus(`Trip report ready for ${report.office.name}.`);
  }

  async function generateOfficeTripReportPdf(officeId, { missionScope = "all", onProgress = null } = {}) {
    const report = await generateOfficeTripReportHtml(officeId, { missionScope, onProgress });
    const statusLabel = `Preparing PDF for ${report.office.name}${missionScope !== "all" ? ` (${missionScope})` : ""}...`;
    setStatus(statusLabel);
    if (typeof onProgress === "function") onProgress(statusLabel);
    const blob = await reportHtmlToPdfBlob(report, { statusLabel, onProgress });
    const fileName = `${sanitizeFilePart(report.title.replace(/:/g, ""), "trip-report")}.pdf`;
    return { ...report, blob, fileName };
  }

  async function prepareReportPlan(officeId, { missionScope = "all", onProgress = null } = {}) {
    const waitForReady = async (checkFn, { timeoutMs = 45000, pollMs = 400 } = {}) => {
      const start = Date.now();
      while ((Date.now() - start) < timeoutMs) {
        const result = checkFn();
        if (result === true) return true;
        if (typeof result === "string" && result) throw new Error(result);
        await delay(pollMs);
      }
      return false;
    };
    const officeName = state.offices.find((entry) => entry.id === officeId)?.name || "office";
    const scopeSuffix = missionScope !== "all" ? ` (${missionScope})` : "";
    const progress = (message) => {
      setStatus(message);
      if (typeof onProgress === "function") onProgress(message);
    };
    progress(`Preparing route data for ${officeName}${scopeSuffix}...`);
    await refreshRouteMatrix({ force: true, officeId, excludeReportExcluded: true, missionScope });
    const routeReady = await waitForReady(() => {
      if (routeMatrixStatus === "ready") return true;
      if (routeMatrixStatus === "fallback") {
        return `Routing not ready: ${routeMatrixError || routeMatrixStatus}`;
      }
      return false;
    });
    progress(`Building trip plan for ${officeName}${scopeSuffix}...`);
    const scope = scopeForOffice(officeId, { excludeReportExcluded: true, missionScope });
    const rawPlan = buildTripPlan(officeId, { excludeReportExcluded: true, missionScope });
    progress(`Pricing trip plan for ${officeName}${scopeSuffix}...`);
    const plan = await enrichPlanCosts(rawPlan, scope.office);
    if (!routeReady || routeMatrixStatus !== "ready") {
      throw new Error(`Routing not ready: ${routingStatusLabel(plan.routing)}`);
    }
    if (state.showTripRoute) {
      progress(`Preparing route overlay for ${officeName}${scopeSuffix}...`);
      const geometryResult = await refreshRouteGeometry(rawPlan, { force: true });
      const reportGeometryKey = String(rawPlan?.routeScopeKey || rawPlan?.routing?.scopeKey || routeScopeKey(scope.office, scope.marinas));
      const hasDirectGeometry = geometryResult?.key === reportGeometryKey
        && geometryResult?.status === "ready"
        && Array.isArray(geometryResult?.features)
        && geometryResult.features.length > 0;
      const hasGlobalGeometry = routeGeometryKey === reportGeometryKey
        && Array.isArray(routeGeometryFeatures)
        && routeGeometryFeatures.length > 0;
      if (!hasDirectGeometry && !hasGlobalGeometry) {
        throw new Error(`Route overlay not ready: ${geometryResult?.error || routeGeometryError || geometryResult?.status || routeGeometryStatus}`);
      }
    }
    const perDiemTone = statusTone("perDiem", plan.totals.perDiemSource || "Not used");
    if (perDiemTone === "bad") {
      throw new Error(`Per diem not ready: ${plan.totals.perDiemSource || "unknown"}${plan.totals.perDiemReason ? ` (${plan.totals.perDiemReason})` : ""}`);
    }
    return { scope, rawPlan, plan, reportRouteScopeKey: String(rawPlan?.routeScopeKey || rawPlan?.routing?.scopeKey || routeScopeKey(scope.office, scope.marinas)) };
  }

  async function generateScopedOfficeReportPdfs(officeId) {
    const scopes = officeReportScopes(officeId, { excludeReportExcluded: true });
    const total = Math.max(1, scopes.length);
    const downloads = [];
    const autoDownload = total === 1;
    const launcherTitle = `Trip Reports: ${scopes[0]?.office?.name || "Office"}`;
    const reportWindow = total > 1
      ? openPreparedReportWindow("Preparing multiple trip reports. Links will appear here as each PDF is ready.", "imggen-trip-report-launcher")
      : null;
    const launcherReports = scopes.map((scope) => ({
      key: scope?.key || "all",
      label: reportLauncherLabel(scope),
      status: "Queued",
      ready: false,
      openUrl: "",
      downloadUrl: "",
      fileName: "",
    }));
    if (reportWindow) {
      updateReportLauncherWindow(reportWindow, launcherTitle, launcherReports, {
        status: autoDownload
          ? `Preparing 0/${total} reports...`
          : `Preparing 0/${total} reports. Download each file from this page when it is ready.`,
        complete: false,
      });
    }
    for (let index = 0; index < total; index += 1) {
      const scope = scopes[index] || null;
      setStatus(`Generating PDF report ${index + 1}/${total} for ${scope?.office?.name || "office"}${scope?.key ? ` (${scope.key})` : ""}...`);
      if (reportWindow && launcherReports[index]) {
        launcherReports[index].status = "Generating...";
        updateReportLauncherWindow(reportWindow, launcherTitle, launcherReports, {
          status: `Preparing ${index + 1}/${total} reports...`,
          complete: false,
        });
      }
      const built = await generateOfficeTripReportPdf(officeId, {
        missionScope: scope?.key || "all",
        onProgress: reportWindow && launcherReports[index]
          ? (message) => {
              launcherReports[index].status = String(message || "Generating...");
              updateReportLauncherWindow(reportWindow, launcherTitle, launcherReports, {
                status: autoDownload
                  ? `Preparing ${index + 1}/${total} reports...`
                  : `Preparing ${index + 1}/${total} reports. Download each file from this page when it is ready.`,
                complete: false,
              });
            }
          : null,
      });
      if (autoDownload) downloadBlob(built.blob, built.fileName);
      downloads.push(built.fileName);
      if (reportWindow && launcherReports[index]) {
        const reportUrls = createPdfLauncherUrls(built.blob, built.fileName);
        launcherReports[index].status = built.fileName;
        launcherReports[index].ready = true;
        launcherReports[index].openUrl = reportUrls.openUrl;
        launcherReports[index].downloadUrl = reportUrls.downloadUrl;
        launcherReports[index].fileName = built.fileName;
        updateReportLauncherWindow(reportWindow, launcherTitle, launcherReports, {
          status: autoDownload
            ? `Prepared ${index + 1}/${total} reports.`
            : `Prepared ${index + 1}/${total} reports. Use the links below to download them.`,
          complete: index === total - 1,
        });
      }
      app.ui.toast(autoDownload ? `Downloaded ${built.fileName}` : `Prepared ${built.fileName}`);
    }
    return downloads;
  }

  async function generateAllOfficeReports() {
    const offices = state.offices.filter((office) => officeReportScopes(office.id, { excludeReportExcluded: true }).length);
    if (!offices.length) {
      app.ui.toast("No office reports are available.");
      return;
    }
    const previousTripOfficeId = state.planning.tripOfficeId;
    const previousSelectedOfficeIds = [...state.selectedOfficeIds];
    const previousVisibleOfficeMarinaIds = [...state.visibleOfficeMarinaIds];
    const previousView = snapshotMapView();
    const previousActiveIndex = state.activeIndex;
    let totalDownloads = 0;
    const officeScopeQueue = offices.flatMap((office) =>
      officeReportScopes(office.id, { excludeReportExcluded: true }).map((scope) => ({
        officeId: office.id,
        officeName: office.name,
        scope,
      }))
    );
    const launcherReports = officeScopeQueue.map((entry, index) => ({
      id: `${entry.officeId}:${entry.scope?.key || "all"}:${index}`,
      label: reportLauncherLabel(entry.scope),
      status: "Queued",
      ready: false,
      openUrl: "",
      downloadUrl: "",
      fileName: "",
    }));
    const summaryRows = [];
    let currentSummaryActions = [];
    const reportWindow = openPreparedReportWindow(
      "Preparing all trip reports. Links will appear here as each PDF is ready.",
      "imggen-all-trip-reports-launcher",
    );
    try {
      updateReportLauncherWindow(reportWindow, "All Trip Reports", launcherReports, {
        status: `Preparing 0/${officeScopeQueue.length} reports. Download each file from this page when it is ready.`,
        complete: false,
        summaryActions: currentSummaryActions,
      });
      for (let officeIndex = 0; officeIndex < offices.length; officeIndex += 1) {
        const office = offices[officeIndex];
        state.planning.tripOfficeId = office.id;
        state.selectedOfficeIds = [office.id];
        state.visibleOfficeMarinaIds = [office.id];
        persistLocalState();
        render();
        zoomToOfficeCoverage(office.id);
        await delay(120);
        await waitForMapToSettle({ timeoutMs: 12000, stableMs: 700, pollMs: 100 });
        setStatus(`Generating office ${officeIndex + 1}/${offices.length}: ${office.name}`);
        const officeQueueEntries = officeScopeQueue
          .map((entry, queueIndex) => ({ ...entry, queueIndex }))
          .filter((entry) => entry.officeId === office.id);
        for (let scopeIndex = 0; scopeIndex < officeQueueEntries.length; scopeIndex += 1) {
          const entry = officeQueueEntries[scopeIndex];
          const globalIndex = entry.queueIndex;
          const progressStatus = `Preparing ${globalIndex + 1}/${officeScopeQueue.length} reports. Download each file from this page when it is ready.`;
          launcherReports[globalIndex].status = "Generating...";
          updateReportLauncherWindow(reportWindow, "All Trip Reports", launcherReports, {
            status: progressStatus,
            complete: false,
            summaryActions: currentSummaryActions,
          });
          const built = await generateOfficeTripReportPdf(office.id, {
            missionScope: entry.scope?.key || "all",
            onProgress: (message) => {
              launcherReports[globalIndex].status = String(message || "Generating...");
              updateReportLauncherWindow(reportWindow, "All Trip Reports", launcherReports, {
                status: progressStatus,
                complete: false,
                summaryActions: currentSummaryActions,
              });
            },
          });
          const reportUrls = createPdfLauncherUrls(built.blob, built.fileName);
          launcherReports[globalIndex].status = built.fileName;
          launcherReports[globalIndex].ready = true;
          launcherReports[globalIndex].openUrl = reportUrls.openUrl;
          launcherReports[globalIndex].downloadUrl = reportUrls.downloadUrl;
          launcherReports[globalIndex].fileName = built.fileName;
          summaryRows.push({
            title: built.title,
            office: built.office?.name || entry.officeName,
            scope: String(entry.scope?.key || "all").toUpperCase(),
            marinas: built.plan?.totals?.marinaCount || 0,
            tripDays: built.plan?.totals?.baseTripDays || 0,
            weatherDays: built.plan?.totals?.weatherContingencyDays || 0,
            travelOnlyDays: built.plan?.totals?.travelOnlyDays || 0,
            hotelNights: built.plan?.totals?.hotelNights || 0,
            mileage: built.plan?.totals?.driveMiles || 0,
            airfare: built.plan?.totals?.airfareCost || 0,
            perDiem: built.plan?.totals?.perDiemCost || 0,
            travelCost: built.plan?.totals?.travelCost || 0,
            supportCost: built.plan?.totals?.laborEquipmentCost || 0,
            estimateTotal: built.plan?.totals?.estimateTotal || 0,
          });
          currentSummaryActions = await buildTripCostSummaryActions(summaryRows);
          updateReportLauncherWindow(reportWindow, "All Trip Reports", launcherReports, {
            status: `Prepared ${globalIndex + 1}/${officeScopeQueue.length} reports. Download each file from this page when it is ready.`,
            complete: globalIndex === officeScopeQueue.length - 1,
            summaryActions: currentSummaryActions,
          });
          totalDownloads += 1;
          app.ui.toast(`Prepared ${built.fileName}`);
        }
      }
      setStatus(`Prepared ${totalDownloads} trip PDF${totalDownloads === 1 ? "" : "s"}.`);
      app.ui.toast(`Prepared ${totalDownloads} trip PDF${totalDownloads === 1 ? "" : "s"}.`);
    } finally {
      state.planning.tripOfficeId = previousTripOfficeId;
      state.selectedOfficeIds = previousSelectedOfficeIds;
      state.visibleOfficeMarinaIds = previousVisibleOfficeMarinaIds;
      persistLocalState();
      if (previousView?.bounds) {
        const bounds = L.latLngBounds(
          [Number(previousView.bounds.south), Number(previousView.bounds.west)],
          [Number(previousView.bounds.north), Number(previousView.bounds.east)],
        );
        if (bounds.isValid()) map.fitBounds(bounds, { animate: false });
      } else if (previousView?.center) {
        map.setView([Number(previousView.center.lat), Number(previousView.center.lng)], Number(previousView.zoom) || state.defaultZoom, { animate: false });
      }
      if (typeof map.setBearing === "function") map.setBearing(Number(previousView?.bearing) || 0);
      state.activeIndex = previousActiveIndex;
      drawPlanningOverlay(currentItem());
      map.invalidateSize(false);
      render();
    }
  }

  async function generateCombinedReportBookPdf() {
    const offices = state.offices.filter((office) => officeReportScopes(office.id, { excludeReportExcluded: true }).length);
    if (!offices.length) {
      app.ui.toast("No office reports are available.");
      return;
    }
    const officeScopeQueue = offices.flatMap((office) =>
      officeReportScopes(office.id, { excludeReportExcluded: true }).map((scope) => ({
        officeId: office.id,
        officeName: office.name,
        scope,
      }))
    );
    const launcherReports = officeScopeQueue.map((entry, index) => ({
      id: `${entry.officeId}:${entry.scope?.key || "all"}:${index}`,
      label: reportLauncherLabel(entry.scope),
      status: "Queued",
      ready: false,
      openUrl: "",
      downloadUrl: "",
      fileName: "",
    }));
    const reportWindow = openPreparedReportWindow(
      "Preparing a combined trip report book. Leave this page open while the summary and report pages are assembled.",
      "imggen-combined-trip-report-book",
    );
    const builtReports = [];
    const summaryRows = [];
    let currentSummaryActions = [];
    let launcherActions = [];

    updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
      status: `Preparing content for 0/${officeScopeQueue.length} reports...`,
      complete: false,
      summaryActions: currentSummaryActions,
    });

    for (let index = 0; index < officeScopeQueue.length; index += 1) {
      const entry = officeScopeQueue[index];
      launcherReports[index].status = "Generating...";
      updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
        status: `Preparing content for ${index + 1}/${officeScopeQueue.length} reports...`,
        complete: false,
        summaryActions: currentSummaryActions,
      });
      const report = await generateOfficeTripReportHtml(entry.officeId, {
        missionScope: entry.scope?.key || "all",
        onProgress: (message) => {
          launcherReports[index].status = String(message || "Generating...");
          updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
            status: `Preparing content for ${index + 1}/${officeScopeQueue.length} reports...`,
            complete: false,
            summaryActions: currentSummaryActions,
          });
        },
      });
      builtReports.push(report);
      summaryRows.push({
        title: report.title,
        office: report.office?.name || entry.officeName,
        scope: String(entry.scope?.key || "all").toUpperCase(),
        marinas: report.plan?.totals?.marinaCount || 0,
        tripDays: report.plan?.totals?.baseTripDays || 0,
        weatherDays: report.plan?.totals?.weatherContingencyDays || 0,
        travelOnlyDays: report.plan?.totals?.travelOnlyDays || 0,
        hotelNights: report.plan?.totals?.hotelNights || 0,
        mileage: report.plan?.totals?.driveMiles || 0,
        airfare: report.plan?.totals?.airfareCost || 0,
        perDiem: report.plan?.totals?.perDiemCost || 0,
        travelCost: report.plan?.totals?.travelCost || 0,
        supportCost: report.plan?.totals?.laborEquipmentCost || 0,
        estimateTotal: report.plan?.totals?.estimateTotal || 0,
      });
      currentSummaryActions = await buildTripCostSummaryActions(summaryRows);
      launcherReports[index].status = "Preparing standalone PDF...";
      updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
        status: `Preparing standalone report ${index + 1}/${officeScopeQueue.length}...`,
        complete: false,
        summaryActions: currentSummaryActions,
      });
      const standaloneFileName = `${sanitizeFilePart(report.title.replace(/:/g, ""), "trip-report")}.pdf`;
      const standaloneBlob = await reportHtmlToPdfBlob(report, {
        statusLabel: `Preparing PDF for ${report.title}...`,
        onProgress: (message) => {
          launcherReports[index].status = String(message || "Preparing standalone PDF...");
          updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
            status: `Preparing standalone report ${index + 1}/${officeScopeQueue.length}...`,
            complete: false,
            summaryActions: currentSummaryActions,
          });
        },
      });
      const standaloneUrls = createPdfLauncherUrls(standaloneBlob, standaloneFileName);
      launcherReports[index].status = "Prepared for book assembly";
      launcherReports[index].ready = true;
      launcherReports[index].openUrl = standaloneUrls.openUrl;
      launcherReports[index].downloadUrl = standaloneUrls.downloadUrl;
      launcherReports[index].fileName = standaloneFileName;
      updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
        status: `Prepared content for ${index + 1}/${officeScopeQueue.length} reports...`,
        complete: false,
        summaryActions: currentSummaryActions,
      });
    }

    const summaryTitle = "Trip Cost Summary";
    const pdf = await createLandscapeLetterPdf();
    let pdfPageIndex = 0;

    updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
      status: "Rendering trip cost summary into the combined PDF...",
      complete: false,
      summaryActions: currentSummaryActions,
    });
    const summaryCanvases = await renderReportHtmlToCanvases(
      { html: buildTripCostSummaryHtml(summaryTitle, summaryRows) },
      {
        statusLabel: `Preparing ${summaryTitle} for combined PDF...`,
        onProgress: (message) => {
          setStatus(message);
          updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
            status: String(message || "Rendering trip cost summary into the combined PDF..."),
            complete: false,
            summaryActions: currentSummaryActions,
          });
        },
      },
    );
    pdfPageIndex = appendCanvasesToPdf(pdf, summaryCanvases, { pageIndex: pdfPageIndex });

    for (let index = 0; index < builtReports.length; index += 1) {
      const report = builtReports[index];
      launcherReports[index].status = "Adding to combined PDF...";
      updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
        status: `Assembling combined PDF ${index + 1}/${builtReports.length}...`,
        complete: false,
        summaryActions: currentSummaryActions,
      });
      const reportCanvases = await renderReportHtmlToCanvases(report, {
        statusLabel: `Preparing ${report.title} for combined PDF...`,
        onProgress: (message) => {
          launcherReports[index].status = String(message || "Adding to combined PDF...");
          updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
            status: `Assembling combined PDF ${index + 1}/${builtReports.length}...`,
            complete: false,
            summaryActions: currentSummaryActions,
          });
        },
      });
      pdfPageIndex = appendCanvasesToPdf(pdf, reportCanvases, { pageIndex: pdfPageIndex });
      launcherReports[index].status = "Included in combined PDF";
      updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
        status: `Assembled ${index + 1}/${builtReports.length} reports into the combined PDF...`,
        complete: false,
        summaryActions: currentSummaryActions,
      });
    }

    const combinedFileName = `${sanitizeFilePart(`Trip Report Book ${new Date().toLocaleDateString("en-US")}`, "trip-report-book")}.pdf`;
    const combinedBlob = pdf.output("blob");
    const combinedUrls = createPdfLauncherUrls(combinedBlob, combinedFileName);
    const summaryArtifacts = await createTripCostSummaryArtifacts(summaryTitle, summaryRows);
    launcherActions = [
      { label: "Open Combined PDF", href: combinedUrls.openUrl },
      { label: "Download Combined PDF", href: combinedUrls.downloadUrl, download: combinedFileName },
      { label: "Open Cost Summary", href: summaryArtifacts.openUrl },
      { label: "Download Cost Summary PDF", href: summaryArtifacts.pdfDownloadUrl, download: summaryArtifacts.pdfFileName },
      { label: "Download Cost Summary Excel", href: summaryArtifacts.excelDownloadUrl, download: summaryArtifacts.excelFileName },
    ];
    updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
      status: `Combined PDF ready with ${builtReports.length} reports plus the summary.`,
      complete: true,
      summaryActions: launcherActions,
    });
    setStatus(`Combined trip report book ready: ${combinedFileName}`);
    app.ui.toast("Combined trip report book ready.");
  }

  async function generateOfficeTripReportHtml(officeId, { missionScope = "all", onProgress = null } = {}) {
    const reportWindow = null;
    return withStableReportViewport(async () => {
      const previousSuppressUiRouteSync = suppressUiRouteSync;
      suppressUiRouteSync = true;
      const { scope, rawPlan, plan, reportRouteScopeKey } = await prepareReportPlan(officeId, { missionScope, onProgress });
      const { office, marinas } = scope;
      if (!office || !marinas.length) {
        throw new Error("No reportable marinas are assigned to that office.");
      }
      const preparingMessage = `Preparing trip report for ${office.name}${missionScope !== "all" ? ` (${missionScope})` : ""}...`;
      setStatus(preparingMessage);
      if (typeof onProgress === "function") onProgress(preparingMessage);
      const previousView = snapshotMapView();
      const previousActiveIndex = state.activeIndex;
      const previousActiveItem = currentItem();
      const imageryLayerIds = findEsriImageryLayerIds();
      const imageryVisibility = captureLayerVisibility(imageryLayerIds);
      suppressAutoSaveView = true;
      try {
        setLayerVisibility(imageryLayerIds, true);
        await warmVisibleLayersForCapture({ extraDelayMs: 900 });
        const reportItems = Array.from(new Map(rawPlan.route.map((stop) => [stop.item.id, stop.item])).values());
        setLayerVisibility(imageryLayerIds, false);
        await warmVisibleLayersForCapture({ extraDelayMs: 250 });
        if (typeof onProgress === "function") onProgress(`Capturing coverage map for ${office.name}${missionScope !== "all" ? ` (${missionScope})` : ""}...`);
        await prepareOfficeCoverageCapture(officeId, { excludeReportExcluded: true, missionScope, routeScopeKey: reportRouteScopeKey });
        const mapImage = await captureCurrentMap({ includeMetadata: true });
        setLayerVisibility(imageryLayerIds, true);
        await warmVisibleLayersForCapture({ extraDelayMs: 350 });
        if (typeof onProgress === "function") onProgress(`Capturing marina images for ${office.name}${missionScope !== "all" ? ` (${missionScope})` : ""}...`);
        const marinaImages = await captureReportMarinaImages(reportItems, { onProgress });
        setLayerVisibility(imageryLayerIds, false);
        const title = reportTitleForScope(office, missionScope, marinas);
        const html = tripReportHtml({
          office,
          plan,
          mapImageDataUrl: mapImage.dataUrl,
          mapImageWidth: mapImage.width,
          mapImageHeight: mapImage.height,
          marinaImages,
          reportTitle: title,
        });
        return { office, marinas, plan, title, html, reportWindow };
      } finally {
        suppressUiRouteSync = previousSuppressUiRouteSync;
        clearCaptureProgress();
        window.clearTimeout(autoSaveViewTimer);
        autoSaveViewTimer = 0;
        suppressAutoSaveView = false;
        state.activeIndex = previousActiveIndex;
        const active = (previousActiveIndex >= 0 && state.items[previousActiveIndex]) ? state.items[previousActiveIndex] : previousActiveItem;
        restoreLayerVisibility(imageryVisibility);
        if (previousView?.bounds) {
          const bounds = L.latLngBounds(
            [Number(previousView.bounds.south), Number(previousView.bounds.west)],
            [Number(previousView.bounds.north), Number(previousView.bounds.east)],
          );
          if (bounds.isValid()) map.fitBounds(bounds, { animate: false });
        } else if (previousView?.center) {
          map.setView([Number(previousView.center.lat), Number(previousView.center.lng)], Number(previousView.zoom) || state.defaultZoom, { animate: false });
        }
        if (typeof map.setBearing === "function") map.setBearing(Number(previousView?.bearing) || 0);
        drawPlanningOverlay(active);
        map.invalidateSize(false);
        render();
      }
    });
  }

  async function focusItem(index, { saveCurrent = true, overlayItems = null } = {}) {
    if (index < 0 || index >= state.items.length) return;
    if (saveCurrent && state.editMarinaViews) saveCurrentViewForActive();
    state.activeIndex = index;
    const item = currentItem();
    applySavedView(item);
    map.invalidateSize(false);
    await delay(100);
    drawPlanningOverlay(item, { items: overlayItems });
    await waitForMapToSettle();
    render();
    setStatus(`Previewing ${index + 1}/${state.items.length}: ${item?.name || "item"}`);
  }

  async function focusRelative(step) {
    if (!state.items.length) return;
    const nextIndex = state.activeIndex < 0
      ? 0
      : Math.max(0, Math.min(state.items.length - 1, state.activeIndex + step));
    await focusItem(nextIndex, { saveCurrent: true });
  }

  function onGlobalKeyDown(event) {
    if (!containerEl || exporting || !state.items.length) return;
    if (event.defaultPrevented) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (isEditableTarget(event.target)) return;
    if (event.key === "ArrowLeft" || event.key === "[") {
      event.preventDefault();
      focusRelative(-1);
      return;
    }
    if (event.key === "ArrowRight" || event.key === "]") {
      event.preventDefault();
      focusRelative(1);
    }
  }

  async function loadDataset(file) {
    setStatus(`Parsing ${file.name}...`);
    const parsed = await parseDatasetFile(file);
    await applyParsedDataset(parsed, file.name);
  }

  function selectedItems() {
    return state.batchScope === "all"
      ? [...state.items]
      : state.items.filter((item) => item.selected);
  }

  async function exportItems(items, { zip = true } = {}) {
    if (exporting) return;
    if (!items.length) {
      app.ui.toast("No preview rows are selected for export.");
      return;
    }
    exporting = true;
    cancelRequested = false;
    saveCurrentViewForActive();
    render();
    const originalCenter = map.getCenter?.();
    const originalZoom = map.getZoom?.();
    const originalBearing = typeof map.getBearing === "function" ? map.getBearing() : 0;
    const ext = state.format === "png" ? "png" : "jpg";
    try {
      if (!zip && items.length === 1) {
        const only = items[0];
        setCaptureProgress(0, 1);
        setStatus(`Capturing ${only.name}`);
        applySavedView(only);
        map.invalidateSize(false);
        drawPlanningOverlay(only);
        await waitForMapToSettle();
        if (cancelRequested) throw new Error("Export cancelled.");
        const dataUrl = await captureCurrentMap();
        const blob = await dataUrlToBlob(dataUrl);
        setCaptureProgress(1, 1);
        triggerDownload(blob, `${sanitizeFilePart(`${state.prefix}_${String((state.items.indexOf(only) + 1)).padStart(3, "0")}_${only.name}`, "capture")}.${ext}`);
        setStatus(`Exported ${only.name}.`);
        app.ui.toast(`Exported ${only.name}.`);
      } else {
        const archive = new JSZip();
        setCaptureProgress(0, items.length);
        for (let index = 0; index < items.length; index += 1) {
          if (cancelRequested) throw new Error("Export cancelled.");
          const item = items[index];
          setStatus(`Capturing ${index + 1}/${items.length}: ${item.name}`);
          applySavedView(item);
          map.invalidateSize(false);
          drawPlanningOverlay(item);
          await waitForMapToSettle();
          if (cancelRequested) throw new Error("Export cancelled.");
          const dataUrl = await captureCurrentMap();
          const base64 = String(dataUrl).split(",")[1] || "";
          const order = state.items.indexOf(item) + 1;
          archive.file(`${sanitizeFilePart(`${state.prefix}_${String(order).padStart(3, "0")}_${item.name}`, "capture")}.${ext}`, base64, { base64: true });
          setCaptureProgress(index + 1, items.length);
        }
        if (cancelRequested) throw new Error("Export cancelled.");
        setStatus("Packaging ZIP...");
        const blob = await archive.generateAsync({ type: "blob" });
        triggerDownload(blob, `${sanitizeFilePart(state.prefix || state.datasetName || "marina-captures", "marina-captures")}.zip`);
        setStatus(`Exported ${items.length} image${items.length === 1 ? "" : "s"} to ZIP.`);
        app.ui.toast(`Exported ${items.length} image${items.length === 1 ? "" : "s"}.`);
      }
    } catch (error) {
      console.error("[ImageGenerator] export failed", error);
      if (/cancelled/i.test(String(error?.message || ""))) {
        setStatus("Export cancelled.");
        app.ui.toast("Image export cancelled.");
      } else {
        setStatus(`Export failed: ${error?.message || "unknown error"}`);
        app.ui.toast("Image export failed.");
      }
    } finally {
      clearCaptureProgress();
      const active = currentItem();
      try { ensureCaptureLayer().clearLayers(); } catch {}
      try {
        if (active) applySavedView(active);
        else map.setView(originalCenter, originalZoom, { animate: false });
      } catch {}
      try {
        if (!active && typeof map.setBearing === "function") map.setBearing(originalBearing || 0);
      } catch {}
      try { if (active) drawPlanningOverlay(active); } catch {}
      try { map.invalidateSize(false); } catch {}
      exporting = false;
      cancelRequested = false;
      render();
    }
  }

  function triggerDownload(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 300);
  }

  async function dataUrlToBlob(dataUrl) {
    const response = await fetch(dataUrl);
    return response.blob();
  }

  function toggleAllSelected(next) {
    state.items.forEach((item) => { item.selected = !!next; });
    render();
  }

  function openPreparedReportWindow(message = "The report window will update automatically when generation is complete.", targetName = "_blank") {
    if (!ENABLE_REPORT_POPUPS) return null;
    let reportWindow = window.open("about:blank", targetName);
    if (!reportWindow || reportWindow.closed) {
      try {
        const anchor = document.createElement("a");
        anchor.href = "about:blank";
        anchor.target = targetName;
        anchor.rel = "noopener";
        anchor.style.display = "none";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        reportWindow = window.open("", targetName);
      } catch (_) {}
    }
    if (reportWindow && !reportWindow.closed) {
      reportWindow.document.open();
      reportWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Preparing Trip Report</title>
    <style>
      body {
        margin: 0;
        font: 16px/1.5 "Segoe UI", Arial, sans-serif;
        color: #10233f;
        background: linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%);
        display: grid;
        place-items: center;
        min-height: 100vh;
      }
      .card {
        width: min(520px, calc(100vw - 48px));
        padding: 28px 30px;
        border-radius: 16px;
        background: rgba(255,255,255,0.92);
        border: 1px solid rgba(23,59,103,0.14);
        box-shadow: 0 20px 48px rgba(16,35,63,0.12);
      }
      h1 {
        margin: 0 0 8px;
        font-size: 28px;
        line-height: 1.1;
        color: #173b67;
      }
      p {
        margin: 0;
        color: #415a77;
      }
    </style>
  </head>
  <body>
    <div class="card">
      <h1>Preparing Trip Report</h1>
      <p>${reportEsc(message)}</p>
    </div>
  </body>
</html>`);
      reportWindow.document.close();
      try { reportWindow.blur?.(); } catch (_) {}
      reclaimHydrotoolsFocus();
    }
    return reportWindow;
  }

  function buildPdfViewerHtml(fileName, pdfUrl) {
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${reportEsc(fileName || "Trip Report")}</title>
    <style>
      body {
        margin: 0;
        min-height: 100vh;
        font: 14px/1.5 "Segoe UI", Arial, sans-serif;
        color: #10233f;
        background: linear-gradient(180deg, #eef5fb 0%, #e6eff8 100%);
      }
      .shell {
        display: grid;
        grid-template-rows: auto 1fr;
        min-height: 100vh;
      }
      .toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 16px 20px;
        background: rgba(255,255,255,0.94);
        border-bottom: 1px solid rgba(23,59,103,0.12);
        box-shadow: 0 10px 28px rgba(16,35,63,0.08);
      }
      .title {
        font-weight: 700;
        color: #173b67;
      }
      .actions {
        display: flex;
        gap: 10px;
      }
      .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 8px 14px;
        border-radius: 999px;
        border: 1px solid rgba(23,59,103,0.16);
        background: #ffffff;
        color: #0f4aa3;
        font-weight: 700;
        text-decoration: none;
      }
      .btn:hover {
        background: #f8fbff;
      }
      iframe {
        width: 100%;
        height: calc(100vh - 74px);
        border: 0;
        background: #dbe7f3;
      }
    </style>
  </head>
  <body>
    <div class="shell">
      <div class="toolbar">
        <div class="title">${reportEsc(fileName || "Trip Report")}</div>
        <div class="actions">
          <a class="btn" href="${reportEsc(pdfUrl)}" download="${reportEsc(fileName || "trip-report.pdf")}">Download PDF</a>
        </div>
      </div>
      <iframe src="${reportEsc(pdfUrl)}" title="${reportEsc(fileName || "Trip Report")}"></iframe>
    </div>
  </body>
</html>`;
  }

  function createPdfLauncherUrls(blob, fileName) {
    const downloadUrl = URL.createObjectURL(blob);
    const viewerBlob = new Blob([buildPdfViewerHtml(fileName, downloadUrl)], { type: "text/html;charset=utf-8" });
    const openUrl = URL.createObjectURL(viewerBlob);
    return { openUrl, downloadUrl };
  }

  function buildTripCostSummaryHtml(title, rows = []) {
    const generatedAt = new Date().toLocaleString();
    const officeCount = new Set(rows.map((row) => String(row.office || "").trim()).filter(Boolean)).size;
    const reportCount = rows.length;
    const totals = rows.reduce((acc, row) => ({
      marinas: acc.marinas + (Number(row.marinas) || 0),
      tripDays: acc.tripDays + (Number(row.tripDays) || 0),
      weatherDays: acc.weatherDays + (Number(row.weatherDays) || 0),
      travelOnlyDays: acc.travelOnlyDays + (Number(row.travelOnlyDays) || 0),
      hotelNights: acc.hotelNights + (Number(row.hotelNights) || 0),
      mileage: acc.mileage + (Number(row.mileage) || 0),
      airfare: acc.airfare + (Number(row.airfare) || 0),
      perDiem: acc.perDiem + (Number(row.perDiem) || 0),
      travelCost: acc.travelCost + (Number(row.travelCost) || 0),
      supportCost: acc.supportCost + (Number(row.supportCost) || 0),
      estimateTotal: acc.estimateTotal + (Number(row.estimateTotal) || 0),
    }), {
      marinas: 0,
      tripDays: 0,
      weatherDays: 0,
      travelOnlyDays: 0,
      hotelNights: 0,
      mileage: 0,
      airfare: 0,
      perDiem: 0,
      travelCost: 0,
      supportCost: 0,
      estimateTotal: 0,
    });
    const bodyRows = rows.map((row) => `
      <tr>
        <td>
          <div class="report-name">${reportEsc(row.title)}</div>
          <div class="report-subline">${reportEsc(row.office)}</div>
        </td>
        <td><span class="scope-badge scope-${reportEsc(String(row.scope || "").toLowerCase())}">${reportEsc(row.scope)}</span></td>
        <td class="num">${reportEsc(String(row.marinas))}</td>
        <td class="num">${reportEsc(String(row.tripDays))}</td>
        <td class="num">${reportEsc(String(row.weatherDays))}</td>
        <td class="num">${reportEsc(String(row.travelOnlyDays))}</td>
        <td class="num">${reportEsc(String(row.hotelNights))}</td>
        <td class="num">${reportEsc(formatMilesPrecise(row.mileage))}</td>
        <td class="num">${reportEsc(formatCurrency(row.airfare))}</td>
        <td class="num">${reportEsc(formatCurrency(row.perDiem))}</td>
        <td class="num">${reportEsc(formatCurrency(row.travelCost))}</td>
        <td class="num">${reportEsc(formatCurrency(row.supportCost))}</td>
        <td class="num total">${reportEsc(formatCurrency(row.estimateTotal))}</td>
      </tr>
    `).join("");
    const totalsRow = `
      <tr class="totals-row">
        <td><strong>Grand Total</strong></td>
        <td></td>
        <td class="num total">${reportEsc(String(totals.marinas))}</td>
        <td class="num total">${reportEsc(String(totals.tripDays))}</td>
        <td class="num total">${reportEsc(String(totals.weatherDays))}</td>
        <td class="num total">${reportEsc(String(totals.travelOnlyDays))}</td>
        <td class="num total">${reportEsc(String(totals.hotelNights))}</td>
        <td class="num total">${reportEsc(formatMilesPrecise(totals.mileage))}</td>
        <td class="num total">${reportEsc(formatCurrency(totals.airfare))}</td>
        <td class="num total">${reportEsc(formatCurrency(totals.perDiem))}</td>
        <td class="num total">${reportEsc(formatCurrency(totals.travelCost))}</td>
        <td class="num total">${reportEsc(formatCurrency(totals.supportCost))}</td>
        <td class="num total">${reportEsc(formatCurrency(totals.estimateTotal))}</td>
      </tr>
    `;
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${reportEsc(title)}</title>
    <style>
      @page { size: landscape; margin: 12mm; }
      :root {
        --ink:#10233f;
        --ink-soft:#50667f;
        --accent:#173b67;
        --panel:#ffffff;
        --panel-soft:#f4f8fc;
        --line:#d9e5f1;
        --good-bg:#e4f3e8;
        --good-line:#9bc8a8;
      }
      body { margin: 0; font: 14px/1.45 "Segoe UI", Arial, sans-serif; color: var(--ink); background: linear-gradient(180deg, #f7fbff 0%, #eef5fb 100%); }
      .page { min-height: 100vh; box-sizing: border-box; padding: 18px; }
      .hero {
        display: grid;
        grid-template-columns: minmax(0, 1.2fr) minmax(360px, 0.8fr);
        gap: 12px;
        align-items: start;
        margin-bottom: 12px;
      }
      .hero-card, .stats-card, .table-shell {
        background: rgba(255,255,255,0.96);
        border: 1px solid rgba(23,59,103,0.12);
        border-radius: 18px;
        box-shadow: 0 18px 36px rgba(16,35,63,0.08);
      }
      .hero-card {
        padding: 16px 18px;
      }
      h1 { margin: 0 0 8px; color: var(--accent); font-size: 28px; line-height: 1.06; letter-spacing: 0.01em; }
      .hero-meta {
        color: var(--ink-soft);
        font-size: 11px;
        line-height: 1.35;
      }
      .hero-meta strong {
        color: var(--ink);
      }
      .stats-card {
        padding: 10px;
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
      }
      .stat {
        padding: 10px 12px 9px;
        border-radius: 12px;
        background: linear-gradient(180deg, #fbfdff 0%, var(--panel-soft) 100%);
        border: 1px solid var(--line);
      }
      .stat-label {
        font-size: 9px;
        text-transform: uppercase;
        letter-spacing: 0.07em;
        color: var(--ink-soft);
      }
      .stat-value {
        margin-top: 4px;
        font-size: 16px;
        line-height: 1.1;
        font-weight: 700;
        color: var(--accent);
      }
      .table-shell {
        overflow: hidden;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
        background: var(--panel);
      }
      th, td {
        box-sizing: border-box;
        padding: 7px 6px;
        border-bottom: 1px solid var(--line);
        text-align: left;
        vertical-align: top;
      }
      th {
        background: #e8f0f8;
        color: var(--accent);
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      th.num-head {
        text-align: right;
      }
      td { font-size: 11px; line-height: 1.28; }
      td.num { text-align: right; font-variant-numeric: tabular-nums; }
      td.total { font-weight: 700; color: #0f2d52; }
      tr:nth-child(even) td { background: #fbfdff; }
      .report-name {
        font-weight: 700;
        color: var(--ink);
      }
      .report-subline {
        margin-top: 2px;
        color: var(--ink-soft);
        font-size: 9px;
      }
      .col-report { width: 27%; }
      .col-scope { width: 7%; }
      .col-small { width: 5.6%; }
      .col-miles { width: 7.2%; }
      .col-money { width: 7.6%; }
      .scope-badge {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        border-radius: 999px;
        font-size: 9px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        border: 1px solid #c7d8ea;
        background: #eef4fb;
        color: var(--accent);
      }
      .scope-road {
        background: #edf6ef;
        border-color: #bdd8c4;
        color: #245a34;
      }
      .scope-air {
        background: #eef2fb;
        border-color: #c7d1ee;
        color: #324f93;
      }
      .totals-row td {
        background: var(--good-bg);
        border-top: 2px solid var(--good-line);
        font-weight: 700;
      }
      .totals-row td:first-child {
        color: #184d2a;
      }
      @media print {
        .page { padding: 0; min-height: auto; }
        .hero { grid-template-columns: minmax(0, 1.1fr) minmax(300px, 0.9fr); gap: 14px; }
        .hero-card, .stats-card, .table-shell { box-shadow: none; }
        h1 { font-size: 24px; }
        .stat-value { font-size: 15px; }
      }
    </style>
  </head>
  <body>
    <div class="page">
    <div class="hero">
      <div class="hero-card">
        <h1>${reportEsc(title)}</h1>
        <div class="hero-meta">
          Generated <strong>${reportEsc(generatedAt)}</strong><br />
          Offices: <strong>${reportEsc(String(officeCount))}</strong> | Reports: <strong>${reportEsc(String(reportCount))}</strong> | Marinas: <strong>${reportEsc(String(totals.marinas))}</strong>
        </div>
      </div>
      <div class="stats-card">
        <div class="stat">
          <div class="stat-label">Estimate Total</div>
          <div class="stat-value">${reportEsc(formatCurrency(totals.estimateTotal))}</div>
        </div>
        <div class="stat">
          <div class="stat-label">Travel Cost</div>
          <div class="stat-value">${reportEsc(formatCurrency(totals.travelCost))}</div>
        </div>
        <div class="stat">
          <div class="stat-label">Support Cost</div>
          <div class="stat-value">${reportEsc(formatCurrency(totals.supportCost))}</div>
        </div>
        <div class="stat">
          <div class="stat-label">Trip Days</div>
          <div class="stat-value">${reportEsc(String(totals.tripDays))}</div>
        </div>
      </div>
    </div>
    <div class="table-shell">
    <table>
      <colgroup>
        <col class="col-report" />
        <col class="col-scope" />
        <col class="col-small" />
        <col class="col-small" />
        <col class="col-small" />
        <col class="col-small" />
        <col class="col-small" />
        <col class="col-miles" />
        <col class="col-money" />
        <col class="col-money" />
        <col class="col-money" />
        <col class="col-money" />
        <col class="col-money" />
      </colgroup>
      <thead>
        <tr>
          <th>Report</th>
          <th>Scope</th>
          <th class="num-head">Marinas</th>
          <th class="num-head">Trip Days</th>
          <th class="num-head">Weather Days</th>
          <th class="num-head">Travel Days</th>
          <th class="num-head">Hotels</th>
          <th class="num-head">Mileage</th>
          <th class="num-head">Airfare</th>
          <th class="num-head">Per Diem</th>
          <th class="num-head">Travel Cost</th>
          <th class="num-head">Support Cost</th>
          <th class="num-head">Estimate Total</th>
        </tr>
      </thead>
      <tbody>${bodyRows}${totalsRow}</tbody>
    </table>
    </div>
    </div>
  </body>
</html>`;
  }

  async function createTripCostSummaryArtifacts(title, rows = []) {
    const totals = rows.reduce((acc, row) => ({
      marinas: acc.marinas + (Number(row.marinas) || 0),
      tripDays: acc.tripDays + (Number(row.tripDays) || 0),
      weatherDays: acc.weatherDays + (Number(row.weatherDays) || 0),
      travelOnlyDays: acc.travelOnlyDays + (Number(row.travelOnlyDays) || 0),
      hotelNights: acc.hotelNights + (Number(row.hotelNights) || 0),
      mileage: acc.mileage + (Number(row.mileage) || 0),
      airfare: acc.airfare + (Number(row.airfare) || 0),
      perDiem: acc.perDiem + (Number(row.perDiem) || 0),
      travelCost: acc.travelCost + (Number(row.travelCost) || 0),
      supportCost: acc.supportCost + (Number(row.supportCost) || 0),
      estimateTotal: acc.estimateTotal + (Number(row.estimateTotal) || 0),
    }), {
      marinas: 0,
      tripDays: 0,
      weatherDays: 0,
      travelOnlyDays: 0,
      hotelNights: 0,
      mileage: 0,
      airfare: 0,
      perDiem: 0,
      travelCost: 0,
      supportCost: 0,
      estimateTotal: 0,
    });
    const html = buildTripCostSummaryHtml(title, rows);
    const openUrl = URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" }));
    const pdfBlob = await reportHtmlToPdfBlob({ html }, { statusLabel: `Preparing ${title} PDF...` });
    const pdfFileName = `${sanitizeFilePart(title, "trip-cost-summary")}.pdf`;
    const pdfDownloadUrl = URL.createObjectURL(pdfBlob);
    const excelFileName = `${sanitizeFilePart(title, "trip-cost-summary")}.xls`;
    const excelBlob = createExcelWorkbookBlob([
      {
        name: "Trip Cost Summary",
        columns: ["Report", "Office", "Scope", "Marinas", "Trip Days", "Weather Days", "Travel Days", "Hotel Nights", "Mileage", "Airfare", "Per Diem", "Travel Cost", "Support Cost", "Estimate Total"],
        rows: [
          ...rows.map((row) => [
            row.title,
            row.office,
            row.scope,
            row.marinas,
            row.tripDays,
            row.weatherDays,
            row.travelOnlyDays,
            row.hotelNights,
            Number(row.mileage || 0).toFixed(1),
            Number(row.airfare || 0).toFixed(2),
            Number(row.perDiem || 0).toFixed(2),
            Number(row.travelCost || 0).toFixed(2),
            Number(row.supportCost || 0).toFixed(2),
            Number(row.estimateTotal || 0).toFixed(2),
          ]),
          ["Grand Total", "", "", totals.marinas, totals.tripDays, totals.weatherDays, totals.travelOnlyDays, totals.hotelNights, totals.mileage.toFixed(1), totals.airfare.toFixed(2), totals.perDiem.toFixed(2), totals.travelCost.toFixed(2), totals.supportCost.toFixed(2), totals.estimateTotal.toFixed(2)],
        ],
      },
    ]);
    const excelDownloadUrl = URL.createObjectURL(excelBlob);
    return {
      openUrl,
      pdfDownloadUrl,
      pdfFileName,
      excelDownloadUrl,
      excelFileName,
    };
  }

  async function buildTripCostSummaryActions(rows = []) {
    if (!Array.isArray(rows) || !rows.length) return [];
    const summaryUrls = await createTripCostSummaryArtifacts("Trip Cost Summary", rows);
    return [
      { label: "Open Cost Summary", href: summaryUrls.openUrl },
      { label: "Download Cost Summary PDF", href: summaryUrls.pdfDownloadUrl, download: summaryUrls.pdfFileName },
      { label: "Download Cost Summary Excel", href: summaryUrls.excelDownloadUrl, download: summaryUrls.excelFileName },
    ];
  }

  function buildReportLauncherHtml(title, reports = [], { status = "", complete = false, summaryActions = [] } = {}) {
    const reportCards = reports.map((report) => `
      <div class="report-card ${report.ready ? "ready" : ""}">
        <div class="report-card-title">${reportEsc(report.label || "Report")}</div>
        <div class="report-card-status">${reportEsc(report.status || (report.ready ? "Ready" : "Queued"))}</div>
        ${(report.openUrl || report.downloadUrl) ? `
          <div class="report-card-actions">
            ${report.openUrl ? `<a href="${reportEsc(report.openUrl)}" target="_blank" rel="noopener">Open</a>` : ""}
            ${report.downloadUrl ? `<a href="${reportEsc(report.downloadUrl)}" download="${reportEsc(report.fileName || "report.pdf")}">Download</a>` : ""}
          </div>
        ` : ""}
      </div>
    `).join("");
    const summaryActionsHtml = summaryActions.length ? `
      <div class="summary-actions">
        ${summaryActions.map((action) => `
          <a href="${reportEsc(action.href || "#")}" ${action.download ? `download="${reportEsc(action.download)}"` : `target="_blank" rel="noopener"`}>${reportEsc(action.label || "Open")}</a>
        `).join("")}
      </div>
    ` : "";
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${reportEsc(title)}</title>
    <style>
      body {
        margin: 0;
        min-height: 100vh;
        font: 15px/1.5 "Segoe UI", Arial, sans-serif;
        color: #10233f;
        background: linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%);
      }
      .shell {
        max-width: 860px;
        margin: 0 auto;
        padding: 28px 24px 40px;
      }
      h1 {
        margin: 0 0 10px;
        font-size: 30px;
        line-height: 1.1;
        color: #173b67;
      }
      .status {
        margin: 0 0 18px;
        color: #415a77;
      }
      .reports {
        display: grid;
        gap: 14px;
      }
      .report-card {
        padding: 16px 18px;
        border-radius: 14px;
        background: rgba(255,255,255,0.92);
        border: 1px solid rgba(23,59,103,0.14);
        box-shadow: 0 14px 32px rgba(16,35,63,0.08);
      }
      .report-card.ready {
        background: rgba(214, 241, 224, 0.82);
        border-color: rgba(70,160,106,0.34);
      }
      .report-card-title {
        font-weight: 700;
        color: #173b67;
      }
      .report-card-status {
        margin-top: 4px;
        color: #415a77;
      }
      .report-card-actions {
        margin-top: 10px;
        display: flex;
        gap: 14px;
        flex-wrap: wrap;
      }
      a {
        color: #0f4aa3;
        font-weight: 600;
        text-decoration: none;
      }
      a:hover {
        text-decoration: underline;
      }
      .summary-actions {
        display: flex;
        gap: 14px;
        flex-wrap: wrap;
        margin: 0 0 18px;
      }
      .summary-actions a {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 8px 14px;
        border-radius: 999px;
        border: 1px solid rgba(23,59,103,0.14);
        background: rgba(255,255,255,0.96);
        text-decoration: none;
      }
      .done {
        margin-top: 18px;
        color: ${complete ? "#166534" : "#415a77"};
        font-weight: ${complete ? "700" : "500"};
      }
    </style>
  </head>
  <body>
    <div class="shell">
      <h1>${reportEsc(title)}</h1>
      <p class="status">${reportEsc(status || "Preparing reports...")}</p>
      ${summaryActionsHtml}
      <div class="reports">${reportCards || "<div>No reports queued.</div>"}</div>
      <div class="done">${complete ? "Reports are ready." : "Leave this tab open while reports are generated."}</div>
    </div>
  </body>
</html>`;
  }

  function updateReportLauncherWindow(reportWindow, title, reports = [], options = {}) {
    if (!reportWindow || reportWindow.closed) return;
    reportWindow.document.open();
    reportWindow.document.write(buildReportLauncherHtml(title, reports, options));
    reportWindow.document.close();
    try { reportWindow.blur?.(); } catch (_) {}
    reclaimHydrotoolsFocus();
  }

  function onClick(event) {
    if (event.target?.closest?.("#imggen-load")) {
      fileInputEl?.click?.();
      return;
    }
    if (event.target?.closest?.("#imggen-prev")) {
      focusRelative(-1);
      return;
    }
    if (event.target?.closest?.("#imggen-next")) {
      focusRelative(1);
      return;
    }
    if (event.target?.closest?.("#imggen-save-view")) {
      if (!state.editMarinaViews) {
        setStatus("Enable Edit marina views to save framing changes.");
        return;
      }
      saveCurrentViewForActive();
      setStatus("Saved current map view for this preview.");
      return;
    }
    if (event.target?.closest?.("#imggen-office-use-center")) {
      useMapCenterForOfficeDraft();
      return;
    }
    if (event.target?.closest?.("#imggen-las-browse")) {
      browseLasSource();
      return;
    }
    if (event.target?.closest?.("#imggen-las-check")) {
      checkLasToolchain();
      return;
    }
    if (event.target?.closest?.("#imggen-las-inspect")) {
      inspectLasSource();
      return;
    }
    if (event.target?.closest?.("#imggen-las-products")) {
      generateLasProducts();
      return;
    }
    if (event.target?.closest?.("#imggen-las-load-products")) {
      loadLasProductsIntoMap();
      return;
    }
    if (event.target?.closest?.("#imggen-las-training-draw")) {
      startDockTrainingDraw();
      return;
    }
    if (event.target?.closest?.("#imggen-las-training-remove")) {
      removeSelectedDockTrainingLabel();
      return;
    }
    if (event.target?.closest?.("#imggen-las-training-export")) {
      downloadDockTrainingLabels();
      return;
    }
    const labelRowBtn = event.target?.closest?.("[data-las-training-label-id]");
    if (labelRowBtn) {
      const labelId = String(labelRowBtn.getAttribute("data-las-training-label-id") || "");
      if (labelId) {
        state.lasDock.training.selectedLabelId = labelId;
        persistLocalState();
        renderDockTrainingLayers();
        render();
      }
      return;
    }
    if (event.target?.closest?.("#imggen-office-add")) {
      addOffice();
      return;
    }
    const officeZoomBtn = event.target?.closest?.("[data-office-zoom]");
    if (officeZoomBtn) {
      const officeId = String(officeZoomBtn.getAttribute("data-office-zoom") || "");
      if (officeId) zoomToOfficeCoverage(officeId);
      return;
    }
    const officeReportBtn = event.target?.closest?.("[data-office-report]");
    if (officeReportBtn) {
      const officeId = String(officeReportBtn.getAttribute("data-office-report") || "");
      (async () => {
        try {
          await generateScopedOfficeReportPdfs(officeId);
        } catch (error) {
          console.error("[ImageGenerator] trip report failed", error);
          setStatus(`Trip report failed: ${error?.message || "unknown error"}`);
          app.ui.toast("Trip report failed.");
        }
      })();
      return;
    }
    if (event.target?.closest?.("#imggen-generate-all-reports")) {
      generateAllOfficeReports().catch((error) => {
        console.error("[ImageGenerator] generate all reports failed", error);
        setStatus(`Generate all reports failed: ${error?.message || "unknown error"}`);
        app.ui.toast("Generate all reports failed.");
      });
      return;
    }
    if (event.target?.closest?.("#imggen-generate-combined-report-book")) {
      generateCombinedReportBookPdf().catch((error) => {
        console.error("[ImageGenerator] generate combined report book failed", error);
        setStatus(`Generate combined report book failed: ${error?.message || "unknown error"}`);
        app.ui.toast("Generate combined report book failed.");
      });
      return;
    }
    const officeExcelBtn = event.target?.closest?.("[data-office-excel]");
    if (officeExcelBtn) {
      const officeId = String(officeExcelBtn.getAttribute("data-office-excel") || "");
      if (officeId) {
        exportOfficeTripTableExcel(officeId).catch((error) => {
          console.error("[ImageGenerator] office Excel export failed", error);
          setStatus(`Office Excel export failed: ${error?.message || "unknown error"}`);
          app.ui.toast("Office Excel export failed.");
        });
      }
      return;
    }
    if (event.target?.closest?.("#imggen-refresh-route")) {
      refreshRouteMatrix({ force: true }).then(() => {
        if (state.showTripRoute) {
          const plan = buildTripPlan();
          ensureRouteGeometryForPlan(plan, { force: true });
        }
      }).catch(() => {});
      return;
    }
    if (event.target?.closest?.("#imggen-trip-day-prev")) {
      focusTripPlaybackRelative(-1);
      return;
    }
    if (event.target?.closest?.("#imggen-trip-day-next")) {
      focusTripPlaybackRelative(1);
      return;
    }
    const tripDayButton = event.target?.closest?.("[data-trip-day-index]");
    if (tripDayButton) {
      const idx = Number(tripDayButton.getAttribute("data-trip-day-index"));
      const plan = buildTripPlan();
      if (Number.isFinite(idx) && Array.isArray(plan?.days) && plan.days[idx]) {
        state.tripPlaybackDayIndex = idx;
        syncTripPlaybackOverlay(plan, { fit: state.tripPlaybackZoomToDay === true });
        render();
      }
      return;
    }
    const officeMarinaToggleBtn = event.target?.closest?.("[data-office-marinas-toggle]");
    if (officeMarinaToggleBtn) {
      const idx = Number(officeMarinaToggleBtn.getAttribute("data-office-marinas-toggle"));
      if (Number.isFinite(idx)) toggleOfficeMarinaVisibility(idx);
      return;
    }
    if (event.target?.closest?.("#imggen-export-database")) {
      exportFullDatabaseExcel();
      return;
    }
    if (event.target?.closest?.("#imggen-export")) {
      exportItems(selectedItems(), { zip: true });
      return;
    }
    const clusterZoomBtn = event.target?.closest?.("[data-cluster-zoom]");
    if (clusterZoomBtn) {
      const label = String(clusterZoomBtn.getAttribute("data-cluster-zoom") || "");
      if (label) zoomToCluster(label);
      return;
    }
    if (event.target?.closest?.("#imggen-cancel")) {
      cancelRequested = true;
      setStatus("Cancelling export...");
      render();
      return;
    }
    if (event.target?.closest?.("#imggen-select-all")) {
      toggleAllSelected(true);
      return;
    }
    if (event.target?.closest?.("#imggen-select-none")) {
      toggleAllSelected(false);
      return;
    }
    const rowBtn = event.target?.closest?.("[data-item-index]");
    if (rowBtn) {
      const idx = Number(rowBtn.getAttribute("data-item-index"));
      if (Number.isFinite(idx)) focusItem(idx, { saveCurrent: true });
      return;
    }
    const exportOneBtn = event.target?.closest?.("[data-export-index]");
    if (exportOneBtn) {
      const idx = Number(exportOneBtn.getAttribute("data-export-index"));
      if (Number.isFinite(idx) && state.items[idx]) exportItems([state.items[idx]], { zip: false });
      return;
    }
    const officeRemoveBtn = event.target?.closest?.("[data-office-remove]");
    if (officeRemoveBtn) {
      const idx = Number(officeRemoveBtn.getAttribute("data-office-remove"));
      if (Number.isFinite(idx)) removeOffice(idx);
    }
  }

  function onChange(event) {
    const target = event.target;
    if (target?.id === "imggen-file") {
      const file = target.files?.[0];
      if (file) {
        loadDataset(file).catch((error) => {
          console.error("[ImageGenerator] load failed", error);
          setStatus(`Load failed: ${error?.message || "unknown error"}`);
          app.ui.toast(`Ports and Harbors Toolset could not read ${file.name}.`);
        });
      }
      target.value = "";
      return;
    }
    handleFormInput(target, { commitFiles: false, source: "change" });
  }

  function handleFormInput(target, { commitFiles = false } = {}) {
    if (!target) return;
    if (commitFiles && target?.id === "imggen-file") return;
    if (target?.id === "imggen-zoom") {
      state.defaultZoom = Math.max(3, Math.min(24, Number(target.value) || 18));
      return;
    }
    if (target?.id === "imggen-scale") {
      state.scale = Math.max(1, Math.min(4, Number(target.value) || 2));
      return;
    }
    if (target?.id === "imggen-format") {
      state.format = String(target.value || "jpg").toLowerCase() === "png" ? "png" : "jpg";
      return;
    }
    if (target?.id === "imggen-marker") {
      state.marker = !!target.checked;
      drawPlanningOverlay(currentItem());
      return;
    }
    if (target?.id === "imggen-show-legend") {
      state.showLegend = !!target.checked;
      updateLegend();
      return;
    }
    if (target?.id === "imggen-edit-views") {
      state.editMarinaViews = !!target.checked;
      window.clearTimeout(autoSaveViewTimer);
      autoSaveViewTimer = 0;
      persistLocalState();
      render();
      setStatus(state.editMarinaViews ? "Edit marina views enabled." : "Edit marina views locked.");
      return;
    }
    if (target?.id === "imggen-show-marina-codes") {
      state.showMarinaCodes = !!target.checked;
      drawPlanningOverlay(currentItem());
      persistLocalState();
      return;
    }
    if (target?.id === "imggen-active-covered-slips") {
      const active = currentItem();
      if (active) {
        active.coveredSlips = !!target.checked;
        persistLocalState();
        render();
      }
      return;
    }
    if (target?.id === "imggen-active-exclude-report") {
      const active = currentItem();
      if (active) {
        active.excludeFromReport = !!target.checked;
        persistLocalState();
        render();
      }
      return;
    }
    if (target?.id === "imggen-active-no-fly-zone") {
      const active = currentItem();
      if (active) {
        active.noFlyZone = !!target.checked;
        persistLocalState();
        render();
      }
      return;
    }
    if (target?.id === "imggen-active-prioritize-air") {
      const active = currentItem();
      if (active) {
        active.prioritizeAirTravel = !!target.checked;
        clearRouteMatrix();
        persistLocalState();
        render();
      }
      return;
    }
    if (target?.id === "imggen-prefix") {
      state.prefix = String(target.value || "").trim() || "marina";
      return;
    }
    if (target?.id === "imggen-las-path") {
      state.lasDock.filePath = String(target.value || "");
      return;
    }
    if (target?.id === "imggen-las-resolution") {
      state.lasDock.resolution = Math.max(0.1, Number(target.value) || 1);
      return;
    }
    if (target?.id === "imggen-las-min-above-water") {
      state.lasDock.minAboveWater = Math.max(0, Number(target.value) || 0.5);
      return;
    }
    if (target?.id === "imggen-las-max-above-water") {
      state.lasDock.maxAboveWater = Math.max(state.lasDock.minAboveWater || 0, Number(target.value) || 6);
      return;
    }
    if (target?.id === "imggen-las-training-class") {
      state.lasDock.training.activeClass = String(target.value || "dock");
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-batch-scope") {
      state.batchScope = String(target.value || "selected") === "all" ? "all" : "selected";
      render();
      return;
    }
    if (target?.id === "imggen-preview-code-filter") {
      const nextValue = String(target.value || "").toUpperCase();
      const selectionStart = typeof target.selectionStart === "number" ? target.selectionStart : nextValue.length;
      const selectionEnd = typeof target.selectionEnd === "number" ? target.selectionEnd : selectionStart;
      state.previewCodeFilter = nextValue.trim();
      persistLocalState();
      render();
      restoreInputFocusById("imggen-preview-code-filter", selectionStart, selectionEnd);
      return;
    }
    if (target?.id === "imggen-office-name") {
      state.officeDraft.name = String(target.value || "");
      return;
    }
    if (target?.id === "imggen-office-lat") {
      state.officeDraft.lat = String(target.value || "");
      return;
    }
    if (target?.id === "imggen-office-lng") {
      state.officeDraft.lng = String(target.value || "");
      return;
    }
    if (target?.id === "imggen-speed") {
      state.planning.averageSpeedMph = Math.max(5, Number(target.value) || 45);
      recomputePlanning();
      clearRouteMatrix();
      drawPlanningOverlay(currentItem());
      render();
      return;
    }
    if (target?.id === "imggen-road-factor") {
      state.planning.roadFactor = Math.max(1, Number(target.value) || 1.2);
      recomputePlanning();
      clearRouteMatrix();
      drawPlanningOverlay(currentItem());
      render();
      return;
    }
    if (target?.id === "imggen-cluster-spacing") {
      state.planning.clusterSpacingMiles = Math.max(0.25, Number(target.value) || 70);
      recomputePlanning();
      clearRouteMatrix();
      drawPlanningOverlay(currentItem());
      render();
      return;
    }
    if (target?.id === "imggen-cluster-size") {
      state.planning.maxMarinasPerCluster = Math.max(1, Math.round(Number(target.value) || 5));
      recomputePlanning();
      clearRouteMatrix();
      drawPlanningOverlay(currentItem());
      render();
      return;
    }
    if (target?.id === "imggen-default-survey-hours") {
      state.planning.defaultSurveyHours = Math.max(0, Number(target.value) || 6);
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-max-work-hours") {
      state.planning.maxWorkHoursPerDay = Math.max(1, Number(target.value) || 7);
      normalizeDailyPlanning("work");
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-max-drive-hours") {
      state.planning.maxDriveHoursPerDay = Math.max(0, Number(target.value) || 3);
      normalizeDailyPlanning("drive");
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-max-marinas-day") {
      state.planning.maxMarinasPerDay = Math.max(1, Math.round(Number(target.value) || 2));
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-price-per-marina") {
      state.planning.pricePerMarina = Math.max(0, Number(target.value) || 0);
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-weather-contingency") {
      state.planning.weatherContingencyPct = Math.max(0, Number(target.value) || 0);
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-travel-mode") {
      const nextMode = String(target.value || "auto").toLowerCase();
      state.planning.travelMode = ["auto", "drive", "air"].includes(nextMode) ? nextMode : "auto";
      clearRouteMatrix();
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-air-overhead-hours") {
      state.planning.airOverheadHours = Math.max(0, Number(target.value) || 0);
      clearRouteMatrix();
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-air-cruise-mph") {
      state.planning.airCruiseMph = Math.max(100, Number(target.value) || 450);
      clearRouteMatrix();
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-gsa-api-key") {
      state.planning.gsaApiKey = String(target.value || "").trim();
      writeTravelPlanningGsaApiKey(state.planning.gsaApiKey);
      clearEnrichedTripPlan();
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-trip-office") {
      state.planning.tripOfficeId = String(target.value || "");
      clearRouteMatrix();
      clearRouteGeometry();
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-return-office") {
      state.planning.returnToOffice = !!target.checked;
      clearRouteGeometry();
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-show-trip-route") {
      state.showTripRoute = !!target.checked;
      if (!state.showTripRoute) clearRouteGeometry();
      persistLocalState();
      drawPlanningOverlay(currentItem());
      render();
      return;
    }
    if (target?.id === "imggen-trip-day-zoom") {
      state.tripPlaybackZoomToDay = !!target.checked;
      persistLocalState();
      return;
    }
    const rowCheck = target?.closest?.("[data-select-index]");
    if (rowCheck) {
      const idx = Number(rowCheck.getAttribute("data-select-index"));
      if (Number.isFinite(idx) && state.items[idx]) {
        state.items[idx].selected = !!target.checked;
        clearRouteMatrix();
        render();
      }
      return;
    }
    const surveyInput = target?.closest?.("[data-survey-index]");
    if (surveyInput) {
      const idx = Number(surveyInput.getAttribute("data-survey-index"));
      if (Number.isFinite(idx) && state.items[idx]) {
        state.items[idx].surveyHours = Math.max(0, Number(target.value) || 0);
        render();
      }
      return;
    }
    const coveredInput = target?.closest?.("[data-covered-index]");
    if (coveredInput) {
      const idx = Number(coveredInput.getAttribute("data-covered-index"));
      if (Number.isFinite(idx) && state.items[idx]) {
        state.items[idx].coveredSlips = !!target.checked;
        persistLocalState();
        render();
      }
      return;
    }
    const excludeInput = target?.closest?.("[data-exclude-report-index]");
    if (excludeInput) {
      const idx = Number(excludeInput.getAttribute("data-exclude-report-index"));
      if (Number.isFinite(idx) && state.items[idx]) {
        state.items[idx].excludeFromReport = !!target.checked;
        persistLocalState();
        render();
      }
      return;
    }
    const noFlyInput = target?.closest?.("[data-no-fly-zone-index]");
    if (noFlyInput) {
      const idx = Number(noFlyInput.getAttribute("data-no-fly-zone-index"));
      if (Number.isFinite(idx) && state.items[idx]) {
        state.items[idx].noFlyZone = !!target.checked;
        clearRouteMatrix();
        persistLocalState();
        render();
      }
      return;
    }
    const prioritizeAirInput = target?.closest?.("[data-prioritize-air-index]");
    if (prioritizeAirInput) {
      const idx = Number(prioritizeAirInput.getAttribute("data-prioritize-air-index"));
      if (Number.isFinite(idx) && state.items[idx]) {
        state.items[idx].prioritizeAirTravel = !!target.checked;
        clearRouteMatrix();
        persistLocalState();
        render();
      }
      return;
    }
  }

  function onInput(event) {
    const target = event.target;
    handleFormInput(target, { source: "input" });
  }

  function restoreInputFocusById(inputId, selectionStart = null, selectionEnd = null) {
    window.requestAnimationFrame(() => {
      const input = containerEl?.querySelector?.(`#${inputId}`);
      if (!input || typeof input.focus !== "function") return;
      input.focus();
      if (
        typeof input.setSelectionRange === "function"
        && Number.isFinite(selectionStart)
        && Number.isFinite(selectionEnd)
      ) {
        input.setSelectionRange(selectionStart, selectionEnd);
      }
    });
  }

  function render() {
    if (!containerEl) return;
    const active = currentItem();
    const selectedCount = state.items.filter((item) => item.selected).length;
    const officeCards = officeSummaries().map((office) => `
      <div style="padding:8px 10px; border:1px solid rgba(148,163,184,0.18); border-radius:10px; background:rgba(15,23,42,0.45); min-width:200px;">
        <div style="font-weight:700;">${esc(office.name)}</div>
        <div class="small">Assigned marinas: ${office.count} (${office.selected} selected)</div>
        <div class="small">Assignment basis: neighborhood-first office selection</div>
        <div class="small">Est. drive load: ${esc(formatDriveMinutes(office.totalDriveMinutes))}</div>
      </div>
    `).join("");
    if (!suppressUiRouteSync) ensureRouteMatrixForOffice(state.planning.tripOfficeId, { excludeReportExcluded: false });
    const rawTripPlan = buildTripPlan();
    ensureEnrichedTripPlan(rawTripPlan);
    const tripPlan = enrichedTripPlanKey === enrichedTripPlanCacheKey(rawTripPlan) && enrichedTripPlan
      ? enrichedTripPlan
      : rawTripPlan;
    const tripPlaybackDayIndex = normalizedTripPlaybackDayIndex(tripPlan);
    const tripPlaybackDay = currentTripPlaybackDay(tripPlan);
    state.tripPlaybackDayIndex = tripPlaybackDayIndex;
    if (!suppressUiRouteSync) ensureRouteGeometryForPlan(rawTripPlan);
    syncTripPlaybackOverlay(tripPlan);
    const tripOfficeOptions = state.offices.map((office) => `<option value="${esc(office.id)}" ${state.planning.tripOfficeId === office.id ? "selected" : ""}>${esc(office.name)}</option>`).join("");
    const lasToolStatus = state.lasDock.toolStatus || null;
    const lasInspection = state.lasDock.inspection || null;
    const lasQuicklook = lasInspection?.quicklook || null;
    const lasProducts = state.lasDock.products || null;
    const lasClassRows = Object.entries(lasInspection?.sample?.classHistogram || {})
      .map(([cls, count]) => `<div class="small">Class ${esc(cls)}: ${Number(count || 0).toLocaleString()}</div>`)
      .join("");
    const dockTraining = state.lasDock.training || { activeClass: "dock", labels: [], selectedLabelId: "" };
    const dockTrainingCounts = DOCK_TRAINING_CLASSES.map((entry) => `<span class="small" style="display:inline-flex; align-items:center; gap:6px; padding:4px 8px; border:1px solid rgba(148,163,184,0.18); border-radius:999px; background:rgba(15,23,42,0.35);"><span style="width:10px; height:10px; border-radius:999px; background:${entry.color};"></span>${esc(entry.label)} ${countDockTrainingByClass(entry.id)}</span>`).join("");
    const dockTrainingRows = (dockTraining.labels || []).map((label, index) => {
      const entry = dockTrainingClassById(label.classId);
      const selected = String(label.id || "") === String(dockTraining.selectedLabelId || "");
      return `<button type="button" data-las-training-label-id="${esc(label.id || "")}" class="btn" style="justify-content:flex-start; text-align:left; padding:8px 10px; border-color:${selected ? entry.color : "rgba(148,163,184,0.18)"}; background:${selected ? "rgba(15,23,42,0.72)" : "rgba(15,23,42,0.45)"};">
        <span style="display:inline-flex; align-items:center; gap:8px; width:100%;">
          <span style="width:10px; height:10px; border-radius:999px; background:${entry.color}; flex:0 0 auto;"></span>
          <span style="display:flex; flex-direction:column; min-width:0;">
            <span style="font-weight:700;">${esc(entry.label)} ${index + 1}</span>
            <span class="small">${esc(new Date(label.createdAt || Date.now()).toLocaleString())}</span>
          </span>
        </span>
      </button>`;
    }).join("");
    const dayCards = tripPlan.days.map((day, index) => `
      <button type="button" data-trip-day-index="${index}" style="padding:8px 10px; border:1px solid ${index === tripPlaybackDayIndex ? "rgba(251,191,36,0.65)" : "rgba(148,163,184,0.18)"}; border-radius:10px; background:${index === tripPlaybackDayIndex ? "rgba(245,158,11,0.16)" : "rgba(15,23,42,0.45)"}; min-width:240px; text-align:left; color:inherit; cursor:pointer; box-shadow:${index === tripPlaybackDayIndex ? "0 0 0 1px rgba(251,191,36,0.22)" : "none"};">
        <div style="font-weight:700;">Day ${day.day}</div>
        <div class="small">Start: ${day.startMode === "hotel" ? "Hotel / field" : "Office / home"}</div>
        <div class="small">Stops: ${day.marinaCount}</div>
        <div class="small">Drive: ${esc(formatDriveMinutes(day.driveMinutes))}</div>
        <div class="small">Survey: ${esc(formatDriveMinutes(day.workMinutes))}</div>
        <div class="small">Day total: ${esc(formatDriveMinutes(day.totalMinutes || (day.driveMinutes + day.workMinutes)))}</div>
        <div class="small">End: ${day.travelOnly ? (day.endMode === "office" ? "Travel only / office arrival" : "Travel only / overnight in field") : (day.endMode === "office" ? "Return home to office" : day.endMode === "hotel" ? "Hotel / overnight in field" : "Field end / return exceeds daily cap")}</div>
        <div style="margin-top:6px; display:grid; gap:4px;">
          ${day.travelOnly ? `<div class="small">${esc(day.travelLabel || "Travel only day")}</div>` : ""}
          ${day.stops.map((stop) => `<div class="small">${esc(stop.item.name)} (${formatMiles(stop.milesFromPrevious)}, ${formatDriveMinutes(stop.driveMinutesFromPrevious)}, ${esc(stop.driveSource || "estimated")}${stop.driveSource === "air" && stop.originAirport?.iata && stop.destAirport?.iata ? ` ${esc(stop.originAirport.iata)}→${esc(stop.destAirport.iata)}` : ""}${stop.airfareFromPrevious ? `, airfare ${esc(formatCurrency(stop.airfareFromPrevious))}` : ""}, survey ${Number(stop.surveyMinutes / 60).toFixed(1)}h)</div>`).join("")}
          ${day.returnLeg ? `<div class="small">Return to office (${formatMiles(day.returnLeg.miles)}, ${formatDriveMinutes(day.returnLeg.driveMinutes)}, ${esc(day.returnLeg.source || "estimated")})</div>` : ""}
        </div>
      </button>
    `).join("");
    const officeRows = state.offices.map((office, index) => `
      <div class="row" style="justify-content:space-between; gap:8px; padding:6px 8px; border:1px solid ${state.selectedOfficeIds.includes(office.id) ? "rgba(245,158,11,0.55)" : "rgba(148,163,184,0.16)"}; border-radius:10px; background:${state.selectedOfficeIds.includes(office.id) ? "rgba(245,158,11,0.08)" : "transparent"};">
        <div style="min-width:0; display:flex; align-items:flex-start; gap:8px;">
          <label style="padding-top:2px;" title="Toggle this office and its marinas on the map">
            <input type="checkbox" data-office-marinas-toggle="${index}" ${state.visibleOfficeMarinaIds.includes(office.id) ? "checked" : ""} ${exporting ? "disabled" : ""} />
          </label>
          <div>
            <div style="font-weight:600;">${esc(office.name)}</div>
          <div class="small">${Number(office.lat).toFixed(5)}, ${Number(office.lng).toFixed(5)}</div>
          <div class="small" style="margin-top:4px;">
            ${(() => {
              const badge = routingBadge(office.id);
              return `<span style="display:inline-flex; align-items:center; padding:2px 8px; border-radius:999px; background:${badge.bg}; border:1px solid ${badge.border}; color:${badge.color}; font-weight:600;">${esc(badge.text)}</span>`;
            })()}
          </div>
          </div>
        </div>
        <div class="row" style="gap:6px;">
          <button class="iconbtn" data-office-zoom="${esc(office.id)}" title="Zoom to office coverage" aria-label="Zoom to office coverage" ${exporting ? "disabled" : ""}>⌕</button>
          <button class="iconbtn" data-office-report="${esc(office.id)}" title="Print trip report" aria-label="Print trip report" ${exporting ? "disabled" : ""}>⎙</button>
          <button class="iconbtn" data-office-excel="${esc(office.id)}" title="Export office trip table to Excel" aria-label="Export office trip table to Excel" ${exporting ? "disabled" : ""}>⇩</button>
          <button class="iconbtn danger" data-office-remove="${index}" title="Remove office" aria-label="Remove office" ${exporting ? "disabled" : ""}>✕</button>
        </div>
      </div>
    `).join("");
    const filteredPreviewItems = state.items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => {
        const filter = String(state.previewCodeFilter || "").trim().toUpperCase();
        if (!filter) return true;
        const code = String(item?.portfolio?.code || "").trim().toUpperCase();
        return code.includes(filter);
      });
    const itemsMarkup = filteredPreviewItems.map(({ item, index }) => `
      <div class="row ${index === state.activeIndex ? "lw-active" : ""}" style="justify-content:space-between; gap:10px; padding:8px 10px; border-bottom:1px solid rgba(148,163,184,0.12); align-items:flex-start;">
        <div class="row" style="gap:8px; align-items:flex-start; flex:1; min-width:0;">
          <label style="padding-top:3px;">
            <input type="checkbox" data-select-index="${index}" ${item.selected ? "checked" : ""} ${exporting ? "disabled" : ""} />
          </label>
          <button class="iconbtn" data-item-index="${index}" title="Preview this marina" ${exporting ? "disabled" : ""}>${index + 1}</button>
          <div style="min-width:0; flex:1;">
            <div style="font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${esc(item.name)}</div>
            <div class="small">${item.lat.toFixed(5)}, ${item.lng.toFixed(5)}</div>
            <div class="small">Code: ${esc(item?.portfolio?.code || "-")}</div>
            <div class="small">Office: ${esc(item.officeName || "Unassigned")}${item.officeDistanceMiles != null ? ` | ${esc(formatMiles(item.officeDistanceMiles))} | ${esc(formatDriveMinutes(item.officeDriveMinutes))}` : ""}</div>
            <div class="small">Neighborhood: ${esc(item.neighborhoodId || "Unassigned")} | Cluster: ${esc(item.clusterLabel || "Unclustered")}</div>
            ${item.portfolio ? `<div class="small">Portfolio: ${esc(item.portfolio.city || "")}${item.portfolio.stateCountry ? `, ${esc(item.portfolio.stateCountry)}` : ""}${item.portfolio.archetype ? ` | ${esc(item.portfolio.archetype)}` : ""}${item.portfolio.wetSlips ? ` | Slips ${esc(item.portfolio.wetSlips)}` : ""}${item.portfolio.dryStorage ? ` | Storage ${esc(item.portfolio.dryStorage)}` : ""}</div>` : `<div class="small">Portfolio: not synced</div>`}
            ${item.portfolio?.coreAttributes ? `<div class="small">Attributes: ${esc(String(item.portfolio.coreAttributes).replace(/\n+/g, " | "))}</div>` : ""}
            ${item.portfolio?.fuelDock || item.portfolio?.watersideRestaurant ? `<div class="small">Ops: ${item.portfolio.fuelDock ? `Fuel ${esc(String(item.portfolio.fuelDock).replace(/\n+/g, ", "))}` : ""}${item.portfolio.fuelDock && item.portfolio.watersideRestaurant ? " | " : ""}${item.portfolio.watersideRestaurant ? `Restaurant ${esc(String(item.portfolio.watersideRestaurant).replace(/\n+/g, ", "))}` : ""}</div>` : ""}
            <div class="small">Survey:
              <input type="number" min="0" step="0.5" value="${esc(item.surveyHours ?? state.planning.defaultSurveyHours)}" data-survey-index="${index}" style="width:74px; margin-left:6px;" />
              hr
            </div>
            <label class="small" style="display:flex; align-items:center; gap:6px; margin-top:4px;">
              <input type="checkbox" data-covered-index="${index}" ${item.coveredSlips ? "checked" : ""} ${exporting ? "disabled" : ""} />
              Covered slips
            </label>
            <label class="small" style="display:flex; align-items:center; gap:6px; margin-top:4px;">
              <input type="checkbox" data-exclude-report-index="${index}" ${item.excludeFromReport ? "checked" : ""} ${exporting ? "disabled" : ""} />
              Exclude
            </label>
            <label class="small" style="display:flex; align-items:center; gap:6px; margin-top:4px;">
              <input type="checkbox" data-no-fly-zone-index="${index}" ${item.noFlyZone ? "checked" : ""} ${exporting ? "disabled" : ""} />
              No Fly Zone
            </label>
            <label class="small" style="display:flex; align-items:center; gap:6px; margin-top:4px;">
              <input type="checkbox" data-prioritize-air-index="${index}" ${item.prioritizeAirTravel ? "checked" : ""} ${exporting ? "disabled" : ""} />
              Prioritize Air Travel
            </label>
            <div class="small">View: ${esc(formatBounds(item.view?.bounds))}${item.view?.zoom ? ` | z${Number(item.view.zoom).toFixed(1)}` : ""}</div>
          </div>
        </div>
        <button class="iconbtn" data-export-index="${index}" title="Export just this image" ${exporting ? "disabled" : ""}>Shot</button>
      </div>
    `).join("");

    const panelSection = (key, title, body, { open = false } = {}) => `
      <details class="toolset-group" data-imggen-panel="${key}" ${open ? "open" : ""}>
        <summary>${title}</summary>
        <div class="toolset-content">
          ${body}
        </div>
      </details>
    `;

    containerEl.innerHTML = `
      ${panelSection("previewQueue", "Preview Queue", `
        <div class="small">Step through the list to review saved framing. Hotkeys: Left/[ = prev, Right/] = next.</div>
        <div class="row" style="margin-top:10px; flex-wrap:wrap;">
          <button id="imggen-load" class="btn">Load Point File</button>
          <button id="imggen-prev" class="btn" ${state.activeIndex <= 0 || exporting ? "disabled" : ""}>&lt;</button>
          <button id="imggen-next" class="btn" ${state.activeIndex < 0 || state.activeIndex >= state.items.length - 1 || exporting ? "disabled" : ""}>&gt;</button>
          <button id="imggen-save-view" class="btn" ${state.activeIndex < 0 || exporting || !state.editMarinaViews ? "disabled" : ""}>Save Current View</button>
          <input id="imggen-file" type="file" accept=".kmz,.kml,.geojson,.json" style="display:none;" />
        </div>
        <div class="small" style="margin-top:8px;">Dataset: <strong>${esc(state.datasetName || "none loaded")}</strong>${state.items.length ? ` - ${state.items.length} item${state.items.length === 1 ? "" : "s"}` : ""}</div>
        <div class="small" style="margin-top:4px;">Active: <strong>${active ? `${state.activeIndex + 1}. ${esc(active.name)}` : "none"}</strong></div>
        <label class="small" style="display:flex; align-items:center; gap:6px; margin-top:8px;">
          <input id="imggen-edit-views" type="checkbox" ${state.editMarinaViews ? "checked" : ""} ${exporting ? "disabled" : ""} />
          Edit marina views
        </label>
        <div class="small" style="margin-top:4px;">View state is ${state.editMarinaViews ? "<strong>editable</strong>" : "<strong>locked</strong>"}. Locked mode prevents preview, report, and export activity from overwriting saved marina framing.</div>
        <label class="small" style="display:flex; align-items:center; gap:6px; margin-top:8px;">
          <input id="imggen-active-covered-slips" type="checkbox" ${active?.coveredSlips ? "checked" : ""} ${active ? "" : "disabled"} />
          Covered Slips
        </label>
        <label class="small" style="display:flex; align-items:center; gap:6px; margin-top:6px;">
          <input id="imggen-active-exclude-report" type="checkbox" ${active?.excludeFromReport ? "checked" : ""} ${active ? "" : "disabled"} />
          Exclude
        </label>
        <label class="small" style="display:flex; align-items:center; gap:6px; margin-top:6px;">
          <input id="imggen-active-no-fly-zone" type="checkbox" ${active?.noFlyZone ? "checked" : ""} ${active ? "" : "disabled"} />
          No Fly Zone
        </label>
        <label class="small" style="display:flex; align-items:center; gap:6px; margin-top:6px;">
          <input id="imggen-active-prioritize-air" type="checkbox" ${active?.prioritizeAirTravel ? "checked" : ""} ${active ? "" : "disabled"} />
          Prioritize Air Travel
        </label>
        <div class="small" style="margin-top:4px;">Saved framing and marina flags persist until you explicitly change them again in edit mode.</div>
        <div class="row" style="margin-top:10px; flex-wrap:wrap;">
          <label class="small">Default zoom
            <input id="imggen-zoom" type="number" min="3" max="24" step="1" value="${esc(state.defaultZoom)}" style="width:80px; margin-left:6px;" />
          </label>
          <label class="small">Scale
            <input id="imggen-scale" type="number" min="1" max="4" step="1" value="${esc(state.scale)}" style="width:70px; margin-left:6px;" />
          </label>
          <label class="small">Format
            <select id="imggen-format" style="margin-left:6px;">
              <option value="jpg" ${state.format === "jpg" ? "selected" : ""}>JPG</option>
              <option value="png" ${state.format === "png" ? "selected" : ""}>PNG</option>
            </select>
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px;">
            <input id="imggen-show-legend" type="checkbox" ${state.showLegend ? "checked" : ""} />
            Legend
          </label>
        </div>
        <div class="row" style="margin-top:10px; gap:8px; flex-wrap:wrap;">
          <input id="imggen-prefix" type="text" value="${esc(state.prefix)}" placeholder="File prefix" style="flex:1; min-width:220px;" />
          <select id="imggen-batch-scope">
            <option value="selected" ${state.batchScope === "selected" ? "selected" : ""}>Batch selected only</option>
            <option value="all" ${state.batchScope === "all" ? "selected" : ""}>Batch all items</option>
          </select>
          <button id="imggen-export" class="btn primary" ${!state.items.length || exporting ? "disabled" : ""}>${exporting ? "Exporting..." : "Run Batch"}</button>
          <button id="imggen-cancel" class="btn" ${exporting ? "" : "disabled"}>Cancel</button>
        </div>
        <div class="row" style="margin-top:8px; gap:8px;">
          <button id="imggen-select-all" class="btn" ${!state.items.length || exporting ? "disabled" : ""}>Select All</button>
          <button id="imggen-select-none" class="btn" ${!state.items.length || exporting ? "disabled" : ""}>Select None</button>
          <button id="imggen-export-database" class="btn" ${!state.items.length || exporting ? "disabled" : ""}>Export Database XLS</button>
          <button id="imggen-generate-all-reports" class="btn" ${!state.offices.length || exporting ? "disabled" : ""}>Generate All Reports</button>
          <button id="imggen-generate-combined-report-book" class="btn" ${!state.offices.length || exporting ? "disabled" : ""}>Generate Combined PDF</button>
          <div class="small" style="align-self:center;">Selected: ${selectedCount}/${state.items.length}</div>
        </div>
        <div id="imggen-status" class="small" style="margin-top:10px;">${esc(status)}</div>
        <div id="imggen-progress-wrap" style="margin-top:6px; ${captureProgressTotal > 0 ? "" : "display:none;"}">
          <div style="height:8px; border-radius:999px; background:rgba(148,163,184,0.18); overflow:hidden;">
            <div
              id="imggen-progress-fill"
              role="progressbar"
              aria-label="Capture progress"
              aria-valuemin="0"
              aria-valuenow="${Math.max(0, Math.min(captureProgressTotal, captureProgressCurrent))}"
              aria-valuemax="${captureProgressTotal || 1}"
              style="height:100%; width:${captureProgressTotal > 0 ? Math.max(0, Math.min(100, (captureProgressCurrent / captureProgressTotal) * 100)) : 0}%; background:linear-gradient(90deg, #f59e0b, #fbbf24); transition:width 120ms ease;"
            ></div>
          </div>
        </div>
      `, { open: state.panels.previewQueue })}

      ${panelSection("officeAccess", "Office Access", `
        <div class="small">Add office/base locations. Nearby marinas are grouped first, then each neighborhood is assigned to the best office as a unit.</div>
        <div class="row" style="margin-top:10px; gap:8px; flex-wrap:wrap;">
          <input id="imggen-office-name" type="text" value="${esc(state.officeDraft.name)}" placeholder="Office name" style="min-width:180px; flex:1;" />
          <input id="imggen-office-lat" type="number" step="0.000001" value="${esc(state.officeDraft.lat)}" placeholder="Lat" style="width:120px;" />
          <input id="imggen-office-lng" type="number" step="0.000001" value="${esc(state.officeDraft.lng)}" placeholder="Lng" style="width:120px;" />
          <button id="imggen-office-use-center" class="btn" ${exporting ? "disabled" : ""}>Use Map Center</button>
          <button id="imggen-office-add" class="btn" ${exporting ? "disabled" : ""}>Add Office</button>
        </div>
        <div class="row" style="margin-top:10px; gap:10px; flex-wrap:wrap;">
          <label class="small">Avg mph
            <input id="imggen-speed" type="number" min="5" step="1" value="${esc(state.planning.averageSpeedMph)}" style="width:80px; margin-left:6px;" />
          </label>
          <label class="small">Road factor
            <input id="imggen-road-factor" type="number" min="1" step="0.05" value="${esc(state.planning.roadFactor)}" style="width:80px; margin-left:6px;" />
          </label>
          <label class="small">Cluster spacing mi
            <input id="imggen-cluster-spacing" type="number" min="0.25" step="0.25" value="${esc(state.planning.clusterSpacingMiles)}" style="width:90px; margin-left:6px;" />
          </label>
          <label class="small">Max marinas/cluster
            <input id="imggen-cluster-size" type="number" min="1" step="1" value="${esc(state.planning.maxMarinasPerCluster)}" style="width:70px; margin-left:6px;" />
          </label>
        </div>
        <div style="margin-top:10px; display:grid; gap:6px;">
          ${officeRows || `<div class="small">No offices added yet.</div>`}
        </div>
        <div style="margin-top:10px; display:flex; gap:8px; flex-wrap:wrap;">
          ${officeCards || ""}
        </div>
      `, { open: state.panels.officeAccess })}

      ${panelSection("tripPlanning", "Trip Planning", `
        <div class="small">Build a first-pass route across the selected marinas assigned to the chosen office. The tool now attempts routed travel time first and falls back to estimated travel if routing is unavailable.</div>
        <div class="small" style="margin-top:4px;">Daily schedule is capped at ${DAILY_TOTAL_HOURS} total hours, combining drive and survey time.</div>
        <div class="small" style="margin-top:4px;">The planner favors returning home when the day still fits, but it keeps crews in the field when that avoids obvious backtracking to the next assigned marina.</div>
        <div class="row" style="margin-top:10px; gap:10px; flex-wrap:wrap;">
          <label class="small">Trip office
            <select id="imggen-trip-office" style="margin-left:6px; min-width:180px;">
              ${tripOfficeOptions}
            </select>
          </label>
          <label class="small">Default survey hrs
            <input id="imggen-default-survey-hours" type="number" min="0" step="0.5" value="${esc(state.planning.defaultSurveyHours)}" style="width:80px; margin-left:6px;" />
          </label>
          <label class="small">Max work hrs/day
            <input id="imggen-max-work-hours" type="number" min="1" step="0.5" value="${esc(state.planning.maxWorkHoursPerDay)}" style="width:80px; margin-left:6px;" />
          </label>
          <label class="small">Max drive hrs/day
            <input id="imggen-max-drive-hours" type="number" min="0" step="0.5" value="${esc(state.planning.maxDriveHoursPerDay)}" style="width:80px; margin-left:6px;" />
          </label>
          <label class="small">Max marinas/day
            <input id="imggen-max-marinas-day" type="number" min="1" step="1" value="${esc(state.planning.maxMarinasPerDay)}" style="width:70px; margin-left:6px;" />
          </label>
          <label class="small">Price / marina
            <input id="imggen-price-per-marina" type="number" min="0" step="50" value="${esc(state.planning.pricePerMarina)}" style="width:100px; margin-left:6px;" />
          </label>
          <label class="small">Weather contingency %
            <input id="imggen-weather-contingency" type="number" min="0" step="1" value="${esc(state.planning.weatherContingencyPct)}" style="width:80px; margin-left:6px;" />
          </label>
          <label class="small">Travel mode
            <select id="imggen-travel-mode" style="margin-left:6px; width:100px;">
              <option value="auto" ${state.planning.travelMode === "auto" ? "selected" : ""}>Auto</option>
              <option value="drive" ${state.planning.travelMode === "drive" ? "selected" : ""}>Drive</option>
              <option value="air" ${state.planning.travelMode === "air" ? "selected" : ""}>Air</option>
            </select>
          </label>
          <label class="small">Air overhead hrs
            <input id="imggen-air-overhead-hours" type="number" min="0" step="0.5" value="${esc(state.planning.airOverheadHours)}" style="width:80px; margin-left:6px;" />
          </label>
          <label class="small">Air cruise mph
            <input id="imggen-air-cruise-mph" type="number" min="100" step="10" value="${esc(state.planning.airCruiseMph)}" style="width:80px; margin-left:6px;" />
          </label>
          <label class="small">GSA API key
            <input id="imggen-gsa-api-key" type="password" value="${esc(state.planning.gsaApiKey || "")}" placeholder="optional" style="width:180px; margin-left:6px;" />
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px;">
            <input id="imggen-return-office" type="checkbox" ${state.planning.returnToOffice ? "checked" : ""} />
            Return to office
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px;">
            <input id="imggen-show-trip-route" type="checkbox" ${state.showTripRoute ? "checked" : ""} />
            Show planned route
          </label>
          <button id="imggen-refresh-route" class="btn" ${tripPlan.totals.marinaCount ? "" : "disabled"}>Refresh Routed Times</button>
        </div>
        <div class="row" style="margin-top:10px; gap:8px; flex-wrap:wrap; align-items:center;">
          <button id="imggen-trip-day-prev" class="btn" ${tripPlaybackDayIndex <= 0 || !tripPlan.days.length ? "disabled" : ""}>&lt;</button>
          <button id="imggen-trip-day-next" class="btn" ${tripPlaybackDayIndex < 0 || tripPlaybackDayIndex >= tripPlan.days.length - 1 || !tripPlan.days.length ? "disabled" : ""}>&gt;</button>
          <div class="small">Playback day: <strong>${tripPlaybackDay ? `Day ${tripPlaybackDay.day}` : "none"}</strong></div>
          <label class="small" style="display:flex; align-items:center; gap:6px;">
            <input id="imggen-trip-day-zoom" type="checkbox" ${state.tripPlaybackZoomToDay ? "checked" : ""} />
            Zoom to Day
          </label>
          <div class="small">${tripPlaybackDay ? (tripPlaybackDay.travelOnly ? esc(tripPlaybackDay.travelLabel || "Travel only day") : `${tripPlaybackDay.marinaCount} marina${tripPlaybackDay.marinaCount === 1 ? "" : "s"} highlighted`) : "No trip days available."}</div>
        </div>
        ${statusBadgeHtml("Routing", routingStatusLabel(tripPlan.routing), "routing")}
        ${statusBadgeHtml("Per diem", tripPlan.totals.perDiemSource || "Not used", "perDiem")}
        ${tripPlan.totals.perDiemReason ? `<div class="small" style="margin-top:4px; color:#94a3b8;">Fallback reason: ${esc(tripPlan.totals.perDiemReason)}</div>` : ""}
        ${statusBadgeHtml("Route overlay", state.showTripRoute ? (routeGeometryStatus === "ready" ? "shown" : routeGeometryStatus === "loading" ? "loading" : routeGeometryError || "pending") : "hidden", "routeOverlay")}
        <div style="margin-top:10px; display:flex; gap:8px; flex-wrap:wrap;">
          <div style="padding:8px 10px; border:1px solid rgba(148,163,184,0.18); border-radius:10px; background:rgba(15,23,42,0.45); min-width:180px;">
            <div style="font-weight:700;">Trip Totals</div>
            <div class="small">Marinas: ${tripPlan.totals.marinaCount}</div>
            <div class="small">Selected overall: ${tripPlan.totals.availableSelectedCount ?? tripPlan.totals.marinaCount}</div>
            <div class="small">Office scope: ${esc(tripPlan.office?.name || "None")}</div>
            <div class="small">Drive: ${esc(formatDriveMinutes(tripPlan.totals.driveMinutes))}</div>
            <div class="small">Mileage: ${esc(formatMilesPrecise(tripPlan.totals.driveMiles || 0))}</div>
            <div class="small">Survey: ${esc(formatDriveMinutes(tripPlan.totals.surveyMinutes))}</div>
            <div class="small">Total: ${esc(formatDriveMinutes(tripPlan.totals.totalMinutes))}</div>
            <div class="small">Weather contingency: ${esc(`${Number(tripPlan.totals.weatherContingencyPct || 0).toFixed(0)}%`)}</div>
            <div class="small">Weather days: ${tripPlan.totals.weatherContingencyDays || 0}</div>
            <div class="small">Trip days w/ weather: ${tripPlan.totals.adjustedTripDays ?? tripPlan.days.length}</div>
            <div class="small">Travel only days: ${tripPlan.totals.travelOnlyDays || 0}</div>
            <div class="small">Travel mode: ${esc(String(tripPlan.travelMode || state.planning.travelMode || "drive").toUpperCase())}</div>
            <div class="small">Per diem source: ${esc(tripPlan.totals.perDiemSource || "Not used")}</div>
            ${tripPlan.totals.perDiemReason ? `<div class="small">Per diem fallback: ${esc(tripPlan.totals.perDiemReason)}</div>` : ""}
            <div class="small">Price / marina: ${esc(formatCurrency(state.planning.pricePerMarina || 0))}</div>
            <div class="small">Marina revenue: ${esc(formatCurrency((tripPlan.totals.marinaCount || 0) * (state.planning.pricePerMarina || 0)))}</div>
            <div class="small">Weather/travel contingency: ${esc(formatCurrency(tripPlan.totals.laborEquipmentCost || 0))}</div>
            <div class="small">Travel cost: ${esc(formatCurrency(tripPlan.totals.travelCost || 0))}</div>
            <div class="small">Airfare: ${esc(formatCurrency(tripPlan.totals.airfareCost || 0))}</div>
            <div class="small">Hotel nights: ${tripPlan.totals.hotelNights}</div>
            ${tripPlan.returnLeg ? `<div class="small">Return leg: ${formatMiles(tripPlan.returnLeg.miles)}, ${formatDriveMinutes(tripPlan.returnLeg.driveMinutes)}, ${esc(tripPlan.returnLeg.source || "estimated")}</div>` : ""}
          </div>
        </div>
        <div style="margin-top:10px; display:flex; gap:8px; flex-wrap:wrap;">
          ${dayCards || `<div class="small">Select marinas assigned to ${esc(tripPlan.office?.name || "the chosen office")} to generate a trip itinerary.</div>`}
        </div>
      `, { open: state.panels.tripPlanning })}

      ${panelSection("slipInventory", "Slip Inventory", `
        <div class="small">Database-backed slip inventory capture using the loaded marina list for Marina ID lookup and map zoom.</div>
        <div id="imggen-slip-inventory-host" style="margin-top:10px;"></div>
      `, { open: state.panels.slipInventory })}

      ${panelSection("dockExtraction", "Dock Extraction", `
        <div class="small">Frame a PDAL-first marina point cloud workflow for automatic dock boundary extraction. Start by verifying the host tools and inspecting the LAS.</div>
        <div class="row" style="margin-top:10px; gap:8px; flex-wrap:wrap; align-items:center;">
          <input id="imggen-las-path" type="text" value="${esc(state.lasDock.filePath || "")}" placeholder="Host LAS/LAZ file path" style="flex:1; min-width:260px;" />
          <button id="imggen-las-browse" type="button" class="btn">Browse LAS</button>
          <button id="imggen-las-check" type="button" class="btn">Check Tools</button>
          <button id="imggen-las-inspect" type="button" class="btn primary"${state.lasDock.filePath ? "" : " disabled"}>Inspect LAS</button>
        </div>
        <div class="row" style="margin-top:8px; gap:8px; flex-wrap:wrap; align-items:center;">
          <label class="small">Raster resolution
            <input id="imggen-las-resolution" type="number" min="0.1" step="0.1" value="${esc(state.lasDock.resolution || 1)}" style="width:80px; margin-left:6px;" />
          </label>
          <label class="small">Min above water
            <input id="imggen-las-min-above-water" type="number" min="0" step="0.1" value="${esc(state.lasDock.minAboveWater || 0.5)}" style="width:80px; margin-left:6px;" />
          </label>
          <label class="small">Max above water
            <input id="imggen-las-max-above-water" type="number" min="0" step="0.1" value="${esc(state.lasDock.maxAboveWater || 6)}" style="width:80px; margin-left:6px;" />
          </label>
          <button id="imggen-las-products" type="button" class="btn"${state.lasDock.filePath && lasInspection ? "" : " disabled"}>Generate Products</button>
          <button id="imggen-las-load-products" type="button" class="btn"${lasProducts ? "" : " disabled"}>Load Products</button>
        </div>
        <div class="small" style="margin-top:8px;">${esc(state.lasDock.statusText || "No LAS inspected yet.")}</div>
        <div style="margin-top:10px; display:grid; gap:8px;">
          <div style="padding:8px 10px; border:1px solid rgba(148,163,184,0.18); border-radius:10px; background:rgba(15,23,42,0.45);">
            <div style="font-weight:700; margin-bottom:4px;">Toolchain</div>
            <div class="small">PDAL: ${lasToolStatus ? (lasToolStatus.pdal?.installed ? `Ready (${esc(lasToolStatus.pdal.version || "installed")})` : `Missing`) : "unchecked"}</div>
            <div class="small">CloudCompare: ${lasToolStatus ? (lasToolStatus.cloudCompare?.installed ? `Ready` : `Missing`) : "unchecked"}</div>
            <div class="small">PotreeConverter: ${lasToolStatus ? (lasToolStatus.potreeConverter?.installed ? `Ready` : `Missing`) : "unchecked"}</div>
            ${lasToolStatus?.cloudCompare?.path ? `<div class="small">CloudCompare path: ${esc(lasToolStatus.cloudCompare.path)}</div>` : ""}
            ${lasToolStatus?.potreeConverter?.path ? `<div class="small">Potree path: ${esc(lasToolStatus.potreeConverter.path)}</div>` : ""}
          </div>
          <div style="padding:8px 10px; border:1px solid rgba(148,163,184,0.18); border-radius:10px; background:rgba(15,23,42,0.45);">
            <div style="font-weight:700; margin-bottom:4px;">LAS Inspection</div>
            ${lasInspection ? `
              <div class="small">Points: ${Number(lasQuicklook?.pointCount || 0).toLocaleString()}</div>
              <div class="small">Extent: ${Number(lasQuicklook?.width || 0).toFixed(2)} x ${Number(lasQuicklook?.height || 0).toFixed(2)} ${esc(lasQuicklook?.horizontalUnits || "units")}</div>
              <div class="small">Average spacing: ${lasQuicklook?.avgPointSpacing != null ? `${Number(lasQuicklook.avgPointSpacing).toFixed(3)} ${esc(lasQuicklook.horizontalUnits || "units")}` : "n/a"}</div>
              <div class="small">Projected code: ${esc(lasQuicklook?.projectedCode || "unknown")}</div>
              <div class="small">Dimensions: ${esc((lasQuicklook?.dimensions || []).join(", "))}</div>
              <div class="small" style="margin-top:6px; font-weight:700;">Sampled classes</div>
              ${lasClassRows || `<div class="small">No class histogram available.</div>`}
            ` : `<div class="small">Run Inspect LAS to populate point density, CRS, and class information.</div>`}
          </div>
          <div style="padding:8px 10px; border:1px solid rgba(148,163,184,0.18); border-radius:10px; background:rgba(15,23,42,0.45);">
            <div style="font-weight:700; margin-bottom:4px;">Planned Workflow</div>
            <div class="small">1. Use PDAL to inspect and rasterize the marina LAS into elevation and intensity products.</div>
            <div class="small">2. Detect above-water dock candidates from elevation, edge contrast, and long narrow planar shapes.</div>
            <div class="small">3. Review proposed dock boundaries in Hydrotools and clean them up before slip generation.</div>
            <div class="small">4. Convert accepted dock geometry into editable slip inventory polygons.</div>
          </div>
          <div style="padding:8px 10px; border:1px solid rgba(148,163,184,0.18); border-radius:10px; background:rgba(15,23,42,0.45);">
            <div style="font-weight:700; margin-bottom:4px;">Generated Products</div>
            ${lasProducts ? `
              <div class="small">Output folder: ${esc(lasProducts.outputDir || "")}</div>
              <div class="small">Elevation TIFF: ${esc(lasProducts.products?.elevation || "")}</div>
              <div class="small">Intensity TIFF: ${esc(lasProducts.products?.intensity || "")}</div>
              <div class="small">Dock band TIFF: ${esc(lasProducts.products?.dockBand || "")}</div>
              <div class="small">Estimated water level: ${Number(lasProducts.derived?.estimatedWaterLevel || 0).toFixed(2)}</div>
              <div class="small">Dock band Z range: ${Number(lasProducts.derived?.dockMinZ || 0).toFixed(2)} to ${Number(lasProducts.derived?.dockMaxZ || 0).toFixed(2)}</div>
              <div class="small">Pipeline: ${esc(lasProducts.pipelinePath || "")}</div>
            ` : `<div class="small">Generate Products to create the first dock-extraction rasters from the LAS.</div>`}
          </div>
          <div style="padding:8px 10px; border:1px solid rgba(148,163,184,0.18); border-radius:10px; background:rgba(15,23,42,0.45);">
            <div style="font-weight:700; margin-bottom:4px;">Training Labels</div>
            <div class="small">Teach Hydrotools what is a dock, boat, water surface, or other structure by drawing labeled polygons over the loaded LAS products.</div>
            <div class="row" style="margin-top:8px; gap:8px; flex-wrap:wrap; align-items:center;">
              <select id="imggen-las-training-class" style="min-width:160px;">
                ${dockTrainingClassOptions(dockTraining.activeClass || "dock")}
              </select>
              <button id="imggen-las-training-draw" type="button" class="btn"${lasProducts ? "" : " disabled"}>Draw Label</button>
              <button id="imggen-las-training-remove" type="button" class="btn"${dockTraining.selectedLabelId ? "" : " disabled"}>Remove Selected</button>
              <button id="imggen-las-training-export" type="button" class="btn"${(dockTraining.labels || []).length ? "" : " disabled"}>Export Labels JSON</button>
            </div>
            <div style="margin-top:8px; display:flex; gap:6px; flex-wrap:wrap;">
              ${dockTrainingCounts || `<span class="small">No labels yet.</span>`}
            </div>
            <div style="margin-top:8px; display:grid; gap:6px; max-height:220px; overflow:auto;">
              ${dockTrainingRows || `<div class="small">No training polygons yet. Load the LAS products, choose a class, then click Draw Label.</div>`}
            </div>
          </div>
        </div>
      `, { open: state.panels.dockExtraction })}

      ${panelSection("previewItems", "All Preview Items", `
        <div class="small">Each row can be toggled in or out of the batch. Shot exports that row only.</div>
        <label class="small" style="display:flex; align-items:center; gap:6px; margin-top:8px;">
          <input id="imggen-marker" type="checkbox" ${state.marker ? "checked" : ""} />
          Show selected marina bullseye and label on map
        </label>
        <label class="small" style="display:flex; align-items:center; gap:6px; margin-top:6px;">
          <input id="imggen-show-marina-codes" type="checkbox" ${state.showMarinaCodes ? "checked" : ""} />
          Show marina CODE labels on map
        </label>
        <div class="row" style="margin-top:8px; gap:8px; align-items:center;">
          <label class="small" for="imggen-preview-code-filter" style="min-width:72px;">Filter code</label>
          <input id="imggen-preview-code-filter" type="text" value="${esc(state.previewCodeFilter || "")}" placeholder="e.g. LLM" style="flex:1; min-width:160px;" />
          <div class="small">${filteredPreviewItems.length}/${state.items.length}</div>
        </div>
        <div style="margin-top:8px;">
          ${itemsMarkup || `<div class="small">No points loaded yet.</div>`}
        </div>
      `, { open: state.panels.previewItems })}
    `;
    const slipInventoryHost = containerEl.querySelector("#imggen-slip-inventory-host");
    if (slipInventoryHost) {
      if (!slipInventoryRuntime) slipInventoryRuntime = createSlipInventoryRuntime(app);
      slipInventoryRuntime.setMarinaItems?.(state.items);
      slipInventoryRuntime.mount(slipInventoryHost);
    }
    fileInputEl = containerEl.querySelector("#imggen-file");
    containerEl.querySelectorAll("details[data-imggen-panel]").forEach((details) => {
      details.ontoggle = () => {
        const key = String(details.dataset.imggenPanel || "");
        if (!key || !state.panels || !(key in state.panels)) return;
        state.panels[key] = !!details.open;
      };
    });
  }

  function mount(nextContainer) {
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("change", onChange);
      containerEl.removeEventListener("input", onInput);
    }
    document.removeEventListener("keydown", onGlobalKeyDown);
    try { map.off("moveend zoomend resize", queueAutoSaveCurrentView); } catch {}
    try { if (typeof map.off === "function") map.off("rotate", queueAutoSaveCurrentView); } catch {}
    containerEl = nextContainer;
    if (!hydratedFromLocalState && !state.items.length) {
      hydratedFromLocalState = true;
      const localState = readLocalState();
      if (localState && typeof localState === "object") restore(localState);
    }
    render();
    renderDockTrainingLayers();
    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("change", onChange);
    containerEl.addEventListener("input", onInput);
    document.addEventListener("keydown", onGlobalKeyDown);
    map.on("moveend zoomend resize", queueAutoSaveCurrentView);
    if (typeof map.on === "function" && typeof map.getBearing === "function") {
      map.on("rotate", queueAutoSaveCurrentView);
    }
    if (!defaultDatasetAttempted && !state.items.length) {
      defaultDatasetAttempted = true;
      setStatus(`Loading ${DEFAULT_DATASET_NAME}...`);
      parseDatasetUrl(DEFAULT_DATASET_URL, DEFAULT_DATASET_NAME)
        .then((parsed) => applyParsedDataset(parsed, DEFAULT_DATASET_NAME))
        .catch((error) => {
          console.error("[ImageGenerator] default dataset load failed", error);
          setStatus(`Default load failed: ${error?.message || "unknown error"}`);
        });
    }
    if (!defaultPortfolioAttempted) {
      defaultPortfolioAttempted = true;
      parsePortfolioWorkbookUrl(DEFAULT_PORTFOLIO_URL, DEFAULT_PORTFOLIO_NAME)
        .then((portfolio) => {
          portfolioWorkbook = portfolio;
          if (state.items.length) {
            const result = syncPortfolioToItems(portfolioWorkbook);
            const removed = filterZeroPropertyCountItems();
            recomputePlanning();
            render();
            setStatus(`Synced portfolio workbook to ${result.matched}/${result.total} marinas.${removed ? ` Removed ${removed} with property count 0.` : ""}`);
          }
        })
        .catch((error) => {
          console.error("[ImageGenerator] default portfolio load failed", error);
        });
    }
    ensureGlobalAirportsLoaded().catch(() => {});
  }

  function snapshot() {
    return {
      datasetName: state.datasetName,
      items: state.items,
      activeIndex: state.activeIndex,
      defaultZoom: state.defaultZoom,
      format: state.format,
      scale: state.scale,
      marker: state.marker,
      showLegend: state.showLegend,
      showMarinaCodes: state.showMarinaCodes,
      showTripRoute: state.showTripRoute,
      tripPlaybackDayIndex: state.tripPlaybackDayIndex,
      tripPlaybackZoomToDay: state.tripPlaybackZoomToDay,
      editMarinaViews: state.editMarinaViews,
      prefix: state.prefix,
      batchScope: state.batchScope,
      offices: state.offices,
      selectedOfficeIds: state.selectedOfficeIds,
      visibleOfficeMarinaIds: state.visibleOfficeMarinaIds,
      previewCodeFilter: state.previewCodeFilter,
      officeDraft: state.officeDraft,
      planning: state.planning,
      panels: state.panels,
      lasDock: state.lasDock,
      status,
    };
  }

  function restore(data) {
    if (!data || typeof data !== "object") return;
    state = {
      ...state,
      datasetName: String(data.datasetName || ""),
      items: Array.isArray(data.items) ? data.items.map((item) => normalizeItem(item)) : [],
      activeIndex: Number.isInteger(data.activeIndex) ? data.activeIndex : -1,
      defaultZoom: Math.max(3, Math.min(24, Number(data.defaultZoom) || 18)),
      format: String(data.format || "jpg").toLowerCase() === "png" ? "png" : "jpg",
      scale: Math.max(1, Math.min(4, Number(data.scale) || 2)),
      marker: data.marker !== false,
      showLegend: data.showLegend !== false,
      showMarinaCodes: data.showMarinaCodes !== false,
      showTripRoute: data.showTripRoute === true,
      tripPlaybackDayIndex: Number.isInteger(data.tripPlaybackDayIndex) ? data.tripPlaybackDayIndex : -1,
      tripPlaybackZoomToDay: data.tripPlaybackZoomToDay === true,
      editMarinaViews: data.editMarinaViews === true,
      prefix: String(data.prefix || "marina"),
      batchScope: String(data.batchScope || "selected") === "all" ? "all" : "selected",
      offices: Array.isArray(data.offices) ? data.offices.map((office) => normalizeOffice(office)) : defaultOffices(),
      selectedOfficeIds: Array.isArray(data.selectedOfficeIds) ? data.selectedOfficeIds : [],
      visibleOfficeMarinaIds: Array.isArray(data.visibleOfficeMarinaIds) ? data.visibleOfficeMarinaIds : [],
      previewCodeFilter: String(data.previewCodeFilter || ""),
      officeDraft: data.officeDraft && typeof data.officeDraft === "object" ? data.officeDraft : { name: "", lat: "", lng: "" },
      planning: data.planning && typeof data.planning === "object"
        ? {
            averageSpeedMph: Math.max(5, Number(data.planning.averageSpeedMph) || 45),
            roadFactor: Math.max(1, Number(data.planning.roadFactor) || 1.2),
            clusterSpacingMiles: Math.max(0.25, Number(data.planning.clusterSpacingMiles) || 70),
            maxMarinasPerCluster: Math.max(1, Math.round(Number(data.planning.maxMarinasPerCluster) || 5)),
            defaultSurveyHours: Math.max(0, Number(data.planning.defaultSurveyHours) || 6),
            maxWorkHoursPerDay: Math.max(1, Number(data.planning.maxWorkHoursPerDay) || 7),
            maxDriveHoursPerDay: Math.max(0, Number(data.planning.maxDriveHoursPerDay) || 3),
            maxMarinasPerDay: Math.max(1, Math.round(Number(data.planning.maxMarinasPerDay) || 2)),
            pricePerMarina: Math.max(0, Number(data.planning.pricePerMarina) || 6150),
            weatherContingencyPct: Math.max(0, Number(data.planning.weatherContingencyPct) || 10),
            gsaApiKey: String(data.planning.gsaApiKey || ""),
            travelMode: ["auto", "drive", "air"].includes(String(data.planning.travelMode || "").toLowerCase()) ? String(data.planning.travelMode || "").toLowerCase() : "auto",
            airOverheadHours: Math.max(0, Number(data.planning.airOverheadHours) || 4),
            airCruiseMph: Math.max(100, Number(data.planning.airCruiseMph) || 450),
            tripOfficeId: String(data.planning.tripOfficeId || DEFAULT_OFFICES[0].id),
            returnToOffice: data.planning.returnToOffice !== false,
          }
        : state.planning,
      panels: data.panels && typeof data.panels === "object"
        ? {
            previewQueue: data.panels.previewQueue !== false,
            officeAccess: data.panels.officeAccess !== false,
            tripPlanning: data.panels.tripPlanning !== false,
            slipInventory: data.panels.slipInventory !== false,
            dockExtraction: data.panels.dockExtraction !== false,
            previewItems: data.panels.previewItems !== false,
          }
        : state.panels,
      lasDock: data.lasDock && typeof data.lasDock === "object"
        ? {
            filePath: String(data.lasDock.filePath || ""),
            resolution: Math.max(0.1, Number(data.lasDock.resolution) || 1),
            minAboveWater: Math.max(0, Number(data.lasDock.minAboveWater) || 0.5),
            maxAboveWater: Math.max(0, Number(data.lasDock.maxAboveWater) || 6),
            toolStatus: data.lasDock.toolStatus || null,
            inspection: data.lasDock.inspection || null,
            products: data.lasDock.products || null,
            statusText: String(data.lasDock.statusText || "No LAS inspected yet."),
            training: data.lasDock.training && typeof data.lasDock.training === "object"
              ? {
                  activeClass: String(data.lasDock.training.activeClass || "dock"),
                  labels: Array.isArray(data.lasDock.training.labels) ? data.lasDock.training.labels : [],
                  selectedLabelId: String(data.lasDock.training.selectedLabelId || ""),
                }
              : { activeClass: "dock", labels: [], selectedLabelId: "" },
          }
        : state.lasDock,
    };
    recomputePlanning();
    normalizeDailyPlanning();
    status = String(data.status || status);
    persistLocalState();
    renderDockTrainingLayers();
    render();
    if (state.activeIndex >= 0 && state.items[state.activeIndex]) {
      applySavedView(state.items[state.activeIndex]);
      syncTripPlaybackOverlay(buildTripPlan());
    }
  }

  function destroy() {
    stopDockTrainingDraw("destroyed");
    try { slipInventoryRuntime?.destroy?.(); } catch {}
    slipInventoryRuntime = null;
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("change", onChange);
      containerEl.removeEventListener("input", onInput);
    }
    document.removeEventListener("keydown", onGlobalKeyDown);
    window.clearTimeout(autoSaveViewTimer);
    autoSaveViewTimer = 0;
    try { map.off("moveend zoomend resize", queueAutoSaveCurrentView); } catch {}
    try { if (typeof map.off === "function") map.off("rotate", queueAutoSaveCurrentView); } catch {}
    try { captureLayer?.clearLayers?.(); } catch {}
    try { if (captureLayer) map.removeLayer(captureLayer); } catch {}
    try { dockTrainingSavedLayer.clearLayers(); } catch {}
    try { dockTrainingSketchLayer.clearLayers(); } catch {}
    try { if (dockTrainingRoot) map.removeLayer(dockTrainingRoot); } catch {}
    try { if (legendControlEl?.parentElement) legendControlEl.parentElement.removeChild(legendControlEl); } catch {}
    captureLayer = null;
    legendControlEl = null;
    containerEl = null;
    fileInputEl = null;
  }

  return { mount, destroy, snapshot, restore };
}
